import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import {
  StagingWmsService,
  WmsService,
  WmsServiceInterface
} from "./services/WmsService";
import {
  RemService,
  RemServiceInterface,
  StagingRemService
} from "./services/RemService";
import {
  DcmService,
  DcmServiceInterface,
  StagingDcmService
} from "./services/DcmService";
import {
  StagingWebexService,
  WebexService,
  WebexServiceInterface
} from "./services/WebexService";
import {
  InboxService,
  InboxServiceInterface,
  StagingInboxService
} from "./services/InboxService";
import {
  AspectRatioMode,
  AwsLanguage,
  CallQueuePositionChangedReason,
  ClientStatus,
  Config,
  DesktopSourceDTO,
  DeviceType,
  EndpointStatus,
  EndpointStatusDetails,
  EndpointStatusDTO,
  ExpertDTO,
  FunctionalAvailabilityPropertyName,
  IceCandidateDTO,
  IncidentDTO,
  KioskDTO,
  MailCollectionDTO,
  MailDTO,
  MessageType,
  QrCodeDTO,
  RecordingParamPropertyName,
  RecordingParams,
  RemSignal,
  RequestedAction,
  RoomDTO,
  ScreenSharingDialogTab,
  ServiceConfig,
  ServiceDefaultPort,
  SessionInfo,
  StatisticMode,
  Survey,
  TelephonyType,
  TicketDTO,
  Timezone,
  TopicDTO,
  UsbIpDeviceDTO,
  VirtualBackgroundConfig,
  VisualHintDTO,
  WebexAccessToken,
  WebexMedia,
  WebexMeeting,
  WebexMeetingMember,
  WebexPerson
} from "./types";
import axios from "axios";
import moment from "moment-timezone";
import Peer from "simple-peer";
// eslint-disable-next-line @typescript-eslint/camelcase
import jwt_decode from "jwt-decode";
import {
  StagingWebdavService,
  WebdavService,
  WebdavServiceInterface
} from "./services/WebdavService";
import { FileStat } from "webdav/web/types";
import {
  LocalAudioTrackPublication,
  LocalVideoTrack,
  LocalVideoTrackPublication,
  RemoteAudioTrack,
  RemoteParticipant,
  RemoteTrack,
  Room
} from "twilio-video";
import {
  StagingTwilioService,
  TwilioService,
  TwilioServiceInterface
} from "./services/TwilioService";
import { RemoteVideoTrack } from "twilio-video/tsdef/RemoteVideoTrack";
import {
  AgentService,
  AgentServiceInterface,
  StagingAgentService
} from "./services/AgentService";
import { getDeviceType } from "./utils";
import { detect } from "detect-browser";
import {
  AwsService,
  AwsServiceInterface,
  StagingAwsService
} from "./services/AwsService";

interface ConfigState {
  config: Config | null;
}

let services: {
  rem?: RemServiceInterface;
  dcm?: DcmServiceInterface;
  wms?: WmsServiceInterface;
  twilio?: TwilioServiceInterface;
  webex?: WebexServiceInterface;
  inbox?: InboxServiceInterface;
  webdav?: WebdavServiceInterface;
  agent?: AgentServiceInterface;
  aws?: AwsServiceInterface;
} = {};

const createServices = (envMode: string) => {
  const isStaging = envMode === "staging";

  services = {
    rem: isStaging ? new StagingRemService() : new RemService(),
    dcm: isStaging ? new StagingDcmService() : new DcmService(),
    wms: isStaging ? new StagingWmsService() : new WmsService(),
    twilio: isStaging ? new StagingTwilioService() : new TwilioService(),
    webex: isStaging ? new StagingWebexService() : new WebexService(),
    inbox: isStaging ? new StagingInboxService() : new InboxService(),
    webdav: isStaging ? new StagingWebdavService() : new WebdavService(),
    agent: isStaging ? new StagingAgentService() : new AgentService(),
    aws: isStaging ? new StagingAwsService() : new AwsService()
  };

  return services;
};

const patchServicesConfig = (
    config: Partial<ServiceConfig>,
    servicesNames?: (
      | "rem"
      | "dcm"
      | "wms"
      | "twilio"
      | "webex"
      | "inbox"
      | "webdav"
      | "agent"
      | "aws"
    )[]
  ) =>
    _.values(
      servicesNames ? _.pick(services, servicesNames) : services
    ).forEach(service => service?.patchConfig(config)),
  unsetServicesConfig = (
    fields: string[],
    servicesNames?: (
      | "rem"
      | "dcm"
      | "wms"
      | "twilio"
      | "webex"
      | "inbox"
      | "webdav"
      | "agent"
      | "aws"
    )[]
  ) =>
    _.values(
      servicesNames ? _.pick(services, servicesNames) : services
    ).forEach(service => service?.unsetConfig(fields));

interface TopicsState {
  list: Record<string, TopicDTO> | null;
  available: Record<string, number[]> | null;
}

interface QueueState {
  position: number | null;
  positionChangeReason: CallQueuePositionChangedReason | null;
  list: Record<string, object[]> | null;
}

interface SessionState {
  info: SessionInfo | null;
  party: string | null;
  visualHints: Record<
    "common" | "kiosk" | "expert",
    {
      baseUrl: string;
      files: FileStat[];
    }
  > | null;
  visualHint: VisualHintDTO | null;
  qrCode: QrCodeDTO | null;
  textHint: { duration: number; text: string } | null;
  isMuted: boolean | null;
  callRequestReceivedAt: number | null;
  hangupRequestReceivedAt: number | null;
  requestedActions: { action: RequestedAction; payload?: any }[] | null;
  room: RoomDTO | null;
  rooms: RoomDTO[] | null;
  ticket: object | null;
  notifications: { text: string; timestamp: number }[] | null;
  recordTranslation: { text: string; audio?: Uint8Array } | null;
}

interface KioskState {
  location: string | null;
  kiosk: {} | null;
  profile: { name: string; value: unknown }[] | null;
  specs:
    | {
        name: string;
        default_value: unknown;
      }[]
    | null;
  aspectRatio: AspectRatioMode | null;
  screenResolution: {
    width: number;
    height: number;
  } | null;
  isMuted: boolean | null;
  isSelfViewVisible: boolean | null;
  microphones: { microphone: MediaDeviceInfo; isActive: boolean }[] | null;
  layouts:
    | {
        name: string;
        value: number;
        isActive: boolean;
      }[]
    | null;
  accessabilityMode: boolean | null;
  topics: Record<string, TopicDTO> | null;
}

interface MediaInputsState {
  cameras:
    | {
        camera: MediaDeviceInfo;
        isActive: boolean;
      }[]
    | null;
  defaultCamera: string | null;
  microphones:
    | {
        microphone: MediaDeviceInfo;
        isActive: boolean;
      }[]
    | null;
  defaultMicrophone: string | null;
}

interface SharingState {
  local: {
    stream: MediaStream | null;
    streamType: "screen" | "barcode" | null;
    file: {
      mimeType: string;
      url: string;
    } | null;
    peer: any | null;
    isInteractive: boolean | null;
    correlationId: string | null;
    candidates: IceCandidateDTO[] | null;
    candidatesTimer: number | null;
  };
  remote: {
    stream: MediaStream | null;
    file: {
      mimeType: string;
      url: string;
    } | null;
    peer: any | null;
    isInteractive: boolean | null;
    correlationId: string | null;
  };
}

interface WebexState {
  webex: any | null;
  accessToken: WebexAccessToken | null;
  meetingId: string | null;
  joinedMeetingParticipantsCount: number | null;
  streams: {
    local: MediaStream | null;
    remoteVideo: MediaStream | null;
    remoteAudio: MediaStream | null;
    remoteShare: MediaStream | null;
  };
}

interface TwilioState {
  accessToken: string | null;
  media: {
    local: HTMLMediaElement | null;
    remote: HTMLMediaElement[] | null;
  };
}

interface AuthState {
  jwt: string | null;
  deviceId: string | null;
  me: {} | null;
  profile: { name: string; value: unknown }[] | null;
  specs:
    | {
        name: string;
        default_value: unknown;
      }[]
    | null;
}

interface NetworkState {
  status: "connected" | "disconnected" | "shutdown";
}

const jwtStorageKey = "auth.jwt",
  deviceIdStorageKey = "auth.device_id",
  clientStatusStorageKey = "auth.clientStatus",
  inboxSmsSnippetsStorageKey = "inbox.smsSnippets",
  defaultCameraStorageKey = "mediaInput.defaultCamera",
  defaultMicrophoneStorageKey = "mediaInput.defaultMicrophone";

const topicsModule = {
  namespaced: true,
  state: (): TopicsState => ({
    list: null,
    available: null
  }),
  actions: {
    getList: async (
      context: {
        commit: (mutation: string, data?: object) => void;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Topics retrieving...");
      services.rem?.getTopics(
        (topics: Record<string, TopicDTO>) => {
          console.log("Topics retrieved: ", topics);
          context.commit("SET_LIST", topics);
          params?.callback?.(topics);
        },
        (err: unknown) => {
          console.log("Topics retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    getAvailable: async (
      context: {
        commit: (mutation: string, data?: object) => void;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Available topics retrieving...");
      services.rem?.getAvailableTopics(
        (topics: Record<string, number[]>) => {
          console.log("Available topics retrieved: ", topics);
          context.commit("SET_AVAILABLE", topics);
          params?.callback?.(topics);
        },
        (err: unknown) => {
          console.log("Available topics retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    }
  },
  mutations: {
    SET_LIST(state: TopicsState, topics: Record<string, TopicDTO>) {
      state.list = topics;
    },
    SET_AVAILABLE(state: TopicsState, topics: Record<string, number[]>) {
      state.available = topics;
    }
  }
};

const queueModule = {
  namespaced: true,
  state: (): QueueState => ({
    position: null,
    positionChangeReason: null,
    list: null
  }),
  getters: {
    isThrownUrgently(state: QueueState) {
      return (
        state.position !== null &&
        +state.position < 0 &&
        state.positionChangeReason !== null &&
        [
          CallQueuePositionChangedReason.timeout,
          CallQueuePositionChangedReason.noTopic,
          CallQueuePositionChangedReason.noExperts
        ].includes(state.positionChangeReason)
      );
    }
  },
  actions: {
    acquire: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: unknown;
      },
      params: {
        target: string;
        foreignId?: string;
        context?: object;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log(
        "Sending queue acquire request...",
        _.omit(params, ["callback", "errorCallback"])
      );
      services.rem?.acquire(
        params.target,
        params.foreignId ?? "",
        params.context ?? {},
        (position: number) => {
          console.log("Queue acquire request has been sent: ", position);
          context.commit("SET_POSITION", position);
          if (position === -1) {
            context.commit(
              "SET_POSITION_CHANGE_REASON",
              CallQueuePositionChangedReason.noExperts
            );
          }
          params.callback?.(position);
        },
        (err: unknown) => {
          console.error("Queue acquire sending error ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    release: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending release request...");
      services.rem?.removeFromQueue(
        (data: unknown) => {
          console.log("Release request has been sent: ", data);
          params?.callback?.(data);
        },
        (err: unknown) => {
          console.error("Release request sending error ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    getPosition: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Queue position retrieving...");
      services.rem?.getQueuePosition(
        (position: number) => {
          console.log("Queue position retrieved: ", position);
          context.commit("SET_POSITION", position);
          params?.callback?.(position);
        },
        (err: unknown) => {
          console.error("Queue position retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    getList: (
      context: {
        commit: (mutation: string, data?: any) => void;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Queue list retrieving...");
      services.rem?.getQueueList(
        (list: Record<string, object[]>) => {
          console.log("Queue list retrieved: ", list);
          context.commit("SET_LIST", list);
          params?.callback?.(list);
        },
        (err: unknown) => {
          console.error("Queue list retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    }
  },
  mutations: {
    SET_POSITION(state: QueueState, position: number) {
      state.position = position;
    },
    UNSET_POSITION(state: QueueState) {
      state.position = null;
    },
    SET_POSITION_CHANGE_REASON(
      state: QueueState,
      reason: CallQueuePositionChangedReason
    ) {
      state.positionChangeReason = reason;
    },
    UNSET_POSITION_CHANGE_REASON(state: QueueState) {
      state.positionChangeReason = null;
    },
    SET_LIST(state: QueueState, list: Record<string, object[]>) {
      state.list = list;
    },
    UNSET_LIST(state: QueueState) {
      state.list = null;
    }
  }
};

export const audioOutputVolumeOptionName = "audio.output.volume",
  audioInputVolumeOptionName = "audio.input.volume";

const kioskModule = {
  namespaced: true,
  state: (): KioskState => ({
    location: null,
    kiosk: null,
    profile: null,
    specs: null,
    aspectRatio: null,
    screenResolution: null,
    isMuted: null,
    isSelfViewVisible: null,
    microphones: null,
    layouts: null,
    accessabilityMode: null,
    topics: null
  }),
  getters: {
    property: (state: KioskState) => (propName: string) =>
      _.find(state.profile, o => o.name === propName)?.value ??
      _.find(state.specs, o => o.name === propName)?.default_value,
    audioLevels(state: KioskState) {
      return {
        output:
          _.find(state.profile, o => o.name === audioOutputVolumeOptionName)
            ?.value ??
          _.find(state.specs, o => o.name === audioOutputVolumeOptionName)
            ?.default_value ??
          null,
        input:
          _.find(state.profile, o => o.name === audioInputVolumeOptionName)
            ?.value ??
          _.find(state.specs, o => o.name === audioInputVolumeOptionName)
            ?.default_value ??
          null
      };
    }
  },
  actions: {
    getProfile: async (
      context: {
        commit: (mutation: string, kiosk: {}) => void;
        rootState: unknown;
      },
      params: {
        uuid: string;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Kiosk profile retrieving...", _.pick(params, "uuid"));
      services.dcm?.getProfile(
        params.uuid,
        (profile: {}) => {
          console.log("Kiosk profile retrieved: ", profile);
          context.commit("SET_PROFILE", profile);
          params.callback?.(profile);
        },
        (err: unknown) => {
          console.error("Kiosk profile retrieving error: ", err);
          params.errorCallback?.(err);
        }
      );
    },
    getKiosk: (
      context: {
        commit: (mutation: string, data?: {}) => void;
        rootState: unknown;
      },
      params: {
        uuid: string;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Kiosk retrieving...", _.pick(params, "uuid"));
      services.dcm?.getClient(
        params.uuid,
        (kiosk: {}) => {
          console.log("Kiosk retrieved: ", kiosk);
          context.commit("SET_KIOSK", kiosk);
          params.callback?.(kiosk);
        },
        (err: unknown) => {
          console.log("Kiosk retrieving error: ", err);
          params.errorCallback?.(err);
        }
      );
    },
    getSpecs: (
      context: {
        commit: (mutation: string, data?: []) => void;
        rootState: unknown;
      },
      params: {
        firmwareUuid: string;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Kiosk specs retrieving...", _.pick(params, "firmwareUuid"));
      services.dcm?.getFirmwareSpecList(
        params.firmwareUuid,
        (specs: []) => {
          console.log("Kiosk spec retrieved: ", specs);
          context.commit("SET_SPECS", specs);
          params.callback?.(specs);
        },
        (err: unknown) => {
          console.log("Kiosk specs retrieving error: ", err);
          params.errorCallback?.(err);
        }
      );
    },
    changeAudioLevels: (
      context: {
        commit: (mutation: string) => void;
        state: any;
        rootState: any;
        dispatch: Function;
      },
      params: {
        uuid: string;
        levels: { input?: number; output?: number };
        withoutProfileRefreshing?: boolean;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      const {
        uuid,
        levels,
        withoutProfileRefreshing,
        callback,
        errorCallback
      } = params;

      const properties = _.map(levels, (v, k) => {
        const spec: {
          //  eslint-disable-next-line @typescript-eslint/camelcase
          default_value: unknown;
          description: string;
          // eslint-disable-next-line @typescript-eslint/camelcase
          firmware_uuid: string;
          flags: number;
          name: string;
          title: string;
          // eslint-disable-next-line @typescript-eslint/camelcase
          type_hash: string;
        } | null = _.find(
          context.state.specs,
          o =>
            o.name ===
            (k === "input"
              ? audioInputVolumeOptionName
              : audioOutputVolumeOptionName)
        );

        if (!spec) {
          errorCallback?.();
          throw new Error("Unknown kiosk spec");
        }

        return {
          name:
            k === "input"
              ? audioInputVolumeOptionName
              : audioOutputVolumeOptionName,
          // eslint-disable-next-line @typescript-eslint/camelcase
          type_hash: spec.type_hash,
          value: v
        };
      });

      console.log("Changing kiosk audio levels: ", levels);

      context.dispatch("updateProperties", {
        uuid,
        properties,
        callback: (data: unknown) => {
          console.log("Kiosk audio levels updated: ", data);

          withoutProfileRefreshing
            ? callback?.(data)
            : context.dispatch("getProfile", {
                uuid,
                callback,
                errorCallback
              });
        },
        errorCallback: (err: unknown) => {
          console.error("Can not set audio levels: ", err);
          errorCallback?.(err);
        }
      });
    },
    updateProperties: (
      context: {
        commit: (mutation: string) => void;
        rootState: unknown;
      },
      params: {
        uuid: string;
        properties: { name: string; type_hash: string; value: unknown }[];
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log(
        "Kiosk properties updating...",
        _.pick(params, ["uuid", "properties"])
      );
      services.dcm?.updateClientProperties(
        params.uuid,
        params.properties,
        (data: unknown) => {
          console.log("Kiosk properties updated: ", data);
          params.callback?.(data);
        },
        (err: unknown) => {
          console.error("Kiosk properties updating error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    getTopics: (
      context: {
        commit: (mutation: string, data: any) => void;
        rootState: unknown;
      },
      params: {
        uuid: string;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Retrieving kiosk topics...");
      services.rem?.getKioskTopics(
        params.uuid,
        (topics: Record<string, TopicDTO>) => {
          console.log("Kiosk topics retrieved: ", topics);
          context.commit("SET_TOPICS", topics);
          params.callback?.(topics);
        },
        (err: unknown) => {
          console.error("Kiosk topics retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    }
  },
  mutations: {
    SET_LOCATION(state: KioskState, location: string) {
      state.location = location;
    },
    UNSET_LOCATION(state: KioskState) {
      state.location = null;
    },
    SET_PROFILE(
      state: KioskState,
      profile: { name: string; value: unknown }[]
    ) {
      state.profile = profile;
    },
    UNSET_PROFILE(state: KioskState) {
      state.profile = null;
    },
    SET_SPECS(state: KioskState, specs: []) {
      state.specs = specs;
    },
    UNSET_SPECS(state: KioskState) {
      state.specs = null;
    },
    SET_KIOSK(state: KioskState, kiosk: {}) {
      state.kiosk = kiosk;
    },
    UNSET_KIOSK(state: KioskState) {
      state.kiosk = null;
    },
    SET_ASPECT_RATIO(state: KioskState, aspectRatio: AspectRatioMode) {
      state.aspectRatio = aspectRatio;
    },
    UNSET_ASPECT_RATIO(state: KioskState) {
      state.aspectRatio = null;
    },
    SET_SCREEN_RESOLUTION(
      state: KioskState,
      resolution: {
        width: number;
        height: number;
      }
    ) {
      state.screenResolution = resolution;
    },
    UNSET_SCREEN_RESOLUTION(state: KioskState) {
      state.screenResolution = null;
    },
    SET_IS_MUTED(state: KioskState, isMuted: boolean) {
      state.isMuted = isMuted;
    },
    UNSET_IS_MUTED(state: KioskState) {
      state.isMuted = null;
    },
    SET_IS_SELF_VIEW_VISIBLE(state: KioskState, isVisible: boolean) {
      state.isSelfViewVisible = isVisible;
    },
    UNSET_IS_SELF_VIEW_VISIBLE(state: KioskState) {
      state.isSelfViewVisible = null;
    },
    SET_MICROPHONES(
      state: KioskState,
      microphones: { microphone: MediaDeviceInfo; isActive: boolean }[]
    ) {
      state.microphones = microphones;
    },
    UNSET_MICROPHONES(state: KioskState) {
      state.microphones = null;
    },
    SET_LAYOUTS(
      state: KioskState,
      layouts: { name: string; value: number; isActive: boolean }[]
    ) {
      state.layouts = layouts;
    },
    UNSET_LAYOUTS(state: KioskState) {
      state.layouts = null;
    },
    SET_ACCESSABILITY_MODE(state: KioskState, mode: boolean) {
      state.accessabilityMode = mode;
    },
    UNSET_ACCESSABILITY_MODE(state: KioskState) {
      state.accessabilityMode = null;
    },
    SET_TOPICS(state: KioskState, topics: Record<string, TopicDTO>) {
      state.topics = topics;
    },
    UNSET_TOPICS(state: KioskState) {
      state.topics = null;
    }
  }
};

const mediaInputsModule = {
  namespaced: true,
  state: (): MediaInputsState => ({
    cameras: null,
    defaultCamera: null,
    microphones: null,
    defaultMicrophone: null
  }),
  getters: {
    defaultCamera(state: MediaInputsState) {
      return (
        state.defaultCamera ?? localStorage.getItem(defaultCameraStorageKey)
      );
    },
    defaultMicrophone(state: MediaInputsState) {
      return (
        state.defaultMicrophone ??
        localStorage.getItem(defaultMicrophoneStorageKey)
      );
    }
  },
  actions: {
    getCameras: async (context: {
      commit: (
        mutation: string,
        cameras: {
          camera: MediaDeviceInfo;
          isActive: boolean;
        }[]
      ) => void;
      getters: any;
      rootGetters: any;
      rootState: any;
    }) => {
      console.log("Getting video cameras...");

      const telephonyType = context.rootState.telephonyType;

      if (
        (telephonyType === TelephonyType.webex &&
          context.rootGetters["webex/meeting"]) ||
        (telephonyType === TelephonyType.twilio &&
          context.rootGetters["twilio/room"])
      ) {
        const cameras =
          telephonyType === TelephonyType.webex
            ? await services.webex?.getCameras(
                context.rootGetters["webex/meeting"]
              )
            : await services.twilio?.getCameras(
                context.rootGetters["twilio/room"]
              );

        console.log("Got video cameras: ", cameras);

        context.commit("SET_CAMERAS", cameras!);

        return cameras;
      }

      console.log("navigator used for cameras getting");

      const cameras = _.map(
        _.filter(
          await navigator.mediaDevices.enumerateDevices(),
          c => c.kind === "videoinput"
        ),
        c => ({
          camera: c,
          isActive: c.deviceId === context.getters.defaultCamera
        })
      );

      console.log("Got video cameras: ", cameras);

      context.commit("SET_CAMERAS", cameras!);

      return cameras;
    },
    switchCamera: async (
      context: {
        commit: (mutation: string, data: any, params?: {}) => void;
        rootGetters: any;
        rootState: any;
      },
      deviceId: string
    ) => {
      console.log(`Switching video camera to "${deviceId}"...`);

      const telephonyType = context.rootState.telephonyType;

      if (
        ![TelephonyType.webex, TelephonyType.twilio].includes(telephonyType)
      ) {
        throw new Error("Unsupported telephony type");
      }

      if (
        telephonyType === TelephonyType.webex &&
        context.rootGetters["webex/meeting"]
      ) {
        await services.webex?.switchCamera(
          context.rootGetters["webex/meeting"],
          deviceId
        );
      } else if (
        telephonyType === TelephonyType.twilio &&
        context.rootGetters["twilio/room"]
      ) {
        const media = await services.twilio?.switchCamera(
          context.rootGetters["twilio/room"],
          deviceId
        );

        context.commit(
          "twilio/SET_MEDIA",
          {
            type: "local",
            media
          },
          { root: true }
        );
      }
      console.log(`Switched video camera to "${deviceId}"`);
    },
    enableCamera: async (context: {
      commit: (mutation: string, data: any) => void;
      dispatch: Function;
      rootGetters: any;
      rootState: any;
    }) => {
      console.log("Enabling camera...");

      const telephonyType = context.rootState.telephonyType;

      if (
        ![TelephonyType.webex, TelephonyType.twilio].includes(telephonyType)
      ) {
        throw new Error("Unsupported telephony type");
      }

      if (telephonyType === TelephonyType.webex) {
        await services.webex?.enableCamera(
          context.rootGetters["webex/meeting"]
        );
      } else {
        const room = context.rootGetters["twilio/room"];
        if (!room) {
          return;
        }

        services.twilio?.enableCamera(room);

        context.dispatch(
          "setEndpointStatus",
          {
            status: EndpointStatus.connected + EndpointStatus.videoOn
          },
          { root: true }
        );
      }

      console.log("Camera enabled");
    },
    disableCamera: async (context: {
      commit: (mutation: string, data: any) => void;
      dispatch: Function;
      rootGetters: any;
      rootState: any;
    }) => {
      console.log("Disabling camera...");

      const telephonyType = context.rootState.telephonyType;

      if (
        ![TelephonyType.webex, TelephonyType.twilio].includes(telephonyType)
      ) {
        throw new Error("Unsupported telephony type");
      }

      if (telephonyType === TelephonyType.webex) {
        await services.webex?.disableCamera(
          context.rootGetters["webex/meeting"]
        );
      } else {
        const room = context.rootGetters["twilio/room"];
        if (!room) {
          return;
        }

        services.twilio?.disableCamera(room);

        context.dispatch(
          "setEndpointStatus",
          {
            status: EndpointStatus.connected
          },
          { root: true }
        );
      }

      console.log("Camera disabled");
    },
    getMicrophones: async (context: {
      commit: (
        mutation: string,
        cameras: {
          microphone: MediaDeviceInfo;
          isActive: boolean;
        }[]
      ) => void;
      getters: any;
      rootGetters: any;
      rootState: any;
    }) => {
      console.log("Getting microphones...");

      const telephonyType = context.rootState.telephonyType;

      if (
        (telephonyType === TelephonyType.webex &&
          context.rootGetters["webex/meeting"]) ||
        (telephonyType === TelephonyType.twilio &&
          context.rootGetters["twilio/room"])
      ) {
        const microphones =
          telephonyType === TelephonyType.webex
            ? await services.webex?.getMicrophones(
                context.rootGetters["webex/meeting"]
              )
            : await services.twilio?.getMicrophones(
                context.rootGetters["twilio/room"]
              );

        console.log("Got microphones: ", microphones);

        context.commit("SET_MICROPHONES", microphones!);

        return microphones;
      }

      console.log("navigator used for microphones getting");

      const microphones = _.map(
        _.filter(
          await navigator.mediaDevices.enumerateDevices(),
          c => c.kind === "audioinput"
        ),
        m => ({
          microphone: m,
          isActive: m.deviceId === context.getters.defaultMicrophone
        })
      );

      console.log("Got microphones: ", microphones);

      context.commit("SET_MICROPHONES", microphones!);

      return microphones;
    },
    switchMicrophone: async (
      context: {
        commit: (mutation: string, data: any) => void;
        rootGetters: any;
        rootState: any;
      },
      deviceId: string
    ) => {
      console.log(`Switching microphone to "${deviceId}"...`);

      const telephonyType = context.rootState.telephonyType;

      if (
        ![TelephonyType.webex, TelephonyType.twilio].includes(telephonyType)
      ) {
        throw new Error("Unsupported telephony type");
      }

      if (
        telephonyType === TelephonyType.webex &&
        context.rootGetters["webex/meeting"]
      ) {
        await services.webex?.switchMicrophone(
          context.rootGetters["webex/meeting"],
          deviceId
        );
      } else if (
        telephonyType === TelephonyType.twilio &&
        context.rootGetters["twilio/room"]
      ) {
        await services.twilio?.switchMicrophone(
          context.rootGetters["twilio/room"],
          deviceId
        );
      }

      console.log(`Switched microphone to "${deviceId}"`);
    }
  },
  mutations: {
    SET_CAMERAS(
      state: MediaInputsState,
      cameras: {
        camera: MediaDeviceInfo;
        isActive: boolean;
      }[]
    ) {
      state.cameras = cameras;
    },
    UNSET_CAMERAS(state: MediaInputsState) {
      state.cameras = null;
    },
    SET_DEFAULT_CAMERA(state: MediaInputsState, deviceId: string) {
      state.defaultCamera = deviceId;
      localStorage.setItem(defaultCameraStorageKey, deviceId);
    },
    UNSET_DEFAULT_CAMERA(state: MediaInputsState) {
      state.defaultCamera = null;
      localStorage.removeItem(defaultCameraStorageKey);
    },
    SET_MICROPHONES(
      state: MediaInputsState,
      microphones: {
        microphone: MediaDeviceInfo;
        isActive: boolean;
      }[]
    ) {
      state.microphones = microphones;
    },
    UNSET_MICROPHONES(state: MediaInputsState) {
      state.microphones = null;
    },
    SET_DEFAULT_MICROPHONE(state: MediaInputsState, deviceId: string) {
      state.defaultMicrophone = deviceId;
      localStorage.setItem(defaultMicrophoneStorageKey, deviceId);
    },
    UNSET_DEFAULT_MICROPHONE(state: MediaInputsState) {
      state.defaultMicrophone = null;
      localStorage.removeItem(defaultMicrophoneStorageKey);
    }
  }
};

const sessionModule = {
  namespaced: true,
  state: (): SessionState => ({
    info: null,
    party: null,
    visualHints: null,
    visualHint: null,
    qrCode: null,
    textHint: null,
    isMuted: null,
    callRequestReceivedAt: null,
    hangupRequestReceivedAt: null,
    requestedActions: null,
    room: null,
    rooms: null,
    ticket: null,
    notifications: null,
    recordTranslation: null
  }),
  actions: {
    getInfo: (
      context: {
        commit: (mutation: string, data: {}) => void;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Session info retrieving...");
      services.rem?.getSessionInfo(
        (info: SessionInfo) => {
          console.log("Session info retrieved: ", info);
          context.commit("SET_INFO", info);
          // if (info.companion_endpoint_status?.extension) {
          //   context.commit("SET_PARTY", info.companion_endpoint_status?.extension);
          // }
          params?.callback?.(info);
        },
        (err: unknown) => {
          console.error("Session info retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    sendJsonMessage: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: unknown;
      },
      params: {
        message: {};
        correlationId?: string;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log(
        "Sending JSON message...",
        _.pick(params, ["message", "correlationId"])
      );
      services.rem?.sendJsonMessage(
        params.message,
        params.correlationId ?? null,
        (data: unknown) => {
          console.log("JSON message sent: ", data);
          params.callback?.(data);
        },
        (err: unknown) => {
          console.error("JSON message sending error: ", err);
          params.errorCallback?.(err);
        }
      );
    },
    sendJoinRequest: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: unknown;
      },
      params: {
        webexRoomId: string;
        personId: string;
        deviceId: string;
        callback: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log(
        "Sending on call join request...",
        _.pick(params, ["webexRoomId", "personId", "deviceId"])
      );

      services.rem?.sendCallJoinRequest(
        params.deviceId,
        params.personId,
        params.webexRoomId,
        (data: unknown) => {
          console.log("Call join request sent: ", data);
          params?.callback?.(data);
        },
        (err: unknown) => {
          console.error("Call join request sending error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    hold: async (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: any;
        rootGetters: any;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending on hold request...");

      services.rem?.holdCall(
        (data: unknown) => {
          console.log("Hold request sent: ", data);
          params?.callback?.(data);
        },
        (err: unknown) => {
          console.error("Hold request sending error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    resume: async (
      context: {
        commit: (mutation: string, data?: number) => void;
        state: SessionState;
        rootState: any;
        rootGetters: any;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending resume request...");
      services.rem?.resumeCall(
        (data: unknown) => {
          console.log("Resume request sent: ", data);
          params?.callback?.(data);
        },
        (err: unknown) => {
          console.error("Resume request sending error: ");
          params?.errorCallback?.(err);
        }
      );
    },
    hangup: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending hangup request...");
      services.rem?.hangup(
        (data: unknown) => {
          console.log("Hangup request sent: ", data);
          params?.callback?.(data);
        },
        (err: unknown) => {
          console.log("Hangup request sending error ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    getRoom: (
      context: {
        commit: (mutation: string, data?: string) => void;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Room retrieving...");

      services.rem?.getRoom(
        (uuid: string) => {
          console.log("Room retrieved: ", uuid);
          context.commit("SET_ROOM", uuid);
          params?.callback?.(uuid);
        },
        (err: unknown) => {
          console.log("Room retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    getRooms: (
      context: {
        commit: (mutation: string, data: RoomDTO[]) => void;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Rooms retrieving...");

      services.rem?.getRooms(
        (rooms: RoomDTO[]) => {
          console.log("Rooms retrieved: ", rooms);
          context.commit("SET_ROOMS", rooms);
          params?.callback?.(rooms);
        },
        (err: unknown) => {
          console.log("Rooms retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    setActiveRoom: (
      context: {
        commit: (mutation: string, data: RoomDTO[]) => void;
        rootState: unknown;
      },
      params: {
        id: string;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log(`Setting room ${params.id} as active...`);

      services.rem?.setActiveRoom(
        params.id,
        (data: unknown) => {
          console.log(`Set room ${params.id} as active: `, data);
          params?.callback?.(data);
        },
        (err: unknown) => {
          console.log(`Setting room ${params.id} as active error: `, err);
          params?.errorCallback?.(err);
        }
      );
    },
    removeRoom: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Room removing...");

      services.rem?.removeRoom(
        (data: unknown) => {
          console.log("Room removed: ", data);
          context.commit("UNSET_ROOM");
          params?.callback?.(data);
        },
        (err: unknown) => {
          console.log("Room removing request sending error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    survey: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending survey request...");
      services.rem?.startSurvey(
        (data: unknown) => {
          console.log("Survey request sent: ", data);
          params?.callback?.(data);
        },
        (err: unknown) => {
          console.log("Survey request sending error ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    answer: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: unknown;
      },
      params: {
        callback: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending answer request...");
      services.rem?.answerCall(
        (data: unknown) => {
          console.log("Answer request sent: ", data);
          params?.callback?.(data);
        },
        (err: unknown) => {
          console.log("Answer request sending error ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    transfer: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: unknown;
      },
      params: {
        expert: ExpertDTO;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending transfer request: ", _.pick(params, "expert"));
      services.rem?.transferCall(
        params.expert,
        (data: unknown) => {
          console.log("Transfer request sent: ", data);
          params?.callback?.(data);
        },
        (err: unknown) => {
          console.log("Transfer request sending error ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    changeTopic: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: unknown;
      },
      params: {
        topic: string;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending topic change request: ", _.pick(params, "topic"));
      services.rem?.changeCallTopic(
        params.topic,
        (data: unknown) => {
          console.log("Topic change request sent: ", data);
          params?.callback?.(data);
        },
        (err: unknown) => {
          console.log("Topic change request sending error ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    request: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: unknown;
      },
      params: {
        deviceId: string;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending call request: ", _.pick(params, "deviceId"));
      services.rem?.requestCall(
        params.deviceId,
        (requestId: number) => {
          console.log("Call request sent: ", requestId);

          services.rem?.setSignalCallback(
            RemSignal.resultReady,
            (data: [string, number, boolean, string]) => {
              const [, reqId, isSuccess] = data;

              if (requestId !== reqId) {
                return;
              }

              isSuccess
                ? params.callback?.(data)
                : params.errorCallback?.(data);
            }
          );

          console.log(`${RemSignal.resultReady} signal callback set`);
        },
        (err: unknown) => {
          console.error("Call request error: ", err);
          params.errorCallback?.(err);
        }
      );
    },
    sendFeedback: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: unknown;
      },
      params: {
        data: {
          rate: number;
          expertId: string;
          expertExt: string;
          clientExt: string;
          sessionStart: number;
        };
        correlationId?: string;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending feedback...", params.data);
      services.dcm?.sendFeedback(
        params.data,
        (data: unknown) => {
          console.log("Feedback sent: ", data);
          params.callback?.(data);
        },
        (err: unknown) => {
          console.error("Sending feedback error: ", err);
          params.errorCallback?.(err);
        }
      );
    },
    getVisualHints: async (context: {
      commit: (
        mutation: string,
        data: Record<
          "common" | "kiosk" | "expert",
          {
            baseUrl: string;
            files: FileStat[];
          }
        >
      ) => void;
      state: SessionState;
      rootState: any;
      rootGetters: any;
    }) => {
      const propertyName = "visual.hint.directory.url";
      const baseUrl =
        _.find(context.rootState.auth.profile, v => v.name === propertyName)
          ?.value ??
        _.find(context.rootState.auth.specs, v => v.name === propertyName)
          ?.default_value;

      if (!baseUrl) {
        console.error(`Can not find "${propertyName}" property`);
        return;
      }

      const clientType = context.rootState.config.config.clientType;

      console.log("Visual hints retrieving...");

      const hints = await services.webdav!.getVisualHints({
        baseUrl,
        expertId:
          clientType === "expert"
            ? context.rootGetters["auth/clientId"]
            : context.state.info?.client_id,
        kioskId:
          clientType === "kiosk"
            ? context.rootGetters["auth/clientId"]
            : context.state.info?.client_id
      });

      console.log("Visual hints retrieved: ", hints);

      context.commit("SET_VISUAL_HINTS", hints);
      return hints;
    },
    sendVisualHint: async (
      context: {
        state: SessionState;
        dispatch: Function;
        commit: Function;
        rootState: any;
        rootGetters: any;
      },
      params: {
        data: VisualHintDTO;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending visual hint...", params.data);

      context.dispatch("sendJsonMessage", {
        message: {
          type: MessageType.presentableInfo,
          format: "image-url",
          layout: params.data.position,
          duration: params.data.duration,
          data: params.data.url
        },
        callback: (data: unknown) => {
          console.log("Visual hint has been successfully sent.");
          params.callback?.(data);
        },
        errorCallback: (err: string | null = null) => {
          console.error("Visual hint sending error: ", err);
          params.errorCallback?.(err);
        }
      });
    },
    requestAction: async (
      context: {
        state: SessionState;
        dispatch: Function;
        commit: Function;
        rootState: any;
        rootGetters: any;
      },
      params: {
        action: RequestedAction;
        payload?: any;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending action request...", params.action);

      const messageType = _.fromPairs([
        [RequestedAction.sendFile, MessageType.actionSendFile],
        [
          RequestedAction.videoChatNextCamera,
          MessageType.actionVideoChatNextCamera
        ],
        [
          RequestedAction.videoChatEnableCamera,
          MessageType.actionVideoChatEnableCamera
        ],
        [
          RequestedAction.videoChatDisableCamera,
          MessageType.actionVideoChatDisableCamera
        ],
        [
          RequestedAction.videoChatSetMicrophone,
          MessageType.actionVideoChatSetMicrophone
        ],
        [RequestedAction.sendSignature, MessageType.actionSendSignature],
        [RequestedAction.cancelSignature, MessageType.actionCancelSignature],
        [RequestedAction.sendTextMessage, MessageType.actionSendTextMessage],
        [
          RequestedAction.sendCallInviteLink,
          MessageType.actionSendCallInviteLink
        ],
        [
          RequestedAction.changeAspectRatio,
          MessageType.actionChangeAspectRatio
        ],
        [
          RequestedAction.screenSharingStart,
          MessageType.actionScreenSharingStart
        ],
        [
          RequestedAction.screenSharingStop,
          MessageType.actionScreenSharingStop
        ],
        [RequestedAction.selfViewShow, MessageType.actionSelfViewShow],
        [RequestedAction.selfViewHide, MessageType.actionSelfViewHide],
        [
          RequestedAction.geolocationSharingStart,
          MessageType.actionGeolocationSharingStart
        ],
        [
          RequestedAction.geolocationSharingStop,
          MessageType.actionGeolocationSharingStop
        ],
        [RequestedAction.setLayout, MessageType.actionSetLayout],
        [
          RequestedAction.setAccessabilityMode,
          MessageType.actionSetAccessabilityMode
        ]
      ])[params.action];

      context.dispatch("sendJsonMessage", {
        message: _.assign(
          {
            type: messageType
          },
          _.pick(params, "payload")
        ),
        callback: (data: unknown) => {
          console.log("Action request has been successfully sent.");
          params.callback?.(data);
        },
        errorCallback: (err: string | null = null) => {
          console.error("Action request sending error: ", err);
          params.errorCallback?.(err);
        }
      });
    },
    sendQrCode: async (
      context: {
        state: SessionState;
        dispatch: Function;
        commit: Function;
        rootState: any;
        rootGetters: any;
      },
      params: {
        data: QrCodeDTO;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending QR code...", params.data);

      context.dispatch("sendJsonMessage", {
        message: {
          type: MessageType.presentableInfo,
          format: "qrcode",
          duration: params.data.duration,
          data: btoa(unescape(encodeURIComponent(params.data.text)))
        },
        callback: (data: unknown) => {
          console.log("QR code has been successfully sent.");
          params.callback?.(data);
        },
        errorCallback: (err: string | null = null) => {
          console.error("QR code sending error: ", err);
          params.errorCallback?.(err);
        }
      });
    },
    sendTextHint: async (
      context: {
        state: SessionState;
        dispatch: Function;
        commit: Function;
        rootState: any;
        rootGetters: any;
      },
      params: {
        data: { duration: number; text: string };
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending visual hint...", params.data);

      context.dispatch("sendJsonMessage", {
        message: {
          type: MessageType.presentableInfo,
          format: "plain-text",
          duration: params.data.duration,
          data: params.data.text
        },
        callback: (data: unknown) => {
          console.log("Text hint has been successfully sent.");
          params.callback?.(data);
        },
        errorCallback: (err: string | null = null) => {
          console.error("Text hint sending error: ", err);
          params.errorCallback?.(err);
        }
      });
    },
    mute: async (context: {
      commit: (mutation: string, data?: any, params?: {}) => void;
      rootState: any;
      rootGetters: any;
    }) => {
      const telephonyType = context.rootState.telephonyType;

      if (telephonyType === TelephonyType.webex) {
        console.log("Webex meeting audio muting...");

        await context.rootGetters["webex/meeting"]?.muteAudio();

        console.log("Webex meeting audio muted");
      } else if (telephonyType === TelephonyType.twilio) {
        context.rootGetters[
          "twilio/room"
        ]?.localParticipant?.audioTracks?.forEach(
          (publication: LocalAudioTrackPublication) =>
            publication.track.disable()
        );
      }

      context.commit("SET_IS_MUTED", true);
    },
    unmute: async (context: {
      commit: (mutation: string, data?: any, params?: {}) => void;
      rootState: any;
      rootGetters: any;
    }) => {
      const telephonyType = context.rootState.telephonyType;

      if (telephonyType === TelephonyType.webex) {
        console.log("Webex meeting audio unmuting...");

        await context.rootGetters["webex/meeting"]?.unmuteAudio();

        console.log("Webex meeting audio unmuted");
      } else if (telephonyType === TelephonyType.twilio) {
        context.rootGetters[
          "twilio/room"
        ]?.localParticipant?.audioTracks?.forEach(
          (publication: LocalAudioTrackPublication) =>
            publication.track.enable()
        );
      }
      context.commit("SET_IS_MUTED", false);
    },
    setStatistic: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootGetters: any;
        rootState: any;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      const telephonyType = context.rootState.telephonyType;

      if (telephonyType !== TelephonyType.webex) {
        console.error("Statistic supported only for Webex telephony");
        return;
      }

      const meeting = context.rootGetters["webex/meeting"];

      if (!meeting) {
        console.error("Empty Webex meeting");
        return;
      }

      const rawStatistic = services.webex?.getStatistic(
        meeting as WebexMeeting
      );

      const mode = context.rootGetters["auth/property"](
        "webex.statistics.mode"
      );

      const calcBitRate = (rate: number[]) =>
          _.max([_.last(rate) ?? 0, 0])! / (8 * 1024),
        calcFrameRate = (rate: number[]) =>
          _.max([_.last(rate) ?? 0, 0])! / 100;

      const statistic = {
        receive: _.assign(
          [StatisticMode.audioOnly, StatisticMode.audioAndVideo].includes(mode)
            ? {
                audio: {
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  bit_rate: calcBitRate(rawStatistic!.audioReceive.bitRate),
                  jitter: _.last(rawStatistic!.audioReceive.jitter) ?? 0,
                  latency: _.last(rawStatistic!.audioReceive.latency) ?? 0,
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  packets_lost_avg: rawStatistic!.audioReceive.packetsLost
                    .length
                    ? _.mean(rawStatistic!.audioReceive.packetsLost)
                    : 0,
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  packets_lost_max:
                    _.max(rawStatistic!.audioReceive.packetsLost) ?? 0
                }
              }
            : {},
          [StatisticMode.videoOnly, StatisticMode.audioAndVideo].includes(mode)
            ? {
                video: {
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  bit_rate: calcBitRate(rawStatistic!.videoReceive.bitRate),
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  frame_rate: calcFrameRate(
                    rawStatistic!.videoReceive.frameRate
                  ),
                  jitter: _.last(rawStatistic!.videoReceive.jitter) ?? 0,
                  latency: _.last(rawStatistic!.videoReceive.latency) ?? 0,
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  packets_lost_avg: rawStatistic!.videoReceive.packetsLost
                    .length
                    ? _.mean(rawStatistic!.videoReceive.packetsLost)
                    : 0,
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  packets_lost_max:
                    _.max(rawStatistic!.videoReceive.packetsLost) ?? 0,
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  resolution_height:
                    _.last(rawStatistic!.videoReceive.resolutionHeight) ?? 0,
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  resolution_width:
                    _.last(rawStatistic!.videoReceive.resolutionWidth) ?? 0
                }
              }
            : {}
        ),
        transmit: _.assign(
          [StatisticMode.audioOnly, StatisticMode.audioAndVideo].includes(mode)
            ? {
                audio: {
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  bit_rate: calcBitRate(rawStatistic!.audioTransmit.bitRate),
                  jitter: _.last(rawStatistic!.audioTransmit.jitter) ?? 0,
                  latency: _.last(rawStatistic!.audioTransmit.latency) ?? 0,
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  packets_lost_avg: rawStatistic!.audioTransmit.packetsLost
                    .length
                    ? _.mean(rawStatistic!.audioTransmit.packetsLost)
                    : 0,
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  packets_lost_max:
                    _.max(rawStatistic!.audioTransmit.packetsLost) ?? 0
                }
              }
            : {},
          [StatisticMode.videoOnly, StatisticMode.audioAndVideo].includes(mode)
            ? {
                video: {
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  bit_rate: calcBitRate(rawStatistic!.videoTransmit.bitRate),
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  frame_rate: calcFrameRate(
                    rawStatistic!.videoTransmit.frameRate
                  ),
                  jitter: _.last(rawStatistic!.videoTransmit.jitter) ?? 0,
                  latency: _.last(rawStatistic!.videoTransmit.latency) ?? 0,
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  packets_lost_avg: rawStatistic!.videoTransmit.packetsLost
                    .length
                    ? _.mean(rawStatistic!.videoTransmit.packetsLost)
                    : 0,
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  packets_lost_max:
                    _.max(rawStatistic!.videoTransmit.packetsLost) ?? 0,
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  resolution_height:
                    _.last(rawStatistic!.videoTransmit.resolutionHeight) ?? 0,
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  resolution_width:
                    _.last(rawStatistic!.videoTransmit.resolutionWidth) ?? 0
                }
              }
            : {}
        )
      };

      console.log("Sending statistic", statistic);
      services.rem?.setStatistic(
        statistic,
        (data: unknown) => {
          console.log("Statistic sent: ", data);
          params?.callback?.(data);
        },
        (err: unknown) => {
          console.error("Sending statistic error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    setClientContext: (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootGetters: any;
        rootState: any;
      },
      params: {
        context: object;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Setting client context...", params.context);
      services.rem?.setClientContext(
        params.context,
        (data: unknown) => {
          console.log("Client context sent: ", data);
          params?.callback?.(data);
        },
        (err: unknown) => {
          console.error("Sending client context error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    startTranscription: async (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootGetters: any;
        rootState: any;
      },
      params: {
        stream: MediaStream;
        sourceLanguage: AwsLanguage;
        callback?: Function;
      }
    ) => {
      console.log(
        "Start audio stream transcription... ",
        _.omit(params, "callback")
      );

      await services.aws?.startTranscription(
        params.stream,
        params.sourceLanguage,
        (data: { text: string; isPartial: boolean }) => {
          console.log("Got audio stream transcription: ", data);
          params.callback?.(data);
        }
      );
    },
    stopTranscription: async (context: {
      commit: (mutation: string, data?: number) => void;
      rootGetters: any;
      rootState: any;
    }) => {
      console.log("Stopping audio stream transcription...");

      await services.aws?.stopTranscription();

      console.log("Audio stream transcription stopped");
    },
    translate: async (
      context: {
        commit: (mutation: string, data?: number) => void;
        rootGetters: any;
        rootState: any;
      },
      params: {
        text: string;
        sourceLanguage: AwsLanguage;
        targetLanguage: AwsLanguage;
      }
    ) => {
      console.log("Translating...", params);

      const data = await services.aws?.translate(
        params.text,
        params.sourceLanguage,
        params.targetLanguage
      );

      console.log("Translated: ", data);

      return data;
    }
  },
  mutations: {
    SET_INFO(state: SessionState, info: SessionInfo) {
      state.info = info;
    },
    UNSET_INFO(state: SessionState) {
      state.info = null;
    },
    SET_VISUAL_HINTS(
      state: SessionState,
      hints: Record<
        "common" | "kiosk" | "expert",
        {
          baseUrl: string;
          files: FileStat[];
        }
      >
    ) {
      state.visualHints = hints;
    },
    UNSET_VISUAL_HINTS(state: SessionState) {
      state.visualHints = null;
    },
    SET_TEXT_HINT(
      state: SessionState,
      hint: { duration: number; text: string }
    ) {
      state.textHint = hint;
    },
    UNSET_TEXT_HINT(state: SessionState) {
      state.textHint = null;
    },
    SET_VISUAL_HINT(state: SessionState, hint: VisualHintDTO) {
      state.visualHint = hint;
    },
    UNSET_VISUAL_HINT(state: SessionState) {
      state.visualHint = null;
    },
    SET_QR_CODE(state: SessionState, code: QrCodeDTO) {
      state.qrCode = code;
    },
    UNSET_QR_CODE(state: SessionState) {
      state.qrCode = null;
    },
    SET_PARTY(state: SessionState, party: string) {
      state.party = party;
    },
    UNSET_PARTY(state: SessionState) {
      state.party = null;
    },
    SET_IS_MUTED(state: SessionState, isMuted: boolean) {
      state.isMuted = isMuted;
    },
    UNSET_IS_MUTED(state: SessionState) {
      state.isMuted = null;
    },
    SET_ROOM(state: SessionState, room: RoomDTO) {
      state.room = room;
    },
    UNSET_ROOM(state: SessionState) {
      state.room = null;
    },
    SET_ROOMS(state: SessionState, rooms: RoomDTO[]) {
      state.rooms = rooms;
    },
    UNSET_ROOMS(state: SessionState) {
      state.rooms = null;
    },
    SET_CALL_REQUEST_RECEIVED_AT(state: SessionState, timestamp: number) {
      state.callRequestReceivedAt = timestamp;
    },
    UNSET_CALL_REQUEST_RECEIVED_AT(state: SessionState) {
      state.callRequestReceivedAt = null;
    },
    SET_HANGUP_REQUEST_RECEIVED_AT(state: SessionState, timestamp: number) {
      state.hangupRequestReceivedAt = timestamp;
    },
    UNSET_HANGUP_REQUEST_RECEIVED_AT(state: SessionState) {
      state.hangupRequestReceivedAt = null;
    },
    SET_REQUESTED_ACTION(
      state: SessionState,
      action: { action: RequestedAction; payload?: any }
    ) {
      state.requestedActions = (state.requestedActions ?? []).concat(action);
    },
    UNSET_REQUESTED_ACTION(state: SessionState, action: RequestedAction) {
      state.requestedActions = _.filter(
        state.requestedActions ?? [],
        a => a.action !== action
      );
    },
    UNSET_REQUESTED_ACTIONS(state: SessionState) {
      state.requestedActions = null;
    },
    SET_TICKET(state: SessionState, ticket: object) {
      state.ticket = ticket;
    },
    SET_NOTIFICATION(
      state: SessionState,
      notification: { text: string; timestamp: number }
    ) {
      state.notifications = (state.notifications ?? []).concat([notification]);
    },
    UNSET_NOTIFICATIONS(state: SessionState) {
      state.notifications = null;
    },
    SET_RECORD_TRANSLATION(
      state: SessionState,
      translation: { text: string; audio?: Uint8Array }
    ) {
      state.recordTranslation = translation;
    },
    UNSET_RECORD_TRANSLATION(state: SessionState) {
      state.recordTranslation = null;
    }
  },
  modules: {
    kiosk: kioskModule,
    mediaInputs: mediaInputsModule
  }
};

const sharingModule = {
  namespaced: true,
  state: (): SharingState => ({
    local: {
      stream: null,
      streamType: null,
      file: null,
      peer: null,
      isInteractive: null,
      correlationId: null,
      candidates: null,
      candidatesTimer: null
    },
    remote: {
      stream: null,
      file: null,
      peer: null,
      isInteractive: null,
      correlationId: null
    }
  }),
  actions: {
    shareStream: async (
      context: {
        commit: (mutation: string, data?: unknown) => void;
        dispatch: Function;
        rootGetters: any;
        state: SharingState;
        rootState: unknown;
      },
      params: {
        type: "screen" | "barcode";
        barcodeCallbacks?: {
          connected?: Function;
          inputReceived?: Function;
        };
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log(`Starting ${params.type} sharing...`);

      if (context.state.local.stream) {
        context.dispatch("stopStreamSharing");
      }

      let stream: MediaStream | null = null;

      if (params.type === "screen") {
        const dialogDefaultTab: ScreenSharingDialogTab | null = context.rootGetters[
          "auth/property"
        ]("screen.sharing.dialog.default.tab") as ScreenSharingDialogTab | null;

        try {
          /* eslint-disable */
          // @ts-ignore
          stream = await navigator.mediaDevices.getDisplayMedia({
            /* eslint-enable */
            video: _.assign(
              {
                cursor: "always"
              },
              [null, ScreenSharingDialogTab.undefined].includes(
                dialogDefaultTab
              )
                ? {}
                : {
                    displaySurface: ScreenSharingDialogTab[dialogDefaultTab!]
                  }
            ),
            audio: false
          });
        } catch (err) {
          console.error("Screen sharing error: ", err);
          if (err instanceof DOMException && err.name === "NotAllowedError") {
            return;
          }
          params?.errorCallback?.(err);
        }
      } else {
        const black = ({ width = 128, height = 128 } = {}) => {
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          canvas.getContext("2d")?.fillRect?.(0, 0, width, height);
          /* eslint-disable */
          // @ts-ignore
          const stream = canvas.captureStream();
          /* eslint-enable */
          return _.assign(stream.getVideoTracks()[0], { enabled: false });
        };

        stream = new MediaStream([black()]);
      }

      if (!stream) {
        console.error("Can not get screen sharing screen");
        params?.errorCallback?.();
        return;
      }

      if (context.rootGetters.endpointStatusCode !== EndpointStatus.connected) {
        console.error(
          `Do not start screen sharing, endpoint status should be ${EndpointStatus.connected}`
        );
        return;
      }

      context.commit("SET_CORRELATION_ID", {
        type: "local",
        id: `{${uuidv4()}}`
      });

      stream.getVideoTracks()[0].addEventListener("ended", () => {
        console.log("Shared video track stopped");

        context.dispatch("stopStreamSharing");
      });

      console.log("RTC configs retrieving...");

      services.rem?.getRtcConnectionConfig(
        (config: {
          iceServers: {
            password: string;
            urls: string | string[];
            username: string;
          }[];
        }) => {
          console.log("RTC connection config retrieved: ", config);
          const rtcConfiguration: RTCConfiguration | null = config.iceServers
            ?.length
            ? {
                iceServers: _.map(config.iceServers, s => ({
                  username: s.username,
                  credential: s.password,
                  urls: s.urls
                }))
              }
            : null;

          const peer = new Peer(
            _.assign(
              {
                initiator: true,
                stream: stream!
              },
              rtcConfiguration ? { config: rtcConfiguration } : {}
            )
          );
          peer.on("signal", (data: any) => {
            console.log("RTC peer signal received: ", data);
            const signalType = data.type;
            if (!["offer", "candidate"].includes(signalType)) {
              return;
            }
            context.dispatch(
              "session/sendJsonMessage",
              {
                message:
                  signalType === "candidate"
                    ? {
                        type: MessageType.desktopSharingIceCandidate,
                        iceCandidate: {
                          candidate: data.candidate.candidate,
                          sdpMid: data.candidate.sdpMid,
                          sdpMLineIdx: data.candidate.sdpMLineIndex
                        }
                      }
                    : {
                        type: MessageType.desktopSharingStart,
                        rtcOffer: data.sdp,
                        hints: {
                          isVideoDisabled: params.type === "barcode"
                        },
                        connectionConfig: rtcConfiguration
                          ? {
                              iceServers: _.map(
                                rtcConfiguration.iceServers,
                                s => ({
                                  username: s.username,
                                  urls: s.urls,
                                  password: s.credential
                                })
                              )
                            }
                          : {}
                      },
                correlationId: context.state.local.correlationId,
                callback: (data: unknown) => {
                  console.log(
                    `${
                      signalType === "candidate"
                        ? "ICE candidate "
                        : "Screen sharing offer "
                    } request has been successfully sent.`
                  );
                },
                errorCallback: (err: string | null = null) => {
                  console.error(
                    `${
                      signalType === "candidate"
                        ? "ICE candidate "
                        : "Screen sharing offer "
                    } request sending error: `,
                    err
                  );

                  if (signalType === "offer") {
                    context.dispatch("stopStreamSharing");

                    params?.errorCallback?.();
                  }
                }
              },
              { root: true }
            );
          });

          if (params.type === "barcode") {
            peer.on("connect", () => {
              peer.send(
                JSON.stringify({
                  type: "state",
                  videoDisabled: true,
                  interactive: true
                })
              );

              params.barcodeCallbacks?.connected?.();
            });

            peer.on("data", (data: string) => {
              console.log("Peer data received:", data);

              const dataParsed = JSON.parse(data) as {
                type: string;
                down: boolean;
                key: string;
              };

              if (dataParsed.type === "key") {
                params.barcodeCallbacks?.inputReceived?.(dataParsed);
              }
            });
          }

          context.commit("SET_STREAM", {
            type: "local",
            stream: stream!
          });
          context.commit("SET_STREAM_TYPE", params.type);
          context.commit("SET_PEER", { type: "local", peer });

          params?.callback?.();
        },
        (err: unknown) => {
          console.error("RTC connection config retrieving error ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    stopStreamSharing: (context: {
      commit: (mutation: string, data?: unknown) => void;
      dispatch: Function;
      state: SharingState;
      rootState: unknown;
    }) => {
      const cb = () => {
        if (context.state.local.peer) {
          context.commit("UNSET_PEER", "local");
        }
        if (context.state.local.stream) {
          context.commit("UNSET_STREAM", "local");
        }
        if (context.state.local.streamType) {
          context.commit("UNSET_STREAM_TYPE");
        }
        if (context.state.local.correlationId) {
          context.commit("UNSET_CORRELATION_ID", "local");
        }
        if (context.state.local.isInteractive !== null) {
          context.commit("UNSET_IS_INTERACTIVE", "local");
        }
        if (context.state.local.candidatesTimer) {
          context.commit("UNSET_CANDIDATES_TIMER");
        }
        if (context.state.local.candidates) {
          context.commit("UNSET_CANDIDATES");
        }
        console.log("Sharing stopped");
      };

      context.dispatch(
        "session/sendJsonMessage",
        {
          message: {
            type: MessageType.desktopSharingStop
          },
          correlationId: context.state.local.correlationId,
          callback: (data: unknown) => {
            console.log("Sharing stop request has been successfully sent.");
            cb();
          },
          errorCallback: (err: string | null = null) => {
            console.error("Sharing stop request sending error: ", err);
            cb();
          }
        },
        { root: true }
      );
    },
    shareDocument: (
      context: {
        commit: (mutation: string, data?: unknown) => void;
        dispatch: Function;
        rootGetters: any;
        state: SharingState;
        rootState: any;
      },
      params: {
        file: File | string;
        type?: "signature";
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      const sendDocument = (p: { fileUrl: string; mimeType: string }) => {
        const { fileUrl, mimeType } = p;

        const format = _.startsWith(mimeType, "image/")
          ? "image"
          : mimeType === "application/pdf"
          ? "pdf"
          : [
              "application/msword",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            ].includes(mimeType)
          ? "doc"
          : [
              "application/vnd.ms-excel",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            ].includes(mimeType)
          ? "xls"
          : [
              "application/vnd.ms-powerpoint",
              "application/vnd.openxmlformats-officedocument.presentationml.presentation"
            ].includes(mimeType)
          ? "ppt"
          : null;

        const fileData = fileUrl.replace(/^.+,/, "");

        console.log("Sending document...");

        context.dispatch(
          "session/sendJsonMessage",
          {
            message: {
              type: MessageType.presentableInfo,
              format,
              data:
                format === "pdf"
                  ? fileData
                  : _.assign(
                      {
                        mimeType,
                        data: fileData
                      },
                      params.type ? { type: params.type } : {}
                    )
            },
            correlationId:
              context.rootState.sharing.remote.correlationId ??
              context.rootState.sharing.local.correlationId,
            callback: (data: unknown) => {
              console.log("Document has been successfully sent.");
              params.callback?.(data);
            },
            errorCallback: (err: string | null = null) => {
              console.error("Document sending error: ", err);
              params.errorCallback?.(err);
            }
          },
          { root: true }
        );
      };

      if (typeof params.file === "string") {
        sendDocument({
          mimeType: params.file
            .split(",")[0]!
            .match(/[^:\s*]\w+\/[\w-+\d.]+(?=[;| ])/)![0]!,
          fileUrl: params.file
        });
      } else {
        const reader = new FileReader();
        reader.readAsDataURL(params.file);

        reader.onload = () => {
          if (_.startsWith((params.file as File).type, "image/")) {
            const img = new Image();

            img.onload = () => {
              const maxWidth = 2000;
              const maxHeight = 2000;
              const targetMimeType = "image/jpeg";

              const imgWidth = img.width;
              const imgHeight = img.height;

              const cb = (url: string) => {
                sendDocument({ mimeType: targetMimeType, fileUrl: url });
              };

              if (
                (params.file as File).type !== targetMimeType ||
                imgHeight > maxHeight ||
                imgWidth > maxWidth
              ) {
                const canvas = document.createElement("canvas");

                if (imgHeight > maxHeight || imgWidth > maxWidth) {
                  const resize = Math.min(
                    maxHeight / imgHeight,
                    maxWidth / imgWidth
                  );

                  canvas.width = imgWidth * resize;
                  canvas.height = imgHeight * resize;
                } else {
                  canvas.width = imgWidth;
                  canvas.height = imgHeight;
                }

                const context = canvas.getContext("2d");
                if ((params.file as File).type !== targetMimeType) {
                  context!.fillStyle = "white";
                  context!.fillRect(0, 0, canvas.width, canvas.height);
                }
                context!.drawImage(img, 0, 0, canvas.width, canvas.height);

                cb(context!.canvas.toDataURL(targetMimeType, 0.75));
              } else {
                cb(img.src);
              }
            };

            img.src = `${reader.result}`;
          } else {
            sendDocument({
              fileUrl: `${reader.result}`,
              mimeType: (params.file as File).type
            });
          }
        };

        reader.onerror = () => {
          console.log(reader.error);
          params.errorCallback?.(reader.error);
        };
      }
    }
  },
  mutations: {
    SET_PEER(
      state: SharingState,
      params: { type: "local" | "remote"; peer: unknown }
    ) {
      state[params.type].peer = params.peer;
    },
    UNSET_PEER(state: SharingState, type: "local" | "remote") {
      state[type].peer?.destroy();
      console.log(`${type} shared peer destroyed`);
      state[type].peer = null;
    },
    SET_FILE(
      state: SharingState,
      params: {
        type: "local" | "remote";
        file: {
          mimeType: string;
          url: string;
        };
      }
    ) {
      state[params.type].file = params.file;
    },
    UNSET_FILE(state: SharingState, type: "local" | "remote") {
      state[type].file = null;
    },
    SET_CORRELATION_ID(
      state: SharingState,
      params: { id: string; type: "local" | "remote" }
    ) {
      state[params.type].correlationId = params.id;
    },
    UNSET_CORRELATION_ID(state: SharingState, type: "local" | "remote") {
      state[type].correlationId = null;
    },
    SET_STREAM(
      state: SharingState,
      params: { type: "local" | "remote"; stream: MediaStream }
    ) {
      state[params.type].stream = params.stream;
    },
    UNSET_STREAM(state: SharingState, type: "local" | "remote") {
      if (type === "local") {
        state.local.stream
          ?.getTracks()
          .forEach((track: MediaStreamTrack) => track.stop());
      }

      state[type].stream = null;
    },
    SET_STREAM_TYPE(state: SharingState, type: "screen" | "barcode") {
      state.local.streamType = type;
    },
    UNSET_STREAM_TYPE(state: SharingState) {
      state.local.streamType = null;
    },
    SET_IS_INTERACTIVE(
      state: SharingState,
      params: { type: "local" | "remote"; isInteractive: boolean }
    ) {
      state[params.type].isInteractive = params.isInteractive;
    },
    UNSET_IS_INTERACTIVE(state: SharingState, type: "local" | "remote") {
      state[type].isInteractive = null;
    },
    SET_CANDIDATES_TIMER(state: SharingState, timer: number) {
      state.local.candidatesTimer = timer;
    },
    UNSET_CANDIDATES_TIMER(state: SharingState) {
      clearInterval(state.local.candidatesTimer ?? undefined);
      state.local.candidatesTimer = null;
    },
    SET_CANDIDATE(state: SharingState, candidate: IceCandidateDTO) {
      state.local.candidates = state.local.candidates
        ? state.local.candidates.concat([candidate])
        : [candidate];
    },
    UNSET_CANDIDATES(state: SharingState) {
      state.local.candidates = null;
    }
  }
};

const webexModule = {
  namespaced: true,
  state: (): WebexState => ({
    webex: null,
    accessToken: null,
    meetingId: null,
    joinedMeetingParticipantsCount: null,
    streams: {
      local: null,
      remoteVideo: null,
      remoteAudio: null,
      remoteShare: null
    }
  }),
  getters: {
    webex(state: WebexState) {
      /* eslint-disable */
      // @ts-ignore
      return window.webex ?? null
      /* eslint-enable */
    },
    meeting(state: WebexState) {
      /* eslint-disable */
      // @ts-ignore
      return state.meetingId ? (_.find(window.webex?.meetings.getAllMeetings(), (m: WebexMeeting) => m.id === state.meetingId) ?? null) : null
      /* eslint-enable */
    }
  },
  actions: {
    init: async (
      context: {
        commit: (mutation: string, data?: object) => void;
        state: WebexState;
        dispatch: Function;
        rootState: any;
      },
      params?: {
        createMeeting?: boolean;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Webex initialization...");

      if (context.state.webex) {
        console.error(
          "Webex has been already initialized: ",
          context.state.webex
        );
        return;
      }

      if (context.rootState.config.config.clientType === "expert") {
        patchServicesConfig({
          webexRedirectUri: `${window.location.protocol}//${
            window.location.host
          }${window.location.pathname ?? ""}${window.location.search ??
            ""}${window.location.hash ?? ""}`
        });
      }
      const scrypt = document.createElement("script");
      scrypt.setAttribute(
        "src",
        `https://unpkg.com/webex@${_.find(
          context.rootState.auth.profile,
          i => i.name === "webex.sdk.version"
        )?.value || "^2"}/umd/webex.min.js`
      );
      scrypt.addEventListener(
        "load",
        async () => {
          await services.webex?.init(
            async (webex: any) => {
              console.log("Webex successfully initialized: ", webex);

              context.commit("SET_WEBEX", webex);

              if (context.rootState.config.config.clientType === "kiosk") {
                const cb = () =>
                  context.dispatch(
                    "setEndpointStatus",
                    {
                      status: EndpointStatus.ready,
                      callback: () => params?.callback?.(webex)
                    },
                    { root: true }
                  );

                params?.createMeeting ?? true
                  ? services.webex?.createMeeting(
                      webex,
                      async meeting => {
                        context.commit("SET_MEETING_ID", meeting.id);
                        cb();
                      },
                      (err: string) =>
                        context.dispatch(
                          "setEndpointStatus",
                          {
                            status: EndpointStatus.failure,
                            details: `${EndpointStatusDetails.webexMeetingCreatingError} (${err})`,
                            callback: () => params?.errorCallback?.()
                          },
                          { root: true }
                        )
                    )
                  : cb();

                const deviceUrl = webex?.internal?.device?._values?.url;
                if (deviceUrl) {
                  console.log("Sending Webex device url...");

                  services.wms?.setDeviceUrl(
                    deviceUrl,
                    (data: unknown) =>
                      console.log("Webex device url has been sent: ", data),
                    (err: unknown) =>
                      console.error("Webex device url sending error: ", err)
                  );
                }
              } else {
                if (
                  !((await webex.people.get("me"))?.emails ?? []).includes(
                    _.find(
                      context.rootState.auth.profile,
                      v => v.name === "webex.email"
                    )?.value
                  )
                ) {
                  params?.errorCallback?.(
                    "Please use another WebEx account to login."
                  );
                  console.error("User logined into another WebEx account.");
                  return;
                }

                const tokenPath = "credentials.userTokens.models[0]._values";

                const token = _.get(webex, `${tokenPath}.access_token`),
                  expiresAt = _.get(webex, `${tokenPath}.expires`);

                if (token && expiresAt) {
                  context.commit("SET_ACCESS_TOKEN", {
                    // eslint-disable-next-line @typescript-eslint/camelcase
                    token_string: token,
                    expires: _.round(expiresAt / 1000)
                  });
                }

                context.dispatch(
                  "setEndpointStatus",
                  {
                    status: EndpointStatus.ready,
                    callback: () => params?.callback?.(webex)
                  },
                  { root: true }
                );
              }
            },
            (err: string) => {
              console.error("Webex initialization error: ", err);
              context.dispatch(
                "setEndpointStatus",
                {
                  status: EndpointStatus.failure,
                  details: `${EndpointStatusDetails.webexInitializationError} (${err})`
                },
                { root: true }
              );
              params?.errorCallback?.(err);
            }
          );
        },
        false
      );
      document.body.appendChild(scrypt);
    },
    logout: async (context: {
      commit: (mutation: string) => void;
      getters: any;
      state: WebexState;
    }) => {
      const webex = context.getters.webex;

      if (webex?.canAuthorize) {
        await webex.logout();
      }

      context.commit("UNSET_WEBEX");
      context.commit("UNSET_ACCESS_TOKEN");
      context.commit("UNSET_CLIENT_ID");
    },
    getAccessToken: (
      context: {
        commit: (mutation: string, token: WebexAccessToken) => void;
        rootState: unknown;
      },
      params: {
        callback: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Webex assess token retrieving...");

      services.wms?.getAccessToken(
        (token: WebexAccessToken) => {
          console.log("Webex assess token retrieved: ", token);

          context.commit("SET_ACCESS_TOKEN", token);
          params.callback(token);
        },
        (err: any) => {
          console.error("Webex assess token retrieving error: ", err);
          params.errorCallback?.(err);
        }
      );
    },
    setDeviceUrl: (
      context: {
        commit: (mutation: string, token: WebexAccessToken) => void;
        rootState: unknown;
      },
      params: {
        url: string;
        callback: Function;
        errorCallback: Function;
      }
    ) => {
      console.log("Setting Webex device url...");

      services.wms?.setDeviceUrl(
        params.url,
        (data: unknown) => {
          console.log("Webex device url set: ", data);

          params.callback?.(data);
        },
        (err: unknown) => {
          console.log("Setting Webex device url error: ", err);
          params.errorCallback?.(err);
        }
      );
    },
    listenForIncomingMeetings: (context: {
      commit: (mutation: string, data?: unknown, params?: {}) => void;
      state: WebexState;
      dispatch: Function;
      getters: any;
      rootGetters: any;
      rootState: any;
    }) => {
      const webex = context.getters.webex;

      webex?.meetings?.on(
        "meeting:added",
        async (event: { meeting: WebexMeeting; type: string }) => {
          console.log("meeting:added", event);

          const isNotAlone = () =>
            !!_.keys(event.meeting.members.membersCollection.members).find(
              id => {
                const member =
                  event.meeting.members.membersCollection.members[id];
                return !member.isSelf && member.status === "IN_MEETING";
              }
            );

          const party = _.get(
            _.find(
              event.meeting?.members?.membersCollection?.members,
              m => m.isHost
            )?.participant,
            "person.name"
          );

          if (["INCOMING", "JOIN"].includes(event.type) && isNotAlone()) {
            if (context.rootState.config.config.clientType === "kiosk") {
              context.commit("SET_MEETING_ID", event.meeting.id);
              await context.dispatch("bindMeetingEvents");

              return event.meeting.join().then(async () => {
                console.log("Meeting joined: ", event.meeting);
                await context.dispatch("bindMeetingEvents");

                context.commit("session/SET_PARTY", party, {
                  root: true
                });

                context.dispatch(
                  "setEndpointStatus",
                  {
                    status:
                      EndpointStatus.connected +
                      (event.meeting?.mediaProperties?.videoDeviceId === null ||
                      event.meeting?.isVideoMuted()
                        ? 0
                        : EndpointStatus.videoOn)
                  },
                  { root: true }
                );
              });
            } else {
              if (context.getters.meeting) {
                try {
                  console.log(
                    "Leaving Webex meeting...",
                    context.getters.meeting?.id
                  );
                  await context.getters.meeting?.leave();
                  context.commit("UNSET_MEETING_ID");
                } catch (err) {
                  console.error("Leaving meeting error: ", err);
                }
              }

              event.meeting.acknowledge(event.type).then(() => {
                if (!isNotAlone()) {
                  return;
                }

                context.commit("SET_MEETING_ID", event.meeting.id);
                context.dispatch("bindMeetingEvents");

                context.commit("session/SET_PARTY", party, {
                  root: true
                });

                if (
                  context.rootGetters.endpointStatusCode ===
                  EndpointStatus.connected
                ) {
                  console.log("Joining to meeting...", event.meeting);

                  event.meeting
                    ?.join()
                    .then(async () => {
                      if (event.meeting.id !== context.state.meetingId) {
                        console.log("Meeting is not actual, leaving");
                        event.meeting?.leave();
                        return;
                      }

                      const client = _.find(
                        event.meeting.members.membersCollection.members,
                        m => !m.isSelf && m.isUser && m.status === "IN_MEETING"
                      );

                      if (!client) {
                        console.error("No active meeting member");
                        event.meeting?.leave();
                        return;
                      }
                      context.dispatch("webex/bindMeetingEvents", null, {
                        root: true
                      });
                    })
                    .catch((err: Error) =>
                      context.dispatch(
                        "setEndpointStatus",
                        {
                          status: EndpointStatus.failure,
                          details: `${EndpointStatusDetails.webexMeetingJoiningError} (${err.name}: ${err.message})`
                        },
                        { root: true }
                      )
                    );
                } else {
                  context.dispatch(
                    "setEndpointStatus",
                    { status: EndpointStatus.inComingCall },
                    { root: true }
                  );
                }
              });
            }
          } else {
            console.log("No entered meeting members. Meeting ignored");
          }
        }
      );
    },
    unbindMeetingEvents: async (context: { getters: any }) => {
      const meeting = context.getters.meeting;

      console.log("Unbinding meeting events...");

      try {
        [
          "all",
          "error",
          "media:ready",
          "media:stopped",
          "members:update",
          "members:content:update",
          "members:host:update",
          "members:self:update",
          "meeting:startedSharingRemote",
          "meeting:stoppedSharingRemote",
          "meeting:actionsUpdate",
          "meeting:self:mutedByOthers",
          "meeting:self:unmutedByOthers"
        ].forEach(v => meeting?.removeAllListeners(v));
      } catch (error) {
        console.error("Can not unbind meeting events:", error);
      }

      console.log("Meeting events unbound");
    },
    bindMeetingEvents: async (context: {
      commit: (mutation: string, data?: unknown, params?: {}) => void;
      state: WebexState;
      dispatch: Function;
      getters: any;
      rootGetters: any;
      rootState: any;
    }) => {
      const meeting = context.getters.meeting;

      context.dispatch("unbindMeetingEvents");

      console.log("Binding meeting events");

      const checkMeetingActuality = async (meeting: WebexMeeting) =>
        await context.dispatch("checkMeetingActuality", meeting);

      meeting?.on("all", async (event: unknown) => {
        console.log("Meeting [all]:", event);

        if (event === "network:connected") {
          context.commit("network/SET_STATUS", "connected", { root: true });
        }

        if (event === "network:disconnected") {
          context.commit("network/SET_STATUS", "disconnected", { root: true });
        }

        if (
          event === "meeting:reconnectionFailure" &&
          context.rootState.config.config.clientType === "kiosk"
        ) {
          // context.rootState.config.config.clientType === "expert" ? await rejoin() : await recreate();

          context.dispatch(
            "setEndpointStatus",
            {
              status: EndpointStatus.failure,
              details: EndpointStatusDetails.webexMeetingReconnectionError
            },
            { root: true }
          );
        }

        if (
          event === "DESTROY_MEETING" &&
          meeting.id === context.getters.meeting?.id &&
          // event cam be received after previous meeting leaving
          context.rootState.endpointStatus?.status === EndpointStatus.connected
        ) {
          context.dispatch(
            "setEndpointStatus",
            {
              status: EndpointStatus.failure,
              details: EndpointStatusDetails.webexMeetingDestroyed
            },
            { root: true }
          );
        }
      });
      meeting?.on("error", async (err: unknown) => {
        console.error("Meeting error: ", err);

        if (!(await checkMeetingActuality(meeting))) {
          return;
        }

        context.dispatch(
          "setEndpointStatus",
          {
            status: EndpointStatus.failure,
            details: EndpointStatusDetails.webexMeetingErrorCallbackHandled
          },
          { root: true }
        );
      });

      const isMediaValid = (media: WebexMedia | undefined) => {
        if (!media) {
          console.error("Empty media");
          return false;
        }
        if (
          !["local", "remoteVideo", "remoteAudio", "remoteShare"].includes(
            media.type
          )
        ) {
          console.error("Unknown media type: ", media.type);
        }
        return true;
      };

      meeting?.on("media:ready", async (media: WebexMedia | undefined) => {
        console.log("Media ready:", media);

        if (!(await checkMeetingActuality(meeting)) || !isMediaValid(media)) {
          return;
        }

        context.commit("SET_STREAM", _.pick(media, ["type", "stream"]));
      });
      meeting?.on("media:stopped", async (media: WebexMedia | undefined) => {
        console.log("Media stopped:", media);
        if (!(await checkMeetingActuality(meeting)) || !isMediaValid(media)) {
          return;
        }

        context.commit("UNSET_STREAM", media!.type);
      });

      const syncHoldAndVideoStatus = async () => {
        if (!(await checkMeetingActuality(meeting))) {
          return;
        }

        let endpointStatus = context.rootState.endpointStatus?.status ?? 0;

        if (context.rootState.config.config.clientType === "kiosk") {
          const isRemoteMuted =
            meeting?.locusInfo?.parsedLocus?.self?.remoteMuted ?? false;

          if (context.rootGetters.isOnHold !== isRemoteMuted) {
            console.log(
              `User was ${
                isRemoteMuted ? "muted" : "unmuted"
              } by another Webex meeting particmeetingipant`
            );

            endpointStatus += EndpointStatus.onHold * (isRemoteMuted ? 1 : -1);
          }
        }

        if (
          context.rootGetters.endpointStatusCode === EndpointStatus.connected
        ) {
          const isVideoOn = context.rootGetters.isVideoOn;

          if (
            (meeting.mediaProperties?.videoDeviceId === null ||
              meeting.isVideoMuted()) !== !isVideoOn
          ) {
            console.log(
              `User video has been ${isVideoOn ? "muted" : "unmuted"}`
            );

            endpointStatus += EndpointStatus.videoOn * (isVideoOn ? -1 : 1);
          }
        }

        if (
          endpointStatus !== (context.rootState.endpointStatus?.status ?? 0)
        ) {
          context.dispatch(
            "setEndpointStatus",
            {
              status: endpointStatus
            },
            { root: true }
          );
        }
      };

      meeting?.members.on(
        "members:update",
        async (payload: { full: Record<string, WebexMeetingMember> }) => {
          console.log("Members updated:", payload);

          if (!(await checkMeetingActuality(meeting))) {
            return;
          }

          const endpointStatusCode = context.rootGetters.endpointStatusCode;
          const { full: membersData } = payload;

          const joinedParticipantsCount = _.filter(
            _.keys(membersData),
            (id: string) => {
              const memberObject: WebexMeetingMember = (membersData as any)[id];
              return (
                memberObject.isUser &&
                !memberObject.isSelf &&
                memberObject.status === "IN_MEETING"
              );
            }
          ).length;

          context.commit(
            "SET_JOINED_MEETING_PARTICIPANTS_COUNT",
            joinedParticipantsCount
          );

          const joinedMemberId = _.find(_.keys(membersData), (id: string) => {
            const memberObject: WebexMeetingMember = (membersData as any)[id];
            return (
              memberObject.isUser &&
              !memberObject.isSelf &&
              memberObject.status === "IN_MEETING"
            );
          });

          if (
            _.find(
              _.keys(membersData),
              (id: string) => membersData[id].isSelf && membersData[id].isHost
            )
          ) {
            for (const id in membersData) {
              if (membersData[id].status === "IN_LOBBY") {
                console.log("Member in lobby, admitting...", membersData[id]);

                context.getters.meeting
                  ?.admit([id])
                  .then((res: unknown) => {
                    console.log(`Member ${id} has been admitted`);
                  })
                  .catch((e: unknown) => {
                    console.error(`Can not admit member ${id}`, e);
                  });
              }
            }
          }

          if (
            joinedMemberId &&
            context.rootState.config.config.clientType === "expert"
          ) {
            const isMuted =
              _.find(membersData, member => member.id === joinedMemberId)
                ?.isAudioMuted ?? false;
            context.commit("session/kiosk/SET_IS_MUTED", isMuted, {
              root: true
            });
          }

          if (
            joinedMemberId &&
            [EndpointStatus.placingCall, EndpointStatus.transferring].includes(
              endpointStatusCode
            )
          ) {
            // expert connected
            console.log(`Expert "${joinedMemberId}" connected`);

            console.log(
              `Transferring Webex meeting host to member "${joinedMemberId}"...`
            );

            meeting.members
              .transferHostToMember(joinedMemberId)
              .then((data: Record<string, any>) =>
                console.log(
                  `Successfully transferred Webex meeting host to member "${joinedMemberId}"`
                )
              )
              .catch((err: Error) =>
                console.error(
                  `Transfer Webex meeting host to member "${joinedMemberId}" error: `,
                  err
                )
              );

            context.dispatch(
              "setEndpointStatus",
              {
                status:
                  EndpointStatus.connected +
                  (meeting?.mediaProperties?.videoDeviceId === null ||
                  meeting?.isVideoMuted()
                    ? 0
                    : EndpointStatus.videoOn)
              },
              { root: true }
            );

            return;
          }

          syncHoldAndVideoStatus();
        }
      );

      meeting?.on("meeting:actionsUpdate", () => {
        console.log("meeting:actionsUpdate");
        syncHoldAndVideoStatus();
      });

      meeting?.on("meeting:self:mutedByOthers", () => {
        console.log("meeting:self:mutedByOthers");
        syncHoldAndVideoStatus();
      });

      meeting?.on("meeting:self:unmutedByOthers", () => {
        console.log("meeting:self:unmutedByOthers");
        syncHoldAndVideoStatus();
      });

      meeting?.members.on("members:content:update", (payload: unknown) => {
        console.log("Members content updated:", payload);
      });
      meeting?.members.on("members:host:update", (payload: unknown) => {
        console.log("Members hosting updated:", payload);
      });
      meeting?.members.on("members:self:update", (payload: unknown) => {
        console.log("Members self updated:", payload);
      });
      meeting?.on("meeting:startedSharingRemote", () => {
        console.log("Remote sharing started");
      });
      meeting?.on("meeting:stoppedSharingRemote", () => {
        console.log("Remote sharing stopped");
      });

      await context.dispatch("addMeetingMedia");
    },
    checkMeetingActuality: (
      context: {
        commit: (mutation: string, data?: unknown, params?: {}) => void;
        state: WebexState;
        dispatch: Function;
        getters: any;
        rootGetters: any;
        rootState: any;
      },
      meeting: WebexMeeting
    ) => {
      if (meeting.id !== context.getters.meeting?.id) {
        console.log(
          `Meeting event: meeting id ${meeting.id} is not equal current meeting id ${context.getters.meeting?.id}`
        );
        meeting?.leave();
        return false;
      }
      if (context.rootState.session.hangupRequestReceivedAt) {
        console.log(
          `Meeting event: hangup request received at ${context.rootState.session.hangupRequestReceivedAt}, so meeting id ${meeting.id} is not actual`
        );
        meeting?.leave();
        return false;
      }

      if (context.rootGetters["queue/isThrownUrgently"]) {
        console.log(`Meeting event: meeting id ${meeting.id} is not actual`);
        meeting?.leave();
        return false;
      }

      return true;
    },
    addMeetingMedia: async (
      context: {
        commit: (mutation: string, data?: unknown, params?: {}) => void;
        state: WebexState;
        dispatch: Function;
        getters: any;
        rootGetters: any;
        rootState: any;
      },
      withoutLocalStreams = false
    ) => {
      const meeting = context.getters.meeting;

      const checkMeetingActuality = async (meeting: WebexMeeting) =>
        await context.dispatch("checkMeetingActuality", meeting);

      let sendVideo = false;
      let sendAudio = false;

      if (!withoutLocalStreams) {
        try {
          console.log("Checking supported devices");

          ({ sendVideo, sendAudio } = await meeting?.getSupportedDevices({
            sendVideo:
              context.rootGetters["auth/property"](
                "webex.video.from.start.enabled"
              ) ?? true,
            sendAudio: true
          }));
          console.log("sendVideo:", sendVideo, "sendAudio:", sendAudio);
        } catch (error) {
          console.error("Unable to get a list of supported devices:", error);
        }
        if (!(await checkMeetingActuality(meeting))) {
          return;
        }
      }

      if (
        !_.find(
          meeting?.members?.membersCollection?.members,
          member => member.isSelf && member.status === "IN_MEETING"
        )
      ) {
        // media settings can be set only for joined apps
        return;
      }

      const mediaSettings = {
        receiveVideo: true,
        receiveAudio: true,
        receiveShare: false,
        sendVideo,
        sendAudio,
        sendShare: false
      };
      console.debug("Media settings:", mediaSettings);

      console.log("Getting media streams");
      let mediaStreams: Array<MediaStream | undefined> = [];

      const stopStreams = () =>
        (_.filter(mediaStreams) as MediaStream[]).forEach((s: MediaStream) =>
          s.getTracks().forEach(t => t.stop())
        );

      const getDefaultCamera = async () => {
          const deviceId: string | null =
            context.rootGetters["session/mediaInputs/defaultCamera"];

          if (
            deviceId &&
            !_.find(
              await services.webex?.getCameras(meeting),
              c => c.camera.deviceId === deviceId
            )
          ) {
            context.commit("session/mediaInputs/UNSET_DEFAULT_CAMERA", null, {
              root: true
            });

            return null;
          }

          return deviceId;
        },
        getDefaultMicrophone = async () => {
          const deviceId: string | null =
            context.rootGetters["session/mediaInputs/defaultMicrophone"];

          if (
            deviceId &&
            !_.find(
              await services.webex?.getMicrophones(meeting),
              m => m.microphone.deviceId === deviceId
            )
          ) {
            context.commit(
              "session/mediaInputs/UNSET_DEFAULT_MICROPHONE",
              null,
              {
                root: true
              }
            );

            return null;
          }

          return deviceId;
        };

      const clientType = context.rootState.config.config.clientType;

      const defaultCamera =
          clientType === "kiosk" ? null : await getDefaultCamera(),
        defaultMicrophone =
          clientType === "kiosk" ? null : await getDefaultMicrophone();

      try {
        mediaStreams = await meeting?.getMediaStreams(
          mediaSettings,
          _.assign(
            {},
            defaultCamera
              ? {
                  video: {
                    deviceId: defaultCamera
                  }
                }
              : {},

            defaultMicrophone
              ? {
                  audio: {
                    deviceId: defaultMicrophone
                  }
                }
              : {}
          )
        );
        console.log("MediaStreams:", mediaStreams);
      } catch (error) {
        console.error("Unable to get media streams:", error);
      }
      if (!(await checkMeetingActuality(meeting))) {
        stopStreams();
        return;
      }
      const [localStream, localShare] = mediaStreams;
      console.log("Adding media streams to meeting");
      try {
        await meeting?.addMedia({
          localShare,
          localStream,
          mediaSettings
        });
      } catch (err) {
        console.error("Unable to add media to meeting:", err);
        if (!(await checkMeetingActuality(meeting))) {
          return;
        }
        context.dispatch(
          "setEndpointStatus",
          {
            status: EndpointStatus.failure,
            details: `${EndpointStatusDetails.addingMediaToWebexMeetingError} (${err.name}: ${err.message})`
          },
          { root: true }
        );
      }
      if (!(await checkMeetingActuality(meeting))) {
        stopStreams();
        return;
      }
    },
    sendJoinRequest: async (
      context: {
        commit: (mutation: string, data?: unknown) => void;
        state: WebexState;
        dispatch: Function;
        rootState: unknown;
        getters: any;
      },
      params: {
        roomId: string;
        deviceId: string;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      const webex = context.getters.webex;

      webex?.meetings?.on(
        "meeting:added",
        (event: { meeting: WebexMeeting; type: string }) => {
          console.log("meeting:added", event);
          if (
            !context.getters.meeting &&
            event.type === "JOIN" &&
            _.keys(event.meeting.members.membersCollection.members).find(id => {
              const member =
                event.meeting.members.membersCollection.members[id];
              return !member.isSelf && member.status === "IN_MEETING";
            })
          ) {
            context.commit("SET_MEETING_ID", event.meeting.id);

            // Pass the meeting to our join meeting helper
            return event.meeting.join().then(async () =>
              context.dispatch("webex/bindMeetingEvents", null, {
                root: true
              })
            );
          }
        }
      );

      const onError = (err: unknown) => {
        params.errorCallback?.(err);
        context.dispatch(
          "setEndpointStatus",
          {
            status: EndpointStatus.failure,
            details: `${
              EndpointStatusDetails.ownWebexPersonRetrievingError
            } (${_.get(err, "name")}: ${_.get(err, "message")})`
          },
          { root: true }
        );
      };

      const me = await webex?.people
        ?.get("me")
        ?.catch((err: Error) => onError(err));
      if (me instanceof Error) {
        onError(me);
        return;
      }

      services.rem?.sendCallJoinRequest(
        params.deviceId,
        (me as WebexPerson).id,
        params.roomId,
        (data: unknown) => {
          console.log("Meeting join request has been sent: ", data);
          params.callback?.(data);
        },
        params.errorCallback
      );
    },
    leaveMeeting: async (
      context: {
        commit: (mutation: string, data?: any) => void;
        state: WebexState;
        dispatch: Function;
        getters: any;
        rootGetters: any;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      const meeting = context.getters.meeting;

      context.dispatch("unbindMeetingEvents");

      console.log("Leaving Webex meeting: ", meeting);

      const cb = (isSuccess: boolean) => {
        isSuccess ? params?.callback?.() : params?.errorCallback?.();

        if (
          !(
            isSuccess &&
            [EndpointStatus.unknown, EndpointStatus.failure].includes(
              context.rootGetters.endpointStatusCode
            )
          )
        ) {
          context.dispatch(
            "setEndpointStatus",
            {
              status: isSuccess ? EndpointStatus.ready : EndpointStatus.failure
            },
            { root: true }
          );
        }
      };

      context.commit("UNSET_MEETING_ID");
      ["local", "remoteVideo", "remoteAudio", "remoteShare"].forEach(type =>
        context.commit("UNSET_STREAM", type)
      );

      context.commit("UNSET_JOINED_MEETING_PARTICIPANTS_COUNT");

      try {
        await meeting?.leave();
        console.log("Webex meeting left");
        cb(true);
      } catch (err) {
        console.error("Unable to leave Webex meeting: ", err);
        // for some reason meeting leaving can throw error
        cb(true);
      }
    }
  },
  mutations: {
    SET_WEBEX(state: WebexState, webex: any) {
      state.webex = webex;
      /* eslint-disable */
      // @ts-ignore
      window.webex = webex;
      /* eslint-enable */
    },
    UNSET_WEBEX(state: WebexState) {
      state.webex = null;
      /* eslint-disable */
      // @ts-ignore
      delete window.webex
      /* eslint-enable */
    },
    SET_ACCESS_TOKEN(state: WebexState, token: WebexAccessToken) {
      patchServicesConfig({ webexAccessToken: token.token_string });
      state.accessToken = token;
    },
    UNSET_ACCESS_TOKEN(state: WebexState) {
      unsetServicesConfig(["webexAccessToken"]);
      state.accessToken = null;
    },
    SET_MEETING_ID(state: WebexState, id: string) {
      state.meetingId = id;
    },
    UNSET_MEETING_ID(state: WebexState) {
      state.meetingId = null;
    },
    SET_STREAM(
      state: WebexState,
      params: {
        type: "local" | "remoteVideo" | "remoteAudio" | "remoteShare";
        stream: MediaStream;
      }
    ) {
      state.streams[params.type] = params.stream;
    },
    UNSET_STREAM(
      state: WebexState,
      type: "local" | "remoteVideo" | "remoteAudio" | "remoteShare"
    ) {
      state.streams[type] = null;
    },
    SET_JOINED_MEETING_PARTICIPANTS_COUNT(state: WebexState, count: number) {
      state.joinedMeetingParticipantsCount = count;
    },
    UNSET_JOINED_MEETING_PARTICIPANTS_COUNT(state: WebexState) {
      state.joinedMeetingParticipantsCount = null;
    }
  }
};

const watchTwilioParticipantMicrophoneStatus = (
  participant: RemoteParticipant,
  callback: Function
) => {
  participant.audioTracks.forEach(publication => {
    if (publication.isSubscribed) {
      publication.track?.on("disabled", () => callback(true));
      publication.track?.on("enabled", () => callback(false));
    }
    publication.on("unsubscribed", () => callback(true));
    publication.on("subscribed", () => callback(false));
  });
};

const handleTwilioParticipant = (
  participant: RemoteParticipant,
  reason: "roomJoined" | "participantConnected",
  context: {
    commit: (mutation: string, data: any, params?: { root: true }) => void;
    rootGetters: any;
    rootState: any;
    dispatch: Function;
  }
) => {
  console.log("Handling participant: ", {
    participant: participant?.sid,
    reason
  });

  const setIsMuted = (isMuted: boolean) =>
    context.commit("session/kiosk/SET_IS_MUTED", isMuted, {
      root: true
    });

  const handleTrackEnabled = (track: RemoteTrack) => {
      console.log("Remote track enabled: ", track?.sid, track?.kind);

      context.commit("SET_MEDIA", {
        type: "remote",
        media: (track as RemoteVideoTrack | RemoteAudioTrack).attach()
      });

      if (track.kind === "audio") {
        setIsMuted(false);

        track.on("enabled", () => setIsMuted(false));
        track.on("disabled", () => setIsMuted(true));
      }
    },
    handleTrackDisabled = (track: RemoteTrack) => {
      console.log("Remote track disabled: ", track?.sid, track?.kind);

      context.commit("UNSET_MEDIA", "remote");

      if (track.kind === "audio") {
        setIsMuted(true);
      }
    };

  participant.tracks.forEach(publication => {
    publication.on("unsubscribed", track => {
      console.log("publication unsubscribed: ", track?.sid);

      handleTrackDisabled(track);
    });
    publication.on("subscribed", track => handleTrackEnabled(track));

    if (reason === "roomJoined" && publication.track) {
      handleTrackEnabled(publication.track);
    } else if (reason === "participantConnected" && publication.isSubscribed) {
      handleTrackEnabled(publication.track!);
    }
  });
  participant.on("trackSubscribed", track => {
    console.log("publication subscribed: ", track?.sid);
    handleTrackEnabled(track);
  });

  participant.on("trackPublished", publication => {
    console.log("trackPublished: ", publication?.trackSid);

    publication.on("subscribed", track => {
      console.log("publication subscribed: ", track?.sid);
      handleTrackEnabled(publication.track!);
    });

    publication.on("unsubscribed", track => {
      console.log("publication unsubscribed: ", track?.sid);

      handleTrackDisabled(track);
    });
  });
};

const twilioModule = {
  namespaced: true,
  state: (): TwilioState => ({
    accessToken: null,
    media: {
      remote: null,
      local: null
    }
  }),
  getters: {
    room(state: TwilioState) {
      /* eslint-disable */
      // @ts-ignore
      return window.twilioRoom ?? null
      /* eslint-enable */
    }
  },
  actions: {
    getAccessToken: async (
      context: {
        commit: (mutation: string, token: string) => void;
        rootState: unknown;
      },
      params: {
        roomName: string;
      }
    ) => {
      console.log("Retrieving Twilio access token...");
      const token = await services.twilio?.getAccessToken(params.roomName);
      console.log("Twilio access token retrieved: ", token);
      context.commit("SET_ACCESS_TOKEN", token!);
      return token;
    },
    joinRoom: async (
      context: {
        commit: (mutation: string, data: any, params?: { root: true }) => void;
        rootGetters: any;
        rootState: any;
        dispatch: Function;
      },
      params: {
        name: string;
        token: string;
      }
    ) => {
      console.log(`Joining to Twilio room "${params.name}"...`);

      const getDefaultCamera = async () => {
          const deviceId: string | null =
            context.rootGetters["session/mediaInputs/defaultCamera"];

          if (
            deviceId &&
            !_.find(
              await services.twilio?.getCameras(),
              c => c.camera.deviceId === deviceId
            )
          ) {
            context.commit("session/mediaInputs/UNSET_DEFAULT_CAMERA", null, {
              root: true
            });

            return null;
          }

          return deviceId;
        },
        getDefaultMicrophone = async () => {
          const deviceId: string | null =
            context.rootGetters["session/mediaInputs/defaultMicrophone"];

          if (
            deviceId &&
            !_.find(
              await services.twilio?.getMicrophones(),
              m => m.microphone.deviceId === deviceId
            )
          ) {
            context.commit(
              "session/mediaInputs/UNSET_DEFAULT_MICROPHONE",
              null,
              {
                root: true
              }
            );

            return null;
          }

          return deviceId;
        };

      const clientType = context.rootState.config.config.clientType;

      const defaultCamera =
          clientType === "kiosk" ? null : await getDefaultCamera(),
        defaultMicrophone =
          clientType === "kiosk" ? null : await getDefaultMicrophone();

      const room = await services.twilio?.joinRoom(
        params.name,
        params.token,
        _.assign(
          {},
          defaultCamera ? { camera: defaultCamera } : {},
          defaultMicrophone ? { microphone: defaultMicrophone } : {}
        )
      );

      console.log(`Joined to Twilio room "${params.name}"`);

      room?.localParticipant?.videoTracks?.forEach(
        (publication: LocalVideoTrackPublication) =>
          context.commit("SET_MEDIA", {
            type: "local",
            media: publication.track.attach()
          })
      );

      context.commit("SET_ROOM", room);

      context.dispatch(
        "setEndpointStatus",
        {
          status: EndpointStatus.connected + EndpointStatus.videoOn
        },
        { root: true }
      );

      room?.participants?.forEach(participant =>
        handleTwilioParticipant(participant, "roomJoined", context)
      );

      return room;
    },
    bindRoomEvents: async (context: {
      commit: (mutation: string, data?: unknown, params?: {}) => void;
      state: TwilioState;
      dispatch: Function;
      getters: any;
      rootGetters: any;
      rootState: any;
    }) => {
      console.log("Binding Twilio room events...");

      const room: Partial<Room> | null = context.getters.room;

      room?.on?.("participantConnected", participant => {
        console.log(`Participant connected: ${participant.identity}`);

        handleTwilioParticipant(participant, "participantConnected", context);
      });

      room?.on?.("participantDisconnected", participant =>
        console.log(
          `Participant "${participant.identity}" has disconnected from the Room`
        )
      );

      room?.on?.("disconnected", room => {
        // Detach the local media elements
        room.localParticipant.tracks.forEach(publication =>
          (publication.track as LocalVideoTrack).detach()
        );

        context.commit("UNSET_ROOM");
        context.commit("UNSET_MEDIA", "local");
        context.commit("UNSET_MEDIA", "remote");

        context.dispatch(
          "setEndpointStatus",
          { status: EndpointStatus.ready },
          { root: true }
        );
      });
    },
    leaveRoom: (
      context: {
        commit: (mutation: string) => void;
        state: TwilioState;
        dispatch: Function;
        getters: any;
        rootGetters: any;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Leaving Twilio room...");
      const room: Partial<Room> = context.getters.room;
      room?.localParticipant?.videoTracks?.forEach(
        (publication: LocalVideoTrackPublication) => {
          publication.track.stop();
          publication.unpublish();
        }
      );
      room?.localParticipant?.audioTracks?.forEach(
        (publication: LocalAudioTrackPublication) => {
          publication.track.stop();
          publication.unpublish();
        }
      );
      room?.disconnect?.();
    }
  },
  mutations: {
    SET_ACCESS_TOKEN(state: TwilioState, token: string) {
      state.accessToken = token;
    },
    UNSET_ACCESS_TOKEN(state: TwilioState) {
      unsetServicesConfig(["twilioAccessToken"]);
      state.accessToken = null;
    },
    SET_ROOM(state: TwilioState, room: Partial<Room>) {
      /* eslint-disable */
      // @ts-ignore
      window.twilioRoom = room;
      /* eslint-enable */
    },
    UNSET_ROOM(state: TwilioState) {
      /* eslint-disable */
      // @ts-ignore
      delete window.twilioRoom
      /* eslint-enable */
    },
    SET_MEDIA(
      state: TwilioState,
      params: {
        type: "local" | "remote";
        media: HTMLMediaElement;
      }
    ) {
      if (params.type === "remote") {
        state.media.remote = (state.media.remote ?? []).concat([params.media]);
      } else {
        state.media.local = params.media;
      }
    },
    UNSET_MEDIA(state: TwilioState, type: "local" | "remote") {
      state.media[type] = null;
    }
  }
};

const getClientId = (jwt: string) =>
  (jwt_decode(jwt) as {
    client: string;
    iat: number;
    iss: string;
    salt: string;
  }).client;

const authModule = {
  namespaced: true,
  state: (): AuthState => ({
    jwt: null,
    deviceId: null,
    me: null,
    profile: null,
    specs: null
  }),
  getters: {
    jwt(state: AuthState) {
      return state.jwt ?? localStorage.getItem(jwtStorageKey);
    },
    deviceId(state: AuthState) {
      return state.deviceId ?? sessionStorage.getItem(deviceIdStorageKey);
    },
    clientId(state: AuthState) {
      return state.jwt ? getClientId(state.jwt) : null;
    },
    isFunctionalityAvailable: (state: AuthState) => (
      propName: FunctionalAvailabilityPropertyName
    ) => {
      const profileProperty = _.find(state.profile, o => o.name === propName);

      if (profileProperty) {
        return profileProperty.value ?? false;
      }

      const specValue = _.find(state.specs, o => o.name === propName);

      if (specValue) {
        return specValue.default_value ?? false;
      }

      return ![
        FunctionalAvailabilityPropertyName.agentServiceEnabled,
        FunctionalAvailabilityPropertyName.usbIpEnabled,
        FunctionalAvailabilityPropertyName.ipIpEnabled,
        FunctionalAvailabilityPropertyName.ipIpPrintersEnabled,
        FunctionalAvailabilityPropertyName.virtualBackgroundEnabled,
        FunctionalAvailabilityPropertyName.surveillanceCameraEnabled,
        FunctionalAvailabilityPropertyName.surveillanceScreenEnabled
      ].includes(propName);
    },
    property: (state: AuthState) => (propName: string) =>
      _.find(state.profile, o => o.name === propName)?.value ??
      _.find(state.specs, o => o.name === propName)?.default_value
  },
  actions: {
    getJwt: async (
      context: {
        commit: (mutation: string, data?: string) => void;
        rootState: unknown;
      },
      params?: {
        data?: {
          username: string;
          password: string;
        };
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("JWT retrieving...");
      services.dcm?.getJwt(
        params?.data ?? null,
        (jwt: string) => {
          console.log("JWT retrieved: ", jwt);
          context.commit("SET_JWT", jwt);
          params?.callback?.(jwt);
        },
        (err: unknown) => {
          console.error("JWT retrieving error: ", err);
          params?.errorCallback?.("JWT retrieving error: ", err);
        }
      );
    },
    getMe: async (
      context: {
        commit: (mutation: string, data?: {}) => void;
        state: any;
        rootState: any;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Retrieving own client...");

      const clientId = getClientId(context.state.jwt);

      if (!clientId) {
        console.error("Empty client id");
        return;
      }

      services.dcm?.getClient(
        clientId,
        (client: {}) => {
          console.log("Own client retrieved: ", client);
          context.commit("SET_ME", client);
          params?.callback?.(client);
        },
        (err: unknown) => {
          console.error("Own client retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    getProfile: async (
      context: {
        commit: (mutation: string, data?: {}) => void;
        state: any;
        rootState: any;
        getters: any;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Profile retrieving...");

      const clientId = getClientId(context.state.jwt);

      if (!clientId) {
        console.error("Empty client id");
        return;
      }
      services.dcm?.getProfile(
        clientId,
        (profile: {}) => {
          console.log("Profile retrieved: ", profile);

          context.commit("SET_PROFILE", profile);

          const getProperty = (name: string) => context.getters.property(name);

          const remHost =
              getProperty("re.manager.host") ?? getProperty("rem.host"),
            remPort = +(
              ((getProperty("re.manager.port") ?? getProperty("rem.port")) as
                | string
                | number
                | undefined) ?? ServiceDefaultPort.rem
            );
          if (remHost) {
            patchServicesConfig({
              remServiceUrl: `https://${remHost}:${remPort}`,
              remServicePort: remPort
            });
          }
          if (context.rootState.config.config.clientType === "expert") {
            if (getProperty("agent.service.key")) {
              patchServicesConfig(
                {
                  agentServiceKey: getProperty("agent.service.key") as string
                },
                ["agent"]
              );
            }
            patchServicesConfig(
              {
                agentServicePort: (getProperty("agent.service.port") ??
                  ServiceDefaultPort.agent) as number
              },
              ["agent"]
            );
          }

          params?.callback?.(profile);
        },
        (err: unknown) => {
          console.error("Profile retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    getSpecs: (
      context: {
        commit: (mutation: string, data?: []) => void;
        state: AuthState;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Specs retrieving...");

      const firmwareUuid = _.get(context.state.me, "firmware_uuid");

      if (!firmwareUuid) {
        console.error("Empty firmware uuid");
        return;
      }
      services.dcm?.getFirmwareSpecList(
        firmwareUuid,
        (specs: []) => {
          console.log("Specs retrieved: ", specs);
          context.commit("SET_SPECS", specs);
          params?.callback?.(specs);
        },
        (err: unknown) => {
          console.error("Specs retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    logout: (context: {
      commit: (mutation: string, data?: unknown, params?: {}) => void;
      dispatch: Function;
      state: AuthState;
      rootState: any;
      getters: any;
      rootGetters: any;
    }) => {
      if (
        context.getters.isFunctionalityAvailable(
          FunctionalAvailabilityPropertyName.agentServiceEnabled
        )
      ) {
        context.dispatch("agentService/disconnect", null, { root: true });
      }

      context.dispatch("signals/unsubscribe", null, { root: true });

      services.rem?.disconnect();
      context.commit("UNSET_PROFILE");
      context.commit("UNSET_SPECS");
      context.commit("UNSET_ME");
      context.commit("UNSET_JWT");

      const telephonyType = context.rootState.telephonyType;

      if (telephonyType === TelephonyType.webex) {
        context.dispatch("webex/logout", null, { root: true });
      } else if (telephonyType === TelephonyType.twilio) {
        context.commit("twilio/UNSET_ACCESS_TOKEN", null, { root: true });
        if (context.rootGetters["twilio/room"]) {
          context.dispatch("twilio/leaveRoom", null, { root: true });
        }
      }

      context.commit("UNSET_STATUS", null, { root: true });

      console.log("Login outed");
    }
  },
  mutations: {
    SET_JWT(state: AuthState, jwt: string) {
      patchServicesConfig({
        jwt,
        clientId: getClientId(jwt)
      });
      state.jwt = jwt;
      localStorage.setItem(jwtStorageKey, jwt);
    },
    UNSET_JWT(state: AuthState) {
      state.jwt = null;
      localStorage.removeItem(jwtStorageKey);
      unsetServicesConfig(["jwt", "clientId"]);
    },
    SET_DEVICE_ID(state: AuthState, id: string) {
      state.deviceId = id;
      sessionStorage.setItem(deviceIdStorageKey, id);
      patchServicesConfig({ extension: id });
    },
    SET_ME(state: AuthState, client: {}) {
      state.me = client;
      patchServicesConfig({
        clientName: _.get(client, "name"),
        clientDisplayName: _.get(client, "description") || _.get(client, "name")
      });
    },
    UNSET_ME(state: AuthState) {
      state.me = null;
      unsetServicesConfig(["clientName"]);
    },
    SET_PROFILE(state: AuthState, profile: { name: string; value: unknown }[]) {
      state.profile = profile;
    },
    UNSET_PROFILE(state: AuthState) {
      unsetServicesConfig(["extension", "webexClientId"]);
      unsetServicesConfig(
        ["agentServiceKey", "agentServicePort", "recordingParams"],
        ["agent"]
      );

      state.profile = null;
    },
    SET_SPECS(state: AuthState, specs: []) {
      state.specs = specs;
    },
    UNSET_SPECS(state: AuthState) {
      state.specs = null;
    }
  }
};

const iOsMediaInputsBugWorkaround = async (
  context: {
    commit: (mutation: string, data?: unknown, params?: {}) => void;
    dispatch: Function;
    rootGetters: any;
    rootState: any;
  },
  cb: Function
) => {
  if (getDeviceType() === DeviceType.mobile && detect()?.os === "iOS") {
    try {
      // workaround to avoid a bug with bluetooth headsets on Iphones
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });

      const devices = (await navigator.mediaDevices.enumerateDevices()).filter(
        device => device.kind === "audioinput"
      );

      // We need to call getUserMedia media in order to enumerateDevices with labels.
      // Then we have to stop the audio track, since Firefox thinks we're still using
      // the stream if we try to switch devices later.
      for (const track of stream.getAudioTracks()) {
        track.stop();
      }
    } catch (e) {
      context.dispatch(
        "setEndpointStatus",
        {
          status: EndpointStatus.failure,
          details: EndpointStatusDetails.disabledSafariMediaInputsAccess
        },
        { root: true }
      );

      return;
    }
  }

  cb();
};

const handleEndpointStatusChange = (context: {
  commit: (mutation: string, data?: unknown, params?: {}) => void;
  dispatch: Function;
  rootGetters: any;
  rootState: any;
}) => {
  const endpointStatusCode = context.rootGetters.endpointStatusCode;
  const telephonyType = context.rootState.telephonyType;

  if (
    endpointStatusCode === EndpointStatus.unknown &&
    telephonyType === TelephonyType.webex &&
    context.rootGetters["webex/webex"]
  ) {
    context.dispatch(
      "setEndpointStatus",
      { status: EndpointStatus.ready },
      { root: true }
    );
  }

  if (endpointStatusCode === EndpointStatus.inComingCall) {
    if (telephonyType === TelephonyType.webex) {
      const meeting = context.rootGetters["webex/meeting"];

      if (
        meeting &&
        !_.find(
          meeting.members?.membersCollection?.members,
          m => !m.isSelf && m.isUser && m.status === "IN_MEETING"
        )
      ) {
        console.log("No active meeting members, leaving meeting");
        context.dispatch("webex/leaveMeeting", null, { root: true });
      }
    } else if (telephonyType === TelephonyType.twilio) {
      context.commit(
        "session/SET_PARTY",
        context.rootState.endpointStatus?.parties?.[0] ?? null,
        { root: true }
      );
    }
  }

  if (
    endpointStatusCode !== EndpointStatus.connected ||
    context.rootGetters.isOnHold
  ) {
    if (context.rootState.sharing.local.stream) {
      console.log("Stop stream sharing");
      context.dispatch("sharing/stopStreamSharing", null, {
        root: true
      });
    }
  }
  if (endpointStatusCode !== EndpointStatus.connected) {
    ["local", "remote"].forEach(type => {
      if (context.rootState.sharing[type].file) {
        context.commit("sharing/UNSET_FILE", type, { root: true });
      }
    });
    if (context.rootState.sharing.remote.peer) {
      context.commit("sharing/UNSET_PEER", { type: "remote" }, { root: true });
    }
    if (context.rootState.sharing.remote.correlationId) {
      context.commit("sharing/UNSET_CORRELATION_ID", "remote", { root: true });
    }
    if (context.rootState.sharing.remote.isInteractive !== null) {
      context.commit("sharing/UNSET_IS_INTERACTIVE", "remote", { root: true });
    }
    if (telephonyType === TelephonyType.webex) {
      context.commit(
        "session/SET_IS_MUTED",
        context.rootGetters["webex/meeting"]?.isAudioMuted() ?? false,
        { root: true }
      );
    }
  }

  if (
    [EndpointStatus.inComingCall, EndpointStatus.connected].includes(
      context.rootGetters.endpointStatusCode
    )
  ) {
    context.dispatch("session/getInfo", {}, { root: true });
    context.commit("surveillance/UNSET_SURVEYS", null, { root: true });
  }

  if (
    [EndpointStatus.ready, EndpointStatus.transferring].includes(
      context.rootGetters.endpointStatusCode
    )
  ) {
    const unmuteMicrophone =
      telephonyType === TelephonyType.twilio &&
      EndpointStatus.transferring &&
      context.rootState.session.isMuted;

    if (unmuteMicrophone) {
      context.dispatch("session/unmute", null, { root: true });
    }

    [
      "UNSET_INFO",
      // "UNSET_PARTY",
      "UNSET_IS_MUTED",
      "UNSET_VISUAL_HINTS",
      "UNSET_VISUAL_HINT",
      "UNSET_QR_CODE",
      "UNSET_TEXT_HINT",
      "UNSET_CALL_REQUEST_RECEIVED_AT",
      "UNSET_HANGUP_REQUEST_RECEIVED_AT",
      "UNSET_REQUESTED_ACTIONS",
      "UNSET_NOTIFICATIONS",
      "kiosk/UNSET_ASPECT_RATIO",
      "kiosk/UNSET_SCREEN_RESOLUTION",
      "kiosk/UNSET_IS_MUTED",
      "kiosk/UNSET_MICROPHONES",
      "kiosk/UNSET_LOCATION",
      "kiosk/UNSET_PROFILE",
      "kiosk/UNSET_SPECS",
      "kiosk/UNSET_KIOSK",
      "kiosk/UNSET_LAYOUTS",
      "kiosk/UNSET_ACCESSABILITY_MODE",
      "kiosk/UNSET_TOPICS",
      "kiosk/UNSET_IS_SELF_VIEW_VISIBLE",
      "UNSET_RECORD_TRANSLATION"
    ].forEach(m => context.commit(`session/${m}`, null, { root: true }));
    context.commit("surveillance/UNSET_SURVEYS", null, { root: true });

    if (
      telephonyType === TelephonyType.webex &&
      context.rootState.config.config.clientType === "expert"
    ) {
      const webex = context.rootGetters["webex/webex"];
      _.values(webex.meetings?.getAllMeetings()).forEach(m => m.leave());
    }
  }
};

const configModule = {
  namespaced: true,
  state: (): ConfigState => ({
    config: null
  }),
  actions: {
    async getConfig(
      context: {
        commit: (mutation: string, data: unknown, params?: {}) => void;
        dispatch: Function;
        state: ConfigState;
        rootGetters: any;
        rootState: any;
      },
      params?: {
        isTemplatesSupported?: boolean;
      }
    ) {
      console.log("Config retrieving...");

      const data = await axios.get(
        `${window.location.protocol}//${window.location.host}${_.trimEnd(
          window.location.pathname,
          "/"
        )}/${params?.isTemplatesSupported ? "manifest" : "config"}.json`,
        {
          params: { timestamp: moment().unix() }
        }
      );

      const retrievedConfig = params?.isTemplatesSupported
        ? data.data?.configuration ?? {}
        : data.data;

      console.log("Config retrieved: ", retrievedConfig);

      const config: Partial<Config> = _.assign(
        {
          envMode: "production",
          timezone:
            _.find(
              _.entries(Timezone).slice(_.entries(Timezone).length / 2),
              pair => pair[0] === moment.tz.guess()
            )?.[1] ?? Timezone.UTC,
          stagingRequestDuration: 2000
        },
        retrievedConfig
      );

      context.commit("PATCH_CONFIG", config);

      services.rem?.setNetworkCallbacks({
        onLost: () =>
          context.commit("network/SET_STATUS", "disconnected", { root: true }),
        onReconnect: () => {
          context.commit("network/SET_STATUS", "connected", { root: true });

          const clientType = context.rootState.config.config.clientType;

          const cb = () => {
            const cb = () => {
              if (
                context.rootState.auth.jwt &&
                (clientType === "kiosk"
                  ? context.rootState.auth.deviceId
                  : true)
              ) {
                if (context.rootState.telephonyType === TelephonyType.webex) {
                  const webex = context.rootGetters["webex/webex"];
                  const meeting = context.rootGetters["webex/meeting"]
                    ? _.find(
                        webex?.meetings.getAllMeetings(),
                        m => m.id === context.rootGetters["webex/meeting"].id
                      )
                    : null;

                  const endpointStatus = !webex
                    ? EndpointStatus.unknown
                    : !meeting
                    ? EndpointStatus.ready
                    : _.find(
                        meeting.members.membersCollection.members,
                        m => !m.isSelf && m.isUser && m.status === "IN_MEETING"
                      )
                    ? EndpointStatus.connected +
                      (meeting?.mediaProperties?.videoDeviceId === null ||
                      meeting?.isVideoMuted()
                        ? 0
                        : EndpointStatus.videoOn)
                    : context.rootState.config.config.clientType === "kiosk"
                    ? _.find(
                        meeting.members.membersCollection.members,
                        m =>
                          !m.isSelf && m.isUser && m.status === "NOT_IN_MEETING"
                      )
                      ? EndpointStatus.placingCall
                      : EndpointStatus.ready
                    : EndpointStatus.inComingCall;

                  context.dispatch(
                    "setEndpointStatus",
                    {
                      status: endpointStatus,
                      callback: () =>
                        context.dispatch(
                          "getEndpointStatus",
                          {},
                          { root: true }
                        )
                    },
                    { root: true }
                  );
                } else {
                  const room = context.rootGetters["twilio/room"];
                  let isVideoOn = false;

                  if (room) {
                    room?.localParticipant?.videoTracks?.forEach(
                      (publication: LocalVideoTrackPublication) => {
                        if (publication.isEnabled) {
                          isVideoOn = true;
                        }
                      }
                    );
                  }

                  context.dispatch(
                    "setEndpointStatus",
                    {
                      status: room
                        ? EndpointStatus.connected +
                          (isVideoOn ? EndpointStatus.videoOn : 0)
                        : EndpointStatus.ready,
                      callback: () =>
                        context.dispatch(
                          "getEndpointStatus",
                          {},
                          { root: true }
                        )
                    },
                    { root: true }
                  );
                }
              }
            };

            if (clientType === "kiosk") {
              context.dispatch(
                "queue/getPosition",
                {
                  callback: () => cb(),
                  errorCallback: () => cb()
                },
                { root: true }
              );
            } else {
              cb();
            }
          };

          if (clientType === "expert") {
            if (context.rootState.auth.jwt) {
              context.dispatch(
                "setStatus",
                {
                  status: context.rootState.status ?? ClientStatus.unavailable,
                  callback: () => {
                    cb();
                  }
                },
                { root: true }
              );
            } else {
              cb();
            }
          } else {
            cb();
          }
        }
      });

      return config;
    }
  },
  mutations: {
    PATCH_CONFIG(state: ConfigState, config: Partial<Config>) {
      const mergedConfig = _.assign({}, state.config ?? {}, config);

      const envMode = mergedConfig.envMode ?? "production";

      if (!_.keys(services).length) {
        createServices(envMode);
      }

      state.config = mergedConfig;

      patchServicesConfig(
        _.assign(
          _.pick(config, [
            "dcmServiceUrl",
            "inboxServiceUrl",
            "inboxServiceFileStorageUrl",
            "clientName",
            "clientPassword",
            "wbdServiceUrl",
            "clientType",
            "envMode",
            "clientId",
            "clientDisplayName",
            "wbdServiceUrl",
            "extension",
            "mustangExtension",
            "jwt",
            "webexAccessToken",
            "webexClientId",
            "webexRedirectUri",
            "webexScope",
            "webexWithoutLoginInitialization",
            "timezone",
            "telephonyType",
            "tmsServiceUrl",
            "stagingRequestDuration"
          ]) as Partial<Config>,
          config.dcmServiceUrl
            ? {
                dcmServicePort: +(
                  new URL(config.dcmServiceUrl).port || ServiceDefaultPort.dcm
                )
              }
            : {},
          config.remServiceUrl
            ? {
                remServicePort: +(
                  new URL(config.remServiceUrl).port || ServiceDefaultPort.rem
                )
              }
            : {},
          config.wmsServiceUrl
            ? {
                wmsServicePort: +(
                  new URL(config.wmsServiceUrl).port || ServiceDefaultPort.wms
                )
              }
            : {},
          config.wbdServiceUrl && !config.dcmServiceUrl
            ? {
                dcmServiceUrl: `${
                  new URL(config.wbdServiceUrl).protocol === "wss:"
                    ? "https:"
                    : "http:"
                }//${new URL(config.wbdServiceUrl).hostname}:${
                  ServiceDefaultPort.dcm
                }`,
                dcmServicePort: ServiceDefaultPort.dcm
              }
            : {}
        )
      );
    }
  }
};

const signalsModule = {
  namespaced: true,
  actions: {
    subscribe(
      context: {
        commit: (mutation: string, data?: unknown, params?: {}) => void;
        dispatch: Function;
        state: SharingState;
        rootGetters: any;
        rootState: any;
      },
      params?: {
        callbacks?: Record<RemSignal, Function>;
        callback?: Function;
      }
    ) {
      const sendResponse = (params: {
        requestId: number;
        isSuccess: boolean;
        message?: string;
        callback?: Function;
        errorCallback?: Function;
      }) => {
        console.log(
          "Sending signal response...",
          _.pick(params, ["requestId", "isSuccess"])
        );
        services.rem?.sendSignalResponse(
          params.requestId,
          params.isSuccess,
          params.message ?? "",
          (data: unknown) => {
            console.log(
              `Signal (requestId: ${params.requestId}) response has been sent`
            );
            params.callback?.(data);
          },
          (err: unknown) => {
            console.error(
              `Signal (requestId: ${params.requestId}) response sending error: `,
              err
            );
            params.errorCallback?.(err);
          }
        );
      };

      const logSignal = (type: RemSignal, data: any) =>
        console.log(`"${type}" REM signal received: `, data);

      console.log("REM signals subscribing...");

      services.rem?.subscribeOnSignals(
        _.fromPairs([
          [
            RemSignal.answerRequested,
            async (data: [string, string, number] | [string, number]) => {
              logSignal(RemSignal.answerRequested, data);

              const requestId = _.last(data) as number,
                party = data.length === 3 ? data[1] : null;

              if (party) {
                context.commit("session/SET_PARTY", party, {
                  root: true
                });
              }

              const telephonyType = context.rootState.telephonyType;

              const cb = (status: number, details?: string) =>
                context.dispatch(
                  "setEndpointStatus",
                  _.assign(
                    {
                      status
                    },
                    details
                      ? {
                          details
                        }
                      : {}
                  ),
                  { root: true }
                );

              if (telephonyType === TelephonyType.webex) {
                const meeting = context.rootGetters["webex/meeting"];

                if (!meeting) {
                  console.error("Can not answer, no Webex meeting");

                  sendResponse({
                    requestId,
                    isSuccess: false,
                    callback: () =>
                      context.dispatch(
                        "setEndpointStatus",
                        {
                          status: EndpointStatus.unknown
                        },
                        { root: true }
                      )
                  });
                } else {
                  sendResponse({
                    requestId,
                    isSuccess: true
                  });
                }

                meeting
                  ?.join()
                  .then(async () => {
                    if (
                      meeting.id !== context.rootGetters["webex/meeting"]?.id
                    ) {
                      console.log("Meeting is not actual, leaving", meeting);
                      meeting?.leave();
                      return;
                    }

                    cb(
                      EndpointStatus.connected +
                        (meeting.mediaProperties?.videoDeviceId === null ||
                        meeting.isVideoMuted()
                          ? 0
                          : EndpointStatus.videoOn)
                    );
                    const client = _.find(
                      meeting.members.membersCollection.members,
                      m => !m.isSelf && m.isUser && m.status === "IN_MEETING"
                    );

                    if (!client) {
                      console.error("No active meeting member");
                      context.dispatch("webex/leaveMeeting", null, {
                        root: true
                      });
                      return;
                    }
                    context.dispatch("webex/bindMeetingEvents", null, {
                      root: true
                    });
                  })
                  .catch((err: Error) => {
                    if (
                      meeting.id !== context.rootGetters["webex/meeting"]?.id
                    ) {
                      console.log("Meeting is not actual, leaving", meeting);
                      meeting?.leave();
                      return;
                    }
                    cb(
                      EndpointStatus.failure,
                      `${EndpointStatusDetails.invitingToWebexMeetingError} (${err.name}: ${err.message})`
                    );
                  });
              }
            }
          ],
          [
            RemSignal.callRequested,
            async (data: [string, string, number]) => {
              logSignal(RemSignal.callRequested, data);

              const [, party, requestId] = data;

              if (context.rootState.session.callRequestReceivedAt) {
                console.log(
                  `${RemSignal.callRequested} signal has been already received. Current signal ignored`
                );
                sendResponse({
                  requestId,
                  isSuccess: false,
                  message: `${RemSignal.callRequested} signal has been already received.`
                });
                return;
              }

              context.commit("session/UNSET_HANGUP_REQUEST_RECEIVED_AT", null, {
                root: true
              });

              context.commit(
                "session/SET_CALL_REQUEST_RECEIVED_AT",
                +moment(),
                {
                  root: true
                }
              );

              const telephonyType = context.rootState.telephonyType;

              context.commit("session/SET_PARTY", party, {
                root: true
              });

              if (telephonyType === TelephonyType.webex) {
                const webex = context.rootGetters["webex/webex"];

                if (!webex) {
                  console.error("Webex not ready");
                  sendResponse({
                    requestId,
                    isSuccess: false
                  });
                  return;
                }

                sendResponse({
                  requestId,
                  isSuccess: true
                });

                const isActual = () =>
                    party === context.rootState.session.party &&
                    !context.rootState.session.hangupRequestReceivedAt,
                  handleIsNoActual = (meeting: WebexMeeting) => {
                    console.error(
                      "Current meeting is not actual, leaving..",
                      meeting
                    );
                    meeting.leave();
                  };

                const cb = async () => {
                  const meeting = context.rootGetters["webex/meeting"];

                  if (!isActual()) {
                    handleIsNoActual(meeting);
                    return;
                  }

                  iOsMediaInputsBugWorkaround(context, async () => {
                    context.dispatch("webex/bindMeetingEvents", null, {
                      root: true
                    });

                    console.log(`Inviting ${party} to Webex meeting...`);
                    try {
                      await meeting.invite({ emailAddress: party }, false);

                      console.log(`Invited ${party} to Webex meeting`);

                      context.dispatch(
                        "setEndpointStatus",
                        {
                          status: EndpointStatus.placingCall
                        },
                        { root: true }
                      );
                    } catch (err) {
                      console.error(
                        `Inviting ${party} to Webex meeting error: `,
                        err
                      );

                      if (
                        meeting.id !== context.rootGetters["webex/meeting"]?.id
                      ) {
                        console.log("Meeting is not actual, leaving", meeting);
                        meeting?.leave();
                        return;
                      }

                      context.dispatch(
                        "setEndpointStatus",
                        {
                          status: EndpointStatus.failure,
                          details: `${EndpointStatusDetails.invitingToWebexMeetingError} (${err.name} ${err.message})`
                        },
                        { root: true }
                      );
                    }
                  });
                };

                console.log("meeting", context.rootGetters["webex/meeting"]);

                context.rootGetters["webex/meeting"]
                  ? cb()
                  : services.webex?.createMeeting(
                      webex,
                      meeting => {
                        context.commit("webex/SET_MEETING_ID", meeting.id, {
                          root: true
                        });
                        cb();
                      },
                      (err: string) =>
                        context.dispatch(
                          "setEndpointStatus",
                          {
                            status: EndpointStatus.failure,
                            details: `${EndpointStatusDetails.webexMeetingCreatingError} (${err})`
                          },
                          { root: true }
                        )
                    );
              }
            }
          ],
          [
            RemSignal.positionChanged,
            (data: [any, number, CallQueuePositionChangedReason]) => {
              logSignal(RemSignal.positionChanged, data);

              const position = data[1],
                reason = data[2];

              context.commit("queue/SET_POSITION", position, { root: true });
              context.commit("queue/SET_POSITION_CHANGE_REASON", reason, {
                root: true
              });

              if (
                context.rootGetters["webex/meeting"] &&
                context.rootGetters["queue/isThrownUrgently"]
              ) {
                context.dispatch("webex/leaveMeeting", null, { root: true });
              }
            }
          ],
          [
            RemSignal.hangupRequested,
            async (data: [string, number]) => {
              logSignal(RemSignal.hangupRequested, data);

              const requestId = data?.[1];

              sendResponse({
                requestId,
                isSuccess: true
              });

              const telephonyType = context.rootState.telephonyType;

              if (telephonyType === TelephonyType.webex) {
                if (context.rootState.config.config.clientType === "kiosk") {
                  // actual for kiosk only. the case: client clicks 'cancel call' button during Webex meeting creation
                  context.commit(
                    "session/SET_HANGUP_REQUEST_RECEIVED_AT",
                    +moment(),
                    { root: true }
                  );

                  context.commit(
                    "session/UNSET_CALL_REQUEST_RECEIVED_AT",
                    null,
                    { root: true }
                  );
                }
                context.dispatch("webex/leaveMeeting", null, { root: true });
              }
            }
          ],
          [
            RemSignal.message,
            async (data: string[]) => {
              logSignal(RemSignal.message, data);

              if (data[1] === "text/plain") {
                // received notification; is base64 encoded
                const notification = decodeURIComponent(
                  atob(data[2])
                    .split("")
                    .map(
                      c => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`
                    )
                    .join("")
                );
                context.commit(
                  "session/SET_NOTIFICATION",
                  { text: notification, timestamp: +moment() },
                  {
                    root: true
                  }
                );
                return;
              }

              const payload =
                  data[1] === "application/json"
                    ? JSON.parse(decodeURIComponent(escape(atob(data[2]))))
                    : data[2],
                correlationId = data[4] || null;

              const type = (payload as { type: MessageType }).type;

              if (type === MessageType.fileSharingStart) {
                // start file sharing
                const url: string = (payload as { url: string }).url;

                const mimeType = payload.mimeType!;

                const setFile = (url: string) =>
                  context.commit(
                    "sharing/SET_FILE",
                    {
                      type: "remote",
                      file: {
                        mimeType,
                        url
                      }
                    },
                    { root: true }
                  );

                if (url.includes("%rews%")) {
                  const cb = () => {
                    const sessionInfo = _.get(
                      context.rootState,
                      "session.info"
                    ) as SessionInfo;

                    setFile(
                      _.replace(url, "%rews%", sessionInfo.ip_addresses[0])
                    );
                  };

                  if (!_.get(context.rootState, "session.info")) {
                    context.dispatch(
                      "session/getInfo",
                      {
                        callback: cb
                      },
                      { root: true }
                    );
                  } else {
                    cb();
                  }
                } else {
                  setFile(url);
                }
              } else if (type === MessageType.fileSharingStop) {
                // stop file sharing
                context.commit("sharing/UNSET_SHARED_FILE", "remote", {
                  root: true
                });
              } else if (type === MessageType.desktopSharingStart) {
                // start sharing

                const connectionConfig = (payload as {
                  connectionConfig?: {
                    iceServers: {
                      password: string;
                      urls: string | string[];
                      username: string;
                    }[];
                  };
                }).connectionConfig;

                const peer = new Peer(
                  connectionConfig
                    ? {
                        config: {
                          iceServers: _.map(connectionConfig.iceServers, s => ({
                            credential: s.password,
                            urls: s.urls,
                            username: s.username
                          }))
                        }
                      }
                    : {}
                );

                peer.signal({
                  type: "offer",
                  sdp: (payload as { rtcOffer: string }).rtcOffer
                });

                context.commit(
                  "sharing/SET_CORRELATION_ID",
                  { type: "remote", id: correlationId },
                  { root: true }
                );

                const candidatesTimer = setInterval(() => {
                  const candidates = _.cloneDeep(
                    context.rootState.sharing.local.candidates
                  );
                  if (candidates?.length) {
                    context.commit("sharing/UNSET_CANDIDATES", null, {
                      root: true
                    });

                    context.dispatch(
                      "session/sendJsonMessage",
                      {
                        message: {
                          type: MessageType.desktopSharingIceCandidate,
                          iceCandidate: candidates
                        },
                        correlationId: _.get(
                          context.rootState,
                          "sharing.remote.correlationId"
                        ),
                        callback: (data: unknown) =>
                          console.log(
                            "ICE candidate request has been successfully sent."
                          ),
                        errorCallback: (err: string | null = null) =>
                          console.error(
                            "ICE candidate request sending error: ",
                            err
                          )
                      },
                      { root: true }
                    );
                  }
                }, 1000);

                context.commit(
                  "sharing/SET_CANDIDATES_TIMER",
                  candidatesTimer,
                  { root: true }
                );

                peer.on("signal", (data: any) => {
                  console.log("RTC peer signal received: ", data);

                  const signalType = data.type;

                  if (signalType === "answer") {
                    context.dispatch(
                      "session/sendJsonMessage",
                      {
                        message: {
                          type: MessageType.desktopSharingAnswerReady,
                          rtcAnswer: (data as { sdp: string }).sdp
                        },
                        correlationId: _.get(
                          context.rootState,
                          "sharing.remote.correlationId"
                        ),
                        callback: (data: unknown) =>
                          console.log(
                            "Screen sharing answer  has been successfully sent."
                          ),
                        errorCallback: (err: string | null = null) =>
                          console.error(
                            "Screen sharing answer request sending error: ",
                            err
                          )
                      },
                      { root: true }
                    );
                  } else if (signalType === "candidate") {
                    context.commit(
                      "sharing/SET_CANDIDATE",
                      {
                        candidate: data.candidate.candidate,
                        sdpMid: data.candidate.sdpMid,
                        sdpMLineIdx: data.candidate.sdpMLineIndex
                      },
                      { root: true }
                    );
                  }
                });

                peer.on("stream", stream => {
                  console.log("RTC peer stream received: ", stream);

                  context.commit(
                    "sharing/SET_STREAM",
                    { type: "remote", stream },
                    { root: true }
                  );
                });

                peer.on("data", (data: string) => {
                  console.log("Peer data received:", data);

                  const dataParsed = JSON.parse(data) as {
                    type: string;
                    interactive: boolean;
                  };

                  if (dataParsed.type === "state") {
                    context.commit(
                      "sharing/SET_IS_INTERACTIVE",
                      {
                        type: "remote",
                        isInteractive: dataParsed.interactive && false
                      },
                      { root: true }
                    );
                  }
                });

                ["close", "error"].forEach(eventName => {
                  peer.on(eventName, () => {
                    console.log(`RTC peer ${eventName} received`);

                    context.commit("sharing/UNSET_PEER", "remote", {
                      root: true
                    });
                    context.commit("sharing/UNSET_STREAM", "remote", {
                      root: true
                    });
                  });
                });

                context.commit(
                  "sharing/SET_PEER",
                  { type: "remote", peer },
                  { root: true }
                );
              } else if (type === MessageType.desktopSharingStop) {
                // stop sharing
                context.commit("sharing/UNSET_PEER", "remote", { root: true });
                context.commit("sharing/UNSET_STREAM", "remote", {
                  root: true
                });
              } else if (type === MessageType.desktopSharingAnswerReady) {
                // screen sharing answer

                _.get(context.rootState, "sharing.local.peer")?.signal({
                  type: "answer",
                  sdp: (payload as { rtcAnswer: string }).rtcAnswer
                });
              } else if (type === MessageType.desktopSharingIceCandidate) {
                // iceCandidate

                const type =
                  context.rootState.sharing.local.correlationId ===
                  correlationId
                    ? "local"
                    : context.rootState.sharing.remote.correlationId ===
                      correlationId
                    ? "remote"
                    : null;

                if (!type) {
                  console.error(
                    `Received correlation id ${correlationId} is incorrect`
                  );
                  return;
                }

                const iceCandidateData = (payload as {
                  iceCandidate: IceCandidateDTO | IceCandidateDTO[];
                }).iceCandidate;

                (Array.isArray(iceCandidateData)
                  ? iceCandidateData
                  : [iceCandidateData]
                ).forEach(candidate =>
                  context.rootState.sharing[type].peer?.signal({
                    type: "candidate",
                    candidate: new RTCIceCandidate({
                      candidate: candidate.candidate,
                      sdpMLineIndex: candidate.sdpMLineIdx,
                      sdpMid: candidate.sdpMid
                    })
                  })
                );
              } else if (type === MessageType.presentableInfo) {
                const format = (payload as { format: string }).format;
                const messageData = (payload as { data: any }).data;

                if (format === "location-string") {
                  if (messageData) {
                    context.commit("session/kiosk/SET_LOCATION", messageData, {
                      root: true
                    });
                  }
                } else if (["image", "doc", "xls", "ppt"].includes(format)) {
                  context.commit(
                    "sharing/SET_FILE",
                    {
                      type: "remote",
                      file: messageData as {
                        mimeType: string;
                        data: string;
                        type?: "signature";
                      }
                    },
                    { root: true }
                  );
                } else if (format === "pdf") {
                  context.commit(
                    "sharing/SET_FILE",
                    {
                      type: "remote",
                      file: {
                        mimeType: "application/pdf",
                        data: messageData as string
                      }
                    },
                    { root: true }
                  );
                } else if (format === "image-url") {
                  context.commit("session/UNSET_VISUAL_HINT", null, {
                    root: true
                  });
                  context.commit(
                    "session/SET_VISUAL_HINT",
                    {
                      url: messageData,
                      duration: _.get(payload, "duration"),
                      position: _.get(payload, "layout")
                    },
                    { root: true }
                  );
                } else if (format === "qrcode") {
                  context.commit("session/UNSET_QR_CODE", null, {
                    root: true
                  });
                  context.commit(
                    "session/SET_QR_CODE",
                    _.assign(
                      {
                        text: decodeURIComponent(escape(atob(messageData))),
                        duration: _.get(payload, "duration")
                      },
                      _.get(payload, "layout")
                        ? { position: _.get(payload, "layout") }
                        : {}
                    ) as QrCodeDTO,
                    { root: true }
                  );
                } else if (format === "plain-text") {
                  context.commit("session/UNSET_TEXT_HINT", null, {
                    root: true
                  });
                  context.commit(
                    "session/SET_TEXT_HINT",
                    {
                      text: messageData,
                      duration: _.get(payload, "duration")
                    },
                    { root: true }
                  );
                } else if (format === "none") {
                  context.commit("session/UNSET_VISUAL_HINT", null, {
                    root: true
                  });
                } else {
                  // invite member to webex meeting
                  const meeting = context.rootGetters["webex/meeting"];

                  if (
                    _.find(
                      meeting?.members?.membersCollection?.members,
                      member => member.isSelf && member.isHost
                    )
                  ) {
                    const email = (payload as { extension: string }).extension;

                    if (!email) {
                      console.error("Empty extension");
                      return;
                    }

                    console.log(`Inviting ${email} to Webex meeting...`);

                    await meeting?.invite({ emailAddress: email }, false);

                    console.log(`Invited ${email} to Webex meeting`);
                  }
                }
              } else if (
                [
                  MessageType.actionSendFile,
                  MessageType.actionVideoChatNextCamera,
                  MessageType.actionVideoChatEnableCamera,
                  MessageType.actionVideoChatDisableCamera,
                  MessageType.actionVideoChatSetMicrophone,
                  MessageType.actionSendSignature,
                  MessageType.actionCancelSignature,
                  MessageType.actionSendTextMessage,
                  MessageType.actionSendCallInviteLink,
                  MessageType.actionChangeAspectRatio,
                  MessageType.actionScreenSharingStart,
                  MessageType.actionScreenSharingStop,
                  MessageType.actionSelfViewShow,
                  MessageType.actionSelfViewHide,
                  MessageType.actionGeolocationSharingStart,
                  MessageType.actionGeolocationSharingStop,
                  MessageType.actionSetLayout,
                  MessageType.actionSetAccessabilityMode
                ].includes(type)
              ) {
                const requestedAction = _.fromPairs([
                  [MessageType.actionSendFile, RequestedAction.sendFile],
                  [
                    MessageType.actionVideoChatNextCamera,
                    RequestedAction.videoChatNextCamera
                  ],
                  [
                    MessageType.actionVideoChatEnableCamera,
                    RequestedAction.videoChatEnableCamera
                  ],
                  [
                    MessageType.actionVideoChatDisableCamera,
                    RequestedAction.videoChatDisableCamera
                  ],
                  [
                    MessageType.actionVideoChatSetMicrophone,
                    RequestedAction.videoChatSetMicrophone
                  ],
                  [
                    MessageType.actionSendSignature,
                    RequestedAction.sendSignature
                  ],
                  [
                    MessageType.actionCancelSignature,
                    RequestedAction.cancelSignature
                  ],
                  [
                    MessageType.actionSendTextMessage,
                    RequestedAction.sendTextMessage
                  ],
                  [
                    MessageType.actionSendCallInviteLink,
                    RequestedAction.sendCallInviteLink
                  ],
                  [
                    MessageType.actionChangeAspectRatio,
                    RequestedAction.changeAspectRatio
                  ],
                  [
                    MessageType.actionScreenSharingStart,
                    RequestedAction.screenSharingStart
                  ],
                  [
                    MessageType.actionScreenSharingStop,
                    RequestedAction.screenSharingStop
                  ],
                  [
                    MessageType.actionSelfViewShow,
                    RequestedAction.selfViewShow
                  ],
                  [
                    MessageType.actionSelfViewHide,
                    RequestedAction.selfViewHide
                  ],
                  [
                    MessageType.actionGeolocationSharingStart,
                    RequestedAction.geolocationSharingStart
                  ],
                  [
                    MessageType.actionGeolocationSharingStop,
                    RequestedAction.geolocationSharingStop
                  ],
                  [MessageType.actionSetLayout, RequestedAction.setLayout],
                  [
                    MessageType.actionSetAccessabilityMode,
                    RequestedAction.setAccessabilityMode
                  ]
                ])[type];

                requestedAction === RequestedAction.cancelSignature
                  ? context.commit(
                      "session/UNSET_REQUESTED_ACTION",
                      RequestedAction.sendSignature,
                      { root: true }
                    )
                  : context.commit(
                      "session/SET_REQUESTED_ACTION",
                      _.assign(
                        {
                          action: requestedAction
                        },
                        _.pick(payload, "payload")
                      ),
                      {
                        root: true
                      }
                    );
              } else if (type === MessageType.aspectRatio) {
                const aspectRatio = _.get(payload, "aspectRatio");

                context.commit("session/kiosk/SET_ASPECT_RATIO", aspectRatio, {
                  root: true
                });
              } else if (type === MessageType.screenResolution) {
                const resolution = _.get(payload, "screenResolution");

                context.commit(
                  "session/kiosk/SET_SCREEN_RESOLUTION",
                  resolution,
                  { root: true }
                );
              } else if (type === MessageType.microphones) {
                // client sent microphones list
                context.commit(
                  "session/kiosk/SET_MICROPHONES",
                  _.get(payload, "data"),
                  {
                    root: true
                  }
                );
              } else if (type === MessageType.selfViewShown) {
                context.commit("session/kiosk/SET_IS_SELF_VIEW_VISIBLE", true, {
                  root: true
                });
              } else if (type === MessageType.selfViewHidden) {
                context.commit(
                  "session/kiosk/SET_IS_SELF_VIEW_VISIBLE",
                  false,
                  {
                    root: true
                  }
                );
              } else if (type === MessageType.layouts) {
                context.commit(
                  "session/kiosk/SET_LAYOUTS",
                  _.get(payload, "layouts"),
                  {
                    root: true
                  }
                );
              } else if (type === MessageType.accessabilityMode) {
                context.commit(
                  "session/kiosk/SET_ACCESSABILITY_MODE",
                  _.get(payload, "mode"),
                  {
                    root: true
                  }
                );
              } else if (type === MessageType.awsTranslationFinished) {
                const { text, audioFilePath } = payload as Record<
                  "text" | "audioFilePath",
                  string
                >;

                if (
                  !context.rootGetters["auth/property"](
                    "aws.playing.translated.audio.enabled"
                  ) ||
                  !audioFilePath
                ) {
                  context.commit(
                    "session/SET_RECORD_TRANSLATION",
                    { text },
                    { root: true }
                  );
                  return;
                }

                console.log("Retrieving audio from AWS S3... ", audioFilePath);

                const audio: Uint8Array = (await services.aws?.getFile(
                  audioFilePath
                )) as Uint8Array;

                console.log(
                  "Retrieved audio from AWS S3, data length : ",
                  audio.length
                );

                context.commit(
                  "session/SET_RECORD_TRANSLATION",
                  { text, audio },
                  { root: true }
                );
              }
            }
          ],
          [
            RemSignal.endpointStatusChanged,
            async (data: [string, EndpointStatusDTO]) => {
              logSignal(RemSignal.endpointStatusChanged, data);

              const status = data[1];

              context.commit("SET_ENDPOINT_STATUS", status, { root: true });

              handleEndpointStatusChange(context);
            }
          ],
          [
            RemSignal.membershipRequested,
            async (data: string[]) => {
              logSignal(RemSignal.membershipRequested, data);

              const webexRoomId = data[2],
                personId = data[1];

              if (
                context.rootGetters.endpointStatusCode !==
                EndpointStatus.connected
              ) {
                console.error(
                  "Can not add guest to meeting. Meeting is not in active state"
                );
                return;
              }
              const webex = context.rootGetters["webex/webex"];

              const currentRoomId = _.get(
                (await webex?.rooms?.list({ max: 1 }))?.items ?? [],
                "[0].id"
              );

              if (currentRoomId !== webexRoomId) {
                console.error("Can not add guest to meeting. Wrong room id");
                return;
              }

              await webex.memberships
                .create({ roomId: webexRoomId, personId })
                .catch((err: Error) => {
                  console.error("Can not add member to meeting:", err);
                });
            }
          ],
          [
            RemSignal.statusChanged,
            (data: [string, ClientStatus, number]) => {
              logSignal(RemSignal.statusChanged, data);

              const [uuid, status, reason] = data;

              if (context.rootState.auth.me?.uuid === uuid) {
                context.commit("SET_STATUS", status, { root: true });
              }
            }
          ],
          [
            RemSignal.recvFrom,
            (data: [string, string, string, string, boolean]) => {
              logSignal(RemSignal.recvFrom, data);

              const [deviceId, serviceName, message, messageId, isError] = data;

              if (serviceName === "arecorder") {
                const survey: Survey | null =
                  _.find(
                    context.rootState.surveillance.surveys,
                    s => s.deviceId === deviceId
                  ) ?? null;

                const patchSurvey = (
                  data: Partial<Survey> | { snapshot: string }
                ) =>
                  context.commit(
                    "surveillance/PATCH_SURVEY",
                    {
                      deviceId,
                      data: _.keys(data).includes("snapshot")
                        ? {
                            snapshots: (survey?.snapshots ?? []).concat(
                              _.values(data) as string[]
                            )
                          }
                        : data
                    },
                    { root: true }
                  );

                if (isError) {
                  console.error(
                    `Can not survey kiosk "${deviceId}": `,
                    message
                  );
                  patchSurvey({ errorMessage: message });
                  return;
                }
                const messageData: {
                  type: string;
                  data: {};
                } = JSON.parse(message);

                const { type, data } = messageData;

                if (type === "surveillance_connection_error") {
                  patchSurvey({
                    errorMessage: (data as Record<
                      "connectionId" | "message",
                      string
                    >).message
                  });
                  return;
                } else if (type === "error") {
                  patchSurvey({
                    errorMessage: (data as { message: string }).message
                  });
                  return;
                } else if (type === "surveillance_offer") {
                  if (!survey) {
                    console.error(
                      `Can not fond survey data for kiosk "${deviceId}"`
                    );
                    return;
                  }
                  const sdp = (data as Record<"connectionId" | "sdp", string>)
                    .sdp;

                  const peer = new Peer(
                    survey.connectionConfig
                      ? {
                          config: survey.connectionConfig
                        }
                      : {}
                  );

                  peer.on("signal", (data: any) => {
                    console.log("RTC peer signal received: ", data);

                    const signalType = data.type;

                    if (!["answer", "candidate"].includes(signalType)) {
                      return;
                    }

                    const transmittedMessage = {
                      type:
                        signalType === "candidate"
                          ? "surveillance_ice_candidate"
                          : "surveillance_answer",
                      data:
                        signalType === "candidate"
                          ? {
                              connectionId: survey.connectionId,
                              sdpMid: data.candidate.sdpMid,
                              sdpMlineIdx: data.candidate.sdpMLineIndex,
                              candidate: data.candidate.candidate
                            }
                          : {
                              connectionId: survey.connectionId,
                              sdp: (data as { sdp: string }).sdp
                            }
                    };

                    if (
                      signalType === "candidate" &&
                      !data.candidate.candidate
                    ) {
                      console.log(
                        "Empty ice candidate, ignored sending to hub: ",
                        transmittedMessage
                      );
                      return;
                    }

                    console.log("Sending to hub: ", {
                      receiverId: deviceId,
                      message: transmittedMessage
                    });

                    services.rem?.sendToHub(
                      {
                        receiverId: deviceId,
                        serviceName,
                        message: transmittedMessage,
                        messageId: `{${uuidv4()}}`
                      },
                      (data: unknown) => console.log("Sent to hub: ", data),
                      (err: unknown) =>
                        console.error("Sending to hub error: ", err)
                    );
                  });

                  peer.on("stream", stream => {
                    console.log("RTC peer stream received: ", stream);

                    patchSurvey({
                      streams: (survey?.streams ?? []).concat([stream])
                    });
                  });

                  ["close", "error"].forEach(eventName => {
                    peer.on(eventName, () => {
                      console.log(`RTC peer ${eventName} received: `);

                      patchSurvey(
                        _.assign(
                          {
                            peer: undefined,
                            stream: undefined
                          },
                          eventName === "error"
                            ? {
                                errorMessage: "RTC peer error"
                              }
                            : {}
                        )
                      );
                    });
                  });

                  type SnapshotChunk =
                    | {
                        type: "snapshot_begin" | "snapshot_end";
                        data: {
                          type?: string;
                          size?: number;
                        };
                      }
                    | Uint8Array;

                  let snapshotChunks: SnapshotChunk[] = [];

                  peer.on("data", (data: Uint8Array) => {
                    console.log("RTC peer data received: ", data);

                    const isJson = (str: Uint8Array): boolean => {
                      try {
                        JSON.parse(new TextDecoder().decode(str));
                      } catch (e) {
                        return false;
                      }
                      return true;
                    };

                    if (!isJson(data)) {
                      snapshotChunks.push(data);
                      return;
                    }

                    const chunk = JSON.parse(
                        new TextDecoder().decode(data)
                      ) as SnapshotChunk,
                      type = _.get(chunk, "type");

                    if (type === "control_info") {
                      patchSurvey({
                        controlInfo: _.get(chunk, "data")
                      } as Partial<Survey>);
                    } else if (type === "snapshot_end") {
                      const startChunkIndex = _.findLastIndex(
                        snapshotChunks,
                        ch =>
                          !(ch instanceof Uint8Array) &&
                          ch.type === "snapshot_begin"
                      );
                      if (startChunkIndex === -1) {
                        console.error(
                          "Can not find snapshot start chunk index"
                        );
                        snapshotChunks = [];
                        return;
                      }
                      const dataChunks = snapshotChunks.splice(
                          startChunkIndex + 1
                        ) as Uint8Array[],
                        size = _.get(
                          snapshotChunks[startChunkIndex],
                          "data.size"
                        ),
                        dataSize = _.reduce(
                          dataChunks,
                          (sum: number, ch: Uint8Array) => sum + ch.length,
                          0
                        );

                      if (size !== dataSize) {
                        console.error(
                          `Snapshot size should be ${size} bytes, not ${dataSize}`
                        );
                        snapshotChunks = [];
                        return;
                      }

                      const merged = new Uint8Array(size);
                      let offset = 0;
                      dataChunks.forEach((item: Uint8Array) => {
                        merged.set(item, offset);
                        offset += item.length;
                      });

                      patchSurvey({
                        snapshots: survey.snapshots.concat([
                          `data:${_.get(
                            snapshotChunks[startChunkIndex],
                            "data.type",
                            "image/png"
                          )};base64,${btoa(
                            _.reduce(
                              merged,
                              (data, byte) =>
                                `${data}${String.fromCharCode(byte)}`,
                              ""
                            )
                          )}`
                        ])
                      });

                      snapshotChunks = snapshotChunks.splice(
                        0,
                        startChunkIndex
                      );
                    } else {
                      snapshotChunks.push(chunk);
                    }
                  });

                  peer.on("connect", () => {
                    console.log("RTC peer connect received: ");

                    patchSurvey({ isDataChannelReady: true });
                  });

                  patchSurvey({ peer });

                  peer.signal({
                    type: "offer",
                    sdp
                  });

                  return;
                } else if (type === "surveillance_ice_candidate") {
                  const {
                    connectionId,
                    sdpMid,
                    sdpMlineIdx,
                    candidate
                  } = data as {
                    connectionId: string;
                    sdpMid: string;
                    sdpMlineIdx: number;
                    candidate: string;
                  };

                  survey?.peer?.signal({
                    type: "candidate",
                    candidate: new RTCIceCandidate({
                      candidate,
                      sdpMLineIndex: sdpMlineIdx,
                      sdpMid
                    })
                  });
                }
              }
            }
          ],
          [
            RemSignal.holdRequested,
            async (data: [string, boolean, number]) => {
              const [, isOnHold, requestId] = data;

              logSignal(RemSignal.holdRequested, data);

              const telephonyType = context.rootState.telephonyType;

              if (telephonyType === TelephonyType.webex) {
                const meeting = context.rootGetters["webex/meeting"];

                if (!meeting) {
                  console.error("No Webex meeting");

                  sendResponse({
                    requestId,
                    isSuccess: false
                  });

                  return;
                }

                const participantId = _.find(
                  meeting?.members?.membersCollection?.members,
                  (m: WebexMeetingMember) => !m.isSelf && m.isUser
                )?.id;

                if (!participantId) {
                  console.error("Can not find meeting participant");

                  sendResponse({
                    requestId,
                    isSuccess: false
                  });

                  return;
                }

                sendResponse({
                  requestId,
                  isSuccess: true
                });

                if (isOnHold) {
                  console.log("Webex meeting audio muting...");

                  await meeting?.muteAudio();

                  console.log("Webex meeting audio muted");

                  console.log("Webex meeting video muting...");

                  await meeting?.muteVideo();

                  console.log("Webex meeting video muted");

                  console.log(
                    `Webex meeting member "${participantId}" muting...`
                  );

                  await meeting?.mute(participantId, true);

                  console.log(`Webex meeting member "${participantId}" muted`);

                  context.dispatch(
                    "setEndpointStatus",
                    {
                      status:
                        (context.rootState.endpointStatus.status ?? 0) +
                        (context.rootGetters.isOnHold
                          ? 0
                          : EndpointStatus.onHold)
                    },
                    { root: true }
                  );
                } else {
                  if (!context.rootState.session.isMuted) {
                    console.log("Webex meeting audio unmuting...");

                    await meeting?.unmuteAudio();

                    console.log("Webex meeting audio unmuted");
                  }

                  console.log("Webex meeting video unmuting...");

                  await meeting?.unmuteVideo();

                  console.log("Webex meeting video unmuted");

                  console.log(
                    `Webex meeting member "${participantId}" unmuting...`
                  );

                  await meeting?.mute(participantId, false);

                  console.log(
                    `Webex meeting member "${participantId}" unmuted`
                  );

                  context.dispatch(
                    "setEndpointStatus",
                    {
                      status:
                        (context.rootState.endpointStatus.status ?? 0) -
                        (context.rootGetters.isOnHold
                          ? EndpointStatus.onHold
                          : 0)
                    },
                    { root: true }
                  );
                }
              }
            }
          ],
          [
            RemSignal.shutdownRequested,
            async (data: unknown) => {
              logSignal(RemSignal.shutdownRequested, data);
              context.commit("network/SET_STATUS", "shutdown", { root: true });
            }
          ],
          [
            RemSignal.sessionInfoChanged,
            async (data: [string, SessionInfo]) => {
              logSignal(RemSignal.sessionInfoChanged, data);
              context.commit("session/SET_INFO", data[1], { root: true });
            }
          ],
          [
            RemSignal.twilioJoinRequested,
            async (data: [string, string, number]) => {
              logSignal(RemSignal.twilioJoinRequested, data);

              const [, party, requestId] = data;

              context.commit("session/SET_PARTY", party, {
                root: true
              });

              sendResponse({
                requestId,
                isSuccess: true
              });

              try {
                const token = await context.dispatch(
                  "twilio/getAccessToken",
                  { roomName: party },
                  { root: true }
                );

                iOsMediaInputsBugWorkaround(context, async () => {
                  await context.dispatch(
                    "twilio/joinRoom",
                    { name: party, token },
                    { root: true }
                  );
                  context.dispatch("twilio/bindRoomEvents", null, {
                    root: true
                  });
                });
              } catch (err) {
                console.error("Twilio error: ", err);
                context.dispatch(
                  "setEndpointStatus",
                  {
                    status: EndpointStatus.failure,
                    details: `${EndpointStatusDetails.twilioAccessTokenRetrievingError} (${err.name} ${err.message})`
                  },
                  { root: true }
                );
              }
            }
          ],
          [
            RemSignal.twilioLeaveRequested,
            async (data: [string, number]) => {
              logSignal(RemSignal.twilioLeaveRequested, data);

              const requestId = data?.[1];

              sendResponse({
                requestId,
                isSuccess: true
              });

              if (
                context.rootGetters.endpointStatusCode ===
                EndpointStatus.inComingCall
              ) {
                context.dispatch(
                  "setEndpointStatus",
                  {
                    status: EndpointStatus.ready
                  },
                  { root: true }
                );
              } else {
                context.dispatch("twilio/leaveRoom", null, { root: true });
              }
            }
          ],
          [
            RemSignal.availableTopicsChanged,
            async (topics: [Record<string, number[]>]) => {
              logSignal(RemSignal.availableTopicsChanged, topics);

              context.commit("topics/SET_AVAILABLE", topics[0], { root: true });
            }
          ],
          [
            RemSignal.ticketsCountChanged,
            async (data: [Record<"total" | "assigned", number>]) => {
              logSignal(RemSignal.ticketsCountChanged, data);

              const [count] = data;

              context.commit("tickets/SET_COUNT", count, { root: true });
            }
          ],
          [
            RemSignal.incidentChanged,
            async (data: [IncidentDTO]) => {
              logSignal(RemSignal.incidentChanged, data);

              const [incident] = data;

              context.commit("incident/SET_INCIDENT", incident, { root: true });
            }
          ]
        ]),
        params?.callback
      );
    },
    unsubscribe(context: {
      commit: (mutation: string, data?: number) => void;
      rootState: unknown;
    }) {
      console.log("REM signals unsubscribing...");
      services.rem?.unsubscribeFromSignals();
    },
    sendSignalResponse(
      context: {
        commit: (mutation: string, data?: number) => void;
        rootState: unknown;
      },
      params: {
        requestId: number;
        isSuccess: boolean;
        message?: string;
        callback: Function;
        errorCallback?: Function;
      }
    ) {
      console.log("Sending REM signal response...");

      services.rem?.sendSignalResponse(
        params.requestId,
        params.isSuccess,
        params.message ?? "",
        (data: unknown) => {
          console.log("REM signal response sent: ", data);

          params.callback?.(data);
        },
        (err: unknown) => {
          console.error("REM signal response sending error: ", err);

          params.errorCallback?.(err);
        }
      );
    }
  }
};

const networkModule = {
  namespaced: true,
  state: (): NetworkState => ({
    status: "connected"
  }),
  getters: {
    status(state: NetworkState) {
      return state.status;
    }
  },
  actions: {
    setCallbacks(
      context: { rootState: unknown },
      callbacks: Record<"onLost" | "onReconnect", Function>
    ) {
      services.rem?.setNetworkCallbacks(callbacks);
    }
  },
  mutations: {
    SET_STATUS(
      state: NetworkState,
      status: "connected" | "disconnected" | "shutdown"
    ) {
      state.status = status;
    }
  }
};

interface ClientsState {
  availableExperts: Record<string, {}> | null;
  experts: ExpertDTO[] | null;
  kiosks: KioskDTO[] | null;
}

const clientsModule = {
  namespaced: true,
  state: (): ClientsState => ({
    availableExperts: null,
    experts: null,
    kiosks: null
  }),
  actions: {
    getAvailableExperts: async (
      context: {
        commit: (mutation: string, experts: Record<string, {}>) => void;
        rootState: unknown;
      },
      params: {
        callback: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Retrieving available experts...");

      services.rem?.getAvailableExperts(
        (experts: Record<string, {}>) => {
          console.log("Available experts retrieved: ", experts);

          context.commit("SET_AVAILABLE_EXPERTS", experts);
          params.callback?.(experts);
        },
        (err: unknown) => {
          console.error("Available experts retrieving error: ", err);

          params.errorCallback?.(err);
        }
      );
    },
    getExperts: async (
      context: {
        commit: (mutation: string, experts: Record<string, {}>) => void;
        rootState: unknown;
      },
      params: {
        callback: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Experts retrieving...");

      services.rem?.getExperts(
        (experts: Record<string, {}>) => {
          console.log("Experts retrieved: ", experts);
          context.commit("SET_EXPERTS", experts);
          params.callback(experts);
        },
        (err: any) => {
          console.error("Experts retrieving error: ", err);
          params.errorCallback?.(err);
        }
      );
    },
    getKiosks: async (
      context: {
        commit: (mutation: string, kiosks: KioskDTO[]) => void;
        rootState: unknown;
      },
      params: {
        callback: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Kiosks retrieving...");

      services.rem?.getKiosks(
        (kiosks: KioskDTO[]) => {
          console.log("Kiosks retrieved: ", kiosks);
          context.commit("SET_KIOSKS", kiosks);
          params.callback(kiosks);
        },
        (err: any) => {
          console.error("Kiosks retrieving error: ", err);
          params.errorCallback?.(err);
        }
      );
    }
  },
  mutations: {
    SET_AVAILABLE_EXPERTS(state: ClientsState, experts: Record<string, {}>) {
      state.availableExperts = experts;
    },
    UNSET_AVAILABLE_EXPERTS(state: ClientsState) {
      state.availableExperts = null;
    },
    SET_EXPERTS(state: ClientsState, experts: []) {
      state.experts = experts;
    },
    UNSET_EXPERTS(state: ClientsState) {
      state.experts = null;
    },
    SET_KIOSKS(state: ClientsState, kiosks: KioskDTO[]) {
      state.kiosks = kiosks;
    },
    UNSET_KIOSKS(state: ClientsState) {
      state.kiosks = null;
    }
  }
};

interface InboxState {
  mails: MailCollectionDTO | null;
  mail: MailDTO | null;
  smsSnippets: Record<string, string[]> | null;
}

const inboxModule = {
  namespaced: true,
  state: (): InboxState => ({
    mails: null,
    mail: null,
    smsSnippets: null
  }),
  getters: {
    mails(state: InboxState) {
      return state.mails;
    },
    mail(state: InboxState) {
      return state.mail;
    },
    getAttachmentsUrl: (
      state: InboxState,
      getters: unknown,
      rootState: unknown
    ) => (params: { phone: string; timestamp: string }) =>
      services.inbox?.getMailAttachmentsUrl(params),
    smsSnippets(state: InboxState) {
      if (state.smsSnippets) {
        return state.smsSnippets;
      }

      const value = localStorage.getItem(inboxSmsSnippetsStorageKey);

      return value ? JSON.parse(value) : null;
    }
  },
  actions: {
    createEmail: async (
      context: {
        commit: (mutation: string) => void;
        rootState: unknown;
      },
      params: {
        phone: string;
        deleteIn?: number;
      }
    ) => {
      console.log("Email creating...");

      const result = await services.inbox?.createEmail(params);

      console.log("Email created: ", result);

      return result;
    },
    getMails: async (
      context: {
        commit: (mutation: string, data: MailCollectionDTO) => void;
        rootState: unknown;
      },
      params: {
        phone: string;
      }
    ) => {
      console.log("Mails retrieving...");

      const mails = await services.inbox?.getMails(params);

      console.log("Mails retrieved: ", mails);

      context.commit("SET_MAILS", mails!);
      return mails;
    },
    getMail: async (
      context: {
        commit: (mutation: string, data: MailDTO) => void;
        rootState: unknown;
      },
      params: {
        phone: string;
        timestamp: string;
      }
    ) => {
      console.log("Mail retrieving...", params);

      const mail = await services.inbox?.getMail(params);

      console.log("Mail retrieved");

      context.commit("SET_MAIL", mail!);
      return mail;
    },
    getMailAttachments: async (
      context: {
        commit: (mutation: string, data: []) => void;
        rootState: unknown;
      },
      params: {
        phone: string;
        timestamp: string;
      }
    ) => {
      console.log("Mail attachments retrieving...", params);

      const attachments = await services.inbox?.getMailAttachments(params);

      console.log("Mail attachments retrieved", attachments);

      return attachments;
    },
    deleteEmail: async (
      context: {
        commit: (mutation: string, data: unknown) => void;
        rootState: unknown;
      },
      params: {
        phone: string;
        deleteIn?: number;
      }
    ) => {
      console.log("Email deleting...", params);

      const result = await services.inbox?.deleteEmail(params);

      console.log("Email deleted: ", result);

      return result;
    },
    sendMail: async (
      context: {
        commit: (mutation: string, data: unknown) => void;
        rootState: unknown;
      },
      params: {
        to: string;
        subject: string;
        body: string;
        attachments?: File[];
      }
    ) => {
      console.log("Sending mail...", params);

      const toBase64 = async (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () =>
            resolve(_.split(`${reader.result}`, ";base64,")[1]);
          reader.onerror = err => reject(err);
        });
      };

      const attachments = params.attachments?.length
        ? await Promise.all(
            params.attachments.map(async a => ({
              // eslint-disable-next-line @typescript-eslint/camelcase
              content_type: a.type,
              filename: a.name,
              payload: await toBase64(a)
            }))
          )
        : [];

      const result = await services.inbox?.sendMail(
        _.assign(
          _.omit(params, "attachments"),
          attachments.length ? { attachments } : {}
        )
      );

      console.log("Mail sent: ", result);

      return result;
    },
    forwardMail: async (
      context: {
        commit: (mutation: string, data: unknown) => void;
        rootState: unknown;
      },
      params: {
        phone: string;
        timestamp: string;
        recipient: string;
      }
    ) => {
      console.log("Forwarding mail...", params);

      const result = await services.inbox?.forwardMail(params);

      console.log("Mail forwarded: ", result);

      return result;
    },
    sendSms: async (
      context: {
        commit: (mutation: string) => void;
        rootState: unknown;
      },
      params: {
        phone: string;
        message: string;
      }
    ) => {
      console.log("Sending sms...", params);

      const result = await services.inbox?.sendSms(params);

      console.log("Sms sent: ", result);

      return result;
    }
  },
  mutations: {
    SET_MAILS(state: InboxState, mails: MailCollectionDTO) {
      state.mails = mails;
    },
    UNSET_MAILS(state: InboxState) {
      state.mails = null;
    },
    SET_MAIL(state: InboxState, mail: MailDTO) {
      state.mail = mail;
    },
    UNSET_MAIL(state: InboxState) {
      state.mail = null;
    },
    SET_SMS_SNIPPETS(state: InboxState, snippets: Record<string, string[]>) {
      state.smsSnippets = snippets;
      localStorage.setItem(
        inboxSmsSnippetsStorageKey,
        JSON.stringify(snippets)
      );
    }
  }
};

interface SurveillanceState {
  surveys: Survey[] | null;
}

const surveillanceModule = {
  namespaced: true,
  state: (): SurveillanceState => ({
    surveys: null
  }),
  actions: {
    survey: (
      context: {
        commit: (mutation: string, data: Survey) => void;
        rootState: unknown;
      },
      params: {
        deviceId: string;
        sources?: {
          type: "screen" | "camera";
          name?: string;
          index: number;
        }[];
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Running survey: ", _.pick(params, "deviceId"));

      console.log("RTC configs retrieving...");

      services.rem?.getRtcConnectionConfig(
        (config: {
          iceServers: {
            password: string;
            urls: string | string[];
            username: string;
          }[];
        }) => {
          console.log("RTC connection config retrieved: ", config);

          const serviceName = "arecorder",
            connectionId = `{${uuidv4()}}`;

          const connectionConfig: RTCConfiguration | null = config.iceServers
            ?.length
            ? {
                iceServers: _.map(config.iceServers, s => ({
                  username: s.username,
                  credential: s.password,
                  urls: s.urls
                }))
              }
            : null;

          const sources = params.sources ?? [
            {
              type: "screen",
              index: 0
            }
            // {
            //   type: "camera",
            //   index: 0
            // }
          ];

          const message = {
            type: "surveillance_create_peer_connection",
            data: {
              connectionId,
              connectionConfig: {
                iceServers: _.map(connectionConfig?.iceServers, s => ({
                  password: s.credential,
                  urls: Array.isArray(s.urls) ? s.urls : [s.urls],
                  username: s.username
                }))
              },
              sources
            }
          };

          console.log("Sending to hub: ", {
            receiverId: params.deviceId,
            message
          });

          services.rem?.sendToHub(
            {
              receiverId: params.deviceId,
              serviceName,
              message,
              messageId: `{${uuidv4()}}`
            },
            (data: unknown) => {
              console.log("Sent to hub: ", data);

              context.commit(
                "SET_SURVEY",
                _.assign(
                  {
                    sources,
                    connectionId,
                    deviceId: params.deviceId,
                    snapshots: [],
                    isDataChannelReady: false
                  },
                  connectionConfig ? { connectionConfig } : {}
                ) as Survey
              );

              params.callback?.(data);
            },
            (err: unknown) => {
              console.error("Sending to hub error: ", err);
              params?.errorCallback?.(err);
            }
          );
        },
        (err: unknown) => {
          console.error("RTC connection config retrieving error ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    takeSnapshot: (
      context: {
        commit: (mutation: string) => void;
        rootState: unknown;
      },
      survey: Survey
    ) => {
      if (!survey.isDataChannelReady) {
        throw new Error("Data channel is not ready");
      }
      survey?.peer?.send(
        JSON.stringify({
          type: "start_snapshot",
          data: { track: 0 }
        })
      );
    },
    toggleLaser: (
      context: {
        commit: (mutation: string) => void;
        rootState: unknown;
      },
      params: {
        survey: Survey;
        isEnabled: boolean;
      }
    ) => {
      if (!params.survey.isDataChannelReady) {
        throw new Error("Data channel is not ready");
      }
      params.survey?.peer?.send(
        JSON.stringify({
          type: "set_laser_enabled",
          data: { track: 0, enabled: params.isEnabled }
        })
      );
    },
    startZoom: (
      context: {
        commit: (mutation: string) => void;
        rootState: unknown;
      },
      params: {
        survey: Survey;
        direction: "in" | "out";
      }
    ) => {
      if (!params.survey.isDataChannelReady) {
        throw new Error("Data channel is not ready");
      }
      params.survey?.peer?.send(
        JSON.stringify({
          type: "start_zoom",
          data: { track: 0, direction: params.direction === "in" ? 1 : -1 }
        })
      );
    },
    stopZoom: (
      context: {
        commit: (mutation: string) => void;
        rootState: unknown;
      },
      survey: Survey
    ) => {
      if (!survey.isDataChannelReady) {
        throw new Error("Data channel is not ready");
      }
      survey?.peer?.send(
        JSON.stringify({
          type: "stop_zoom",
          data: { track: 0 }
        })
      );
    },
    startPan: (
      context: {
        commit: (mutation: string) => void;
        rootState: unknown;
      },
      params: {
        survey: Survey;
        direction: "right" | "left";
      }
    ) => {
      if (!params.survey.isDataChannelReady) {
        throw new Error("Data channel is not ready");
      }
      params.survey?.peer?.send(
        JSON.stringify({
          type: "start_pan",
          data: { track: 0, direction: params.direction === "right" ? 1 : -1 }
        })
      );
    },
    stopPan: (
      context: {
        commit: (mutation: string) => void;
        rootState: unknown;
      },
      survey: Survey
    ) => {
      if (!survey.isDataChannelReady) {
        throw new Error("Data channel is not ready");
      }
      survey?.peer?.send(
        JSON.stringify({
          type: "stop_pan",
          data: { track: 0 }
        })
      );
    },
    startTilt: (
      context: {
        commit: (mutation: string) => void;
        rootState: unknown;
      },
      params: {
        survey: Survey;
        direction: "down" | "up";
      }
    ) => {
      if (!params.survey.isDataChannelReady) {
        throw new Error("Data channel is not ready");
      }
      params.survey?.peer?.send(
        JSON.stringify({
          type: "start_tilt",
          data: { track: 0, direction: params.direction === "down" ? 1 : -1 }
        })
      );
    },
    stopTilt: (
      context: {
        commit: (mutation: string) => void;
        rootState: unknown;
      },
      survey: Survey
    ) => {
      if (!survey.isDataChannelReady) {
        throw new Error("Data channel is not ready");
      }
      survey?.peer?.send(
        JSON.stringify({
          type: "stop_tilt",
          data: { track: 0 }
        })
      );
    }
  },
  mutations: {
    SET_SURVEY(state: SurveillanceState, survey: Survey) {
      state.surveys = (state.surveys ?? []).concat([survey]);
    },
    PATCH_SURVEY(
      state: SurveillanceState,
      params: { deviceId: string; data: Partial<Survey> }
    ) {
      const survey = _.find(state.surveys, s => s.deviceId === params.deviceId);

      if (!survey) {
        console.error(
          `Can not find survey data for device id: ${params.deviceId}`
        );
        return;
      }

      _.assign(survey, params.data);
    },
    UNSET_SURVEY(state: SurveillanceState, survey: Survey) {
      survey.peer?.destroy();

      state.surveys = state.surveys
        ? _.filter(state.surveys, s => s !== survey)
        : null;
    },
    UNSET_SURVEYS(state: SurveillanceState) {
      state.surveys?.forEach(s => s.peer?.destroy());
      state.surveys = null;
    }
  }
};

interface UsbIpState {
  devices:
    | {
        device: UsbIpDeviceDTO;
        status: "attached" | "detached" | "timedOut" | "failed";
      }[]
    | null;
}

const usbIpModule = {
  namespaced: true,
  state: (): UsbIpState => ({
    devices: null
  }),
  actions: {
    start: (
      context: {
        commit: (mutation: string, data: any) => void;
        rootState: any;
        rootGetters: any;
      },
      params?: {
        notificationCallbacks?: {
          connectedToServer?: Function;
          disconnectedFromServer?: Function;
          deviceAttached?: Function;
          deviceDetached?: Function;
          deviceAttachTimedOut?: Function;
          failedToAttachDevice?: Function;
        };
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Starting USB IP ...");

      services.agent?.startUsbIp(
        {
          hostName: (context.rootState.session.info
            .ip_addresses as string[])?.[0],
          deviceAttachTimeout:
            context.rootGetters["auth/property"](
              "usbip.device.connection.timeout"
            ) ?? 2000,
          notificationCallbacks: {
            connectedToServer: () => {
              console.log("USB IP connected to server");
              params?.notificationCallbacks?.connectedToServer?.();
            },
            disconnectedFromServer: () => {
              console.log("USB IP disconnected from server");
              params?.notificationCallbacks?.disconnectedFromServer?.();
            },
            deviceAttached: (device: UsbIpDeviceDTO) => {
              console.log("USB IP device attached: ", device);
              context.commit("SET_DEVICE", {
                device,
                status: "attached"
              });
              params?.notificationCallbacks?.deviceAttached?.(device);
            },
            deviceDetached: (device: UsbIpDeviceDTO) => {
              console.log("USB IP device detached: ", device);
              context.commit("SET_DEVICE_STATUS", {
                device,
                status: "detached"
              });
              params?.notificationCallbacks?.deviceDetached?.(device);
            },
            deviceAttachTimedOut: (device: UsbIpDeviceDTO) => {
              console.log("USB IP device attach timed out: ", device);
              context.commit("SET_DEVICE_STATUS", {
                device,
                status: "timedOut"
              });
              params?.notificationCallbacks?.deviceAttachTimedOut?.(device);
            },
            failedToAttachDevice: (device: UsbIpDeviceDTO) => {
              console.log("USB IP device attach failed: ", device);
              context.commit("SET_DEVICE_STATUS", {
                device,
                status: "failed"
              });
              params?.notificationCallbacks?.failedToAttachDevice?.(device);
            }
          }
        },
        (message?: string) => {
          console.log("Recording started: ", message);
          params?.callback?.(message);
        },
        (message?: string) => {
          console.log("Can not start recording: ", message);
          params?.errorCallback?.(message);
        }
      );
    },
    stop: (
      context: {
        commit: (mutation: string) => void;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Stopping recording...");

      services.agent?.stopUsbIp(
        (message?: string) => {
          console.log("USB IP stopped: ", message);
          context.commit("UNSET_DEVICES");
          params?.callback?.(message);
        },
        (message?: string) => {
          console.log("Can not stop USB IP: ", message);
          params?.errorCallback?.(message);
        }
      );
    }
  },
  mutations: {
    SET_DEVICE(
      state: UsbIpState,
      device: {
        device: UsbIpDeviceDTO;
        status: "attached" | "detached" | "timedOut" | "failed";
      }
    ) {
      state.devices = (state.devices ?? []).concat([device]);
    },
    SET_DEVICE_STATUS(
      state: UsbIpState,
      device: {
        device: UsbIpDeviceDTO;
        status: "attached" | "detached" | "timedOut" | "failed";
      }
    ) {
      (state.devices ?? []).forEach(d => {
        if (_.isEqual(d.device, device.device)) {
          d.status = device.status;
        }
      });
    },
    UNSET_DEVICES(state: UsbIpState) {
      state.devices = null;
    }
  }
};

interface AgentServiceState {
  serviceInfo: { version: string } | null;
}

interface VirtualBackgroundState {
  cameras: { name: string }[] | null;
  config: VirtualBackgroundConfig | null;
}

interface DesktopSourcesState {
  sources: DesktopSourceDTO[] | null;
}

const agentServiceModule = {
  namespaced: true,
  state: (): AgentServiceState => ({
    serviceInfo: null
  }),
  actions: {
    getServiceInfo: (
      context: {
        commit: (mutation: string, data?: any) => void;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Getting agent service info...");

      services.agent?.getServiceInfo(
        (data: { version: string }) => {
          console.log("Got agent service info: ", data);
          context.commit("SET_SERVICE_INFO", data);
          params?.callback?.(data);
        },
        (message?: string) => {
          console.log("Can not get agent service info: ", message);
          params?.errorCallback?.(message);
        }
      );
    },
    sendServiceVersion: (
      context: {
        state: AgentServiceState;
        commit: (mutation: string) => void;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      const version = context.state.serviceInfo?.version ?? "";

      console.log(`Sending agent service version '${version}'...`);

      services.dcm?.sendAgentServiceVersion(
        version,
        () => {
          console.log("Agent service version sent");
          params?.callback?.();
        },
        (message?: string) => {
          console.log("Can not send agent service version: ", message);
          params?.errorCallback?.(message);
        }
      );
    },
    connect: (
      context: {
        commit: (mutation: string) => void;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Connecting to agent service...");

      services.agent?.connect(
        (message?: string) => {
          console.log("Connected to agent service: ", message);
          params?.callback?.(message);
        },
        (message?: string) => {
          console.log("Can not connect to agent service: ", message);
          params?.errorCallback?.(message);
        }
      );
    },
    disconnect: (
      context: {
        commit: (mutation: string) => void;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Disсonnecting from agent service...");

      services.agent?.disconnect(
        (message?: string) => {
          console.log("Disconnected from agent service: ", message);
          params?.callback?.(message);
        },
        (message?: string) => {
          console.log("Can not disconnect from agent service: ", message);
          params?.errorCallback?.(message);
        }
      );
    }
  },
  mutations: {
    SET_SERVICE_INFO(state: AgentServiceState, info: { version: string }) {
      state.serviceInfo = info;
    }
  },
  modules: {
    recording: {
      namespaced: true,
      state: () => ({
        /* empty */
      }),
      actions: {
        start: (
          context: {
            commit: (mutation: string) => void;
            rootState: any;
            rootGetters: any;
          },
          params?: {
            callback?: Function;
            errorCallback?: Function;
          }
        ) => {
          console.log("Starting recording...");

          const getProperty = (name: string) =>
            context.rootGetters["auth/property"](name);

          if (getProperty(RecordingParamPropertyName.destination)) {
            const destinationUrl = new URL(
              getProperty(RecordingParamPropertyName.destination)!
            );
            destinationUrl.searchParams.set(
              "session_id",
              context.rootState.session.info.id
            );

            const recordingParams = {
              destinationUrl: destinationUrl.toString(),
              screenEnabled: getProperty(
                RecordingParamPropertyName.screenEnabled
              ),
              screenResolution: getProperty(
                RecordingParamPropertyName.screenResolution
              ),
              screenFrameRate: getProperty(
                RecordingParamPropertyName.screenFrameRate
              ),
              screenBitRate: getProperty(
                RecordingParamPropertyName.screenBitRate
              ),
              speakersEnabled: getProperty(
                RecordingParamPropertyName.speakersEnabled
              ),
              speakersBitRate: getProperty(
                RecordingParamPropertyName.speakersBitRate
              ),
              microphoneEnabled: getProperty(
                RecordingParamPropertyName.microphoneEnabled
              ),
              microphoneBitRate: getProperty(
                RecordingParamPropertyName.microphoneBitRate
              )
            } as RecordingParams;

            patchServicesConfig(
              {
                recordingParams
              },
              ["agent"]
            );
          }

          services.agent?.startRecording(
            (message?: string) => {
              console.log("Recording started: ", message);
              params?.callback?.(message);
            },
            (message?: string) => {
              console.log("Can not start recording: ", message);
              params?.errorCallback?.(message);
            }
          );
        },
        stop: (
          context: {
            commit: (mutation: string) => void;
          },
          params?: {
            callback?: Function;
            errorCallback?: Function;
          }
        ) => {
          console.log("Stopping recording...");

          services.agent?.stopRecording(
            (message?: string) => {
              console.log("Recording stopped: ", message);
              params?.callback?.(message);
            },
            (message?: string) => {
              console.log("Can not stop recording: ", message);
              params?.errorCallback?.(message);
            }
          );
        }
      }
    },
    usbIp: usbIpModule,
    ipPrinters: {
      namespaced: true,
      state: () => ({
        /* empty */
      }),
      actions: {
        start: (
          context: {
            commit: (mutation: string) => void;
            rootState: any;
            rootGetters: any;
          },
          params: {
            configurationFinishedCallback?: Function;
            callback?: Function;
            errorCallback?: Function;
          }
        ) => {
          console.log("Starting IP printers...");

          services.agent?.startIpPrinters(
            {
              printers: _.concat(
                context.rootGetters["session/kiosk/property"](
                  FunctionalAvailabilityPropertyName.ipIpPrintersEnabled
                )
                  ? context.rootGetters["session/kiosk/property"](
                      "ipip.printers"
                    )
                  : [],
                context.rootGetters["session/kiosk/property"](
                  FunctionalAvailabilityPropertyName.tcpPrintersEnabled
                )
                  ? _.map(
                      context.rootGetters["session/kiosk/property"](
                        "tcp.printers"
                      ),
                      (p: {
                        device: string;
                        port: number;
                        driver_model: string;
                        name: string;
                      }) =>
                        _.assign(_.omit(p, "device"), {
                          // eslint-disable-next-line @typescript-eslint/camelcase
                          ip_address: (context.rootState.session.info
                            .ip_addresses as string[])?.[0]
                        })
                    )
                  : []
              ) as {
                ip_address: string;
                port: number;
                driver_model: string;
                name: string;
              }[],
              configurationFinishedCallback: (warnings?: string[]) => {
                console.log("IP printers configuration finished", { warnings });
                params.configurationFinishedCallback?.(warnings);
              }
            },
            () => {
              console.log("IP printers started:");
              params?.callback?.();
            },
            (message?: string) => {
              console.log("Can not start IP printers: ", message);
              params?.errorCallback?.(message);
            }
          );
        },
        stop: (
          context: {
            commit: (mutation: string) => void;
          },
          params?: {
            callback?: Function;
            errorCallback?: Function;
          }
        ) => {
          console.log("Stopping IP printers...");

          services.agent?.stopIpPrinters(
            () => {
              console.log("IP printers stopped");
              params?.callback?.();
            },
            (message?: string) => {
              console.log("Can not stop IP printers: ", message);
              params?.errorCallback?.(message);
            }
          );
        }
      }
    },
    virtualBackground: {
      namespaced: true,
      state: (): VirtualBackgroundState => ({
        cameras: null,
        config: null
      }),
      actions: {
        download: (
          context: {
            commit: (mutation: string) => void;
            rootState: any;
            rootGetters: any;
          },
          params: {
            callback?: Function;
            errorCallback?: Function;
          }
        ) => {
          console.log("Downloading virtual background...");

          const getProperty = (name: string) =>
            context.rootGetters["auth/property"](name);

          services.agent?.downloadVirtualBackground(
            {
              url: getProperty("virtual.background.url"),
              startedCallback: () =>
                console.log("Download virtual background started"),
              progressCallback: (data: any) =>
                console.log("Download virtual background progress: ", data)
            },
            () => {
              console.log("Virtual background downloaded");
              params?.callback?.();
            },
            (message?: string) => {
              console.log("Can not download virtual background: ", message);
              params?.errorCallback?.(message);
            }
          );
        },
        setConfig: (
          context: {
            state: VirtualBackgroundState;
            commit: (mutation: string, data?: any) => void;
            rootState: any;
            rootGetters: any;
          },
          params: {
            config: Partial<VirtualBackgroundConfig>;
            callback?: Function;
            errorCallback?: Function;
          }
        ) => {
          console.log("Setting virtual background config...");

          services.agent?.setVirtualBackgroundConfig(
            params.config,
            (config: VirtualBackgroundConfig) => {
              console.log("Virtual background config set: ", config);
              context.commit("SET_CONFIG", config);
              params?.callback?.(config);
            },
            (message?: string) => {
              console.log("Can not set virtual background config: ", message);
              params?.errorCallback?.(message);
            }
          );
        },
        getCameras: (
          context: {
            state: VirtualBackgroundState;
            commit: (mutation: string, data?: any) => void;
            rootState: any;
            rootGetters: any;
          },
          params: {
            callback?: Function;
            errorCallback?: Function;
          }
        ) => {
          console.log("Getting virtual background cameras...");

          services.agent?.getVirtualBackgroundCameras(
            (cameras: { name: string }) => {
              console.log("Got virtual background cameras: ", cameras);
              context.commit("SET_CAMERAS", cameras);
              params?.callback?.(cameras);
            },
            (message?: string) => {
              console.log("Can not get virtual background cameras: ", message);
              params?.errorCallback?.(message);
            }
          );
        }
      },
      mutations: {
        SET_CAMERAS(
          state: VirtualBackgroundState,
          cameras: {
            name: string;
          }[]
        ) {
          state.cameras = cameras;
        },
        UNSET_CAMERAS(state: VirtualBackgroundState) {
          state.cameras = null;
        },
        SET_CONFIG(
          state: VirtualBackgroundState,
          config: VirtualBackgroundConfig
        ) {
          state.config = config;
        },
        UNSET_CONFIG(state: VirtualBackgroundState) {
          state.config = null;
        }
      }
    },
    vpn: {
      namespaced: true,
      state: () => ({
        /* empty */
      }),
      actions: {
        start: (
          context: {
            commit: (mutation: string) => void;
            rootState: any;
            rootGetters: any;
          },
          params: {
            callback?: Function;
            errorCallback?: Function;
          }
        ) => {
          console.log("Starting VPN...");

          services.agent?.startVpn(
            context.rootGetters["auth/property"]("vpn.profile") as string,
            () => {
              console.log("VPN started:");
              params?.callback?.();
            },
            (message?: string) => {
              console.log("Can not start VPN: ", message);
              params?.errorCallback?.(message);
            }
          );
        }
      }
    },
    desktopSources: {
      namespaced: true,
      state: (): DesktopSourcesState => ({
        sources: null
      }),
      actions: {
        startEnumerate: (
          context: {
            commit: (mutation: string, data: any) => void;
            state: DesktopSourcesState;
            rootState: any;
            rootGetters: any;
          },
          params: {
            callback?: Function;
            errorCallback?: Function;
          }
        ) => {
          console.log("Starting desktop sources enumerating...");

          services.agent?.startEnumerateDesktopSources(
            {
              addedOrChanged: (source: Partial<DesktopSourceDTO>) => {
                console.log("Desktop source added: ", source);

                context.commit(
                  _.find(context.state.sources, s => s.id === source.id)
                    ? "PATCH_SOURCE"
                    : "SET_SOURCE",
                  source
                );
              },
              removed: (id: string) => {
                console.log("Desktop source removed: ", id);
                context.commit("UNSET_SOURCE", id);
              }
            },
            () => {
              console.log("Desktop sources enumerating started");
              params?.callback?.();
            },
            () => {
              console.error("Desktop sources enumerating starting error");
              params?.errorCallback?.();
            }
          );
        },
        stopEnumerate: (
          context: {
            commit: (mutation: string, data?: any) => void;
            rootState: any;
            rootGetters: any;
          },
          params: {
            callback?: Function;
            errorCallback?: Function;
          }
        ) => {
          console.log("Stopping desktop sources enumerating...");

          services.agent?.stopEnumerateDesktopSources(
            () => {
              console.log("Desktop sources enumerating stopped");
              params?.callback?.();
            },
            () => {
              console.error("Desktop sources enumerating stopping error");
              params?.errorCallback?.();
            }
          );
        }
      },
      mutations: {
        SET_SOURCE(state: DesktopSourcesState, source: DesktopSourceDTO) {
          state.sources = (state.sources ?? []).concat([source]);
        },
        UNSET_SOURCE(state: DesktopSourcesState, id: string) {
          state.sources = state.sources
            ? _.filter(state.sources, s => s.id !== id)
            : null;
        },
        PATCH_SOURCE(
          state: DesktopSourcesState,
          source: Partial<DesktopSourceDTO>
        ) {
          state.sources
            ? _.map(state.sources, s =>
                s.id === source.id ? _.assign({}, s, source) : s
              )
            : null;
        },
        UNSET_SOURCES(state: DesktopSourcesState) {
          state.sources = null;
        }
      }
    },
    barcodeInput: {
      namespaced: true,
      state: () => ({
        /* empty */
      }),
      actions: {
        start: (
          context: {
            commit: (mutation: string) => void;
            rootState: any;
            rootGetters: any;
          },
          params: {
            desktopSourceId: string;
            callback?: Function;
            errorCallback?: Function;
          }
        ) => {
          console.log("Starting barcode input...");

          const getProperty = (name: string) =>
            context.rootGetters["auth/property"](name);

          services.agent?.startBarcodeInput(
            params.desktopSourceId,
            getProperty("barcode.scanner.hotkey") as string | null,
            () => {
              console.log("Barcode input started");
              params?.callback?.();
            },
            (message?: string) => {
              console.error("Barcode input starting error: ", message);
              params?.errorCallback?.(message);
            }
          );
        },
        send: (
          context: {
            commit: (mutation: string) => void;
            rootState: any;
            rootGetters: any;
          },
          params: {
            data: object;
            callback?: Function;
            errorCallback?: Function;
          }
        ) => {
          console.log("Sending barcode input...", params.data);
          services.agent?.sendBarcodeInput(
            params.data,
            () => {
              console.log("Barcode input stopped");
              params?.callback?.();
            },
            (message?: string) => {
              console.error("Barcode input stopping error: ", message);
              params?.errorCallback?.(message);
            }
          );
        },
        stop: (
          context: {
            commit: (mutation: string, data?: any) => void;
            rootState: any;
            rootGetters: any;
          },
          params: {
            callback?: Function;
            errorCallback?: Function;
          }
        ) => {
          console.log("Stopping barcode input...");
          services.agent?.stopBarcodeInput(
            () => {
              console.log("Barcode input stopped");
              params?.callback?.();
            },
            (message?: string) => {
              console.error("Barcode input stopping error: ", message);
              params?.errorCallback?.(message);
            }
          );
        }
      }
    }
  }
};

interface TicketsState {
  count: Record<"total" | "assigned", number> | null;
  ticket: TicketDTO | null;
}

const ticketsModule = {
  namespaced: true,
  state: (): TicketsState => ({
    count: null,
    ticket: null
  }),
  actions: {
    getCount: (
      context: {
        commit: (mutation: string, data?: any) => void;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Getting tickets count...");

      services.rem?.getTicketsCount(
        (count: Record<"total" | "assigned", number>) => {
          console.log("Got tickets count: ", count);
          context.commit("SET_COUNT", count);
          params?.callback?.(count);
        },
        (err?: any) => {
          console.error("Can not get tickets count: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    getOne: (
      context: {
        commit: (mutation: string, data?: any) => void;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Getting ticket...");

      services.rem?.getTicket(
        (ticket: TicketDTO) => {
          console.log("Got ticket: ", ticket);
          context.commit("SET_TICKET", ticket);
          params?.callback?.(ticket);
        },
        (err?: any) => {
          console.error("Can not get ticket: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    create: (
      context: {
        commit: (mutation: string, data?: any) => void;
      },
      params: {
        incidentUuid: string;
        payload?: object;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Creating ticket...", _.pick(params, "uuid", "payload"));

      services.rem?.createTicket(
        params.incidentUuid,
        params.payload ?? {},
        (data?: any) => {
          console.log("Ticket created");
          params?.callback?.(data);
        },
        (err?: any) => {
          console.error("Can not create ticket: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    close: (
      context: {
        commit: (mutation: string, data?: any) => void;
      },
      params: {
        uuid: string;
        payload?: object;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Closing ticket...", _.pick(params, ["uuid", "payload"]));

      services.rem?.closeTicket(
        params.uuid,
        params.payload ?? {},
        () => {
          console.log("Ticket closed");
          context.commit("UNSET_TICKET");
          params?.callback?.();
        },
        (err?: any) => {
          console.error("Can not close ticket: ", err);
          params?.errorCallback?.(err);
        }
      );
    }
  },
  mutations: {
    SET_COUNT(
      state: TicketsState,
      count: Record<"total" | "assigned", number>
    ) {
      state.count = count;
    },
    UNSET_COUNT(state: TicketsState) {
      state.count = null;
    },
    SET_TICKET(state: TicketsState, ticket: TicketDTO) {
      state.ticket = ticket;
    },
    UNSET_TICKET(state: TicketsState) {
      state.ticket = null;
    }
  }
};

interface IncidentState {
  incident: IncidentDTO | null;
}

const incidentModule = {
  namespaced: true,
  state: (): IncidentState => ({
    incident: null
  }),
  actions: {
    startProcessing: (
      context: {
        state: IncidentState;
        commit: (mutation: string, data?: any) => void;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Starting incident processing...");

      services.rem?.startProcessingIncident(
        context.state.incident!.id,
        (data?: any) => {
          console.log("Starting incident processing: ", data);
          params?.callback?.(data);
        },
        (err?: any) => {
          console.error("Can not start incident processing: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    get: (
      context: {
        commit: (mutation: string, data?: any) => void;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Getting incident...");

      services.rem?.getIncident(
        (incident: IncidentDTO) => {
          console.log("Got incident: ", incident);
          context.commit("SET_INCIDENT", incident);
          params?.callback?.(incident);
        },
        (err?: any) => {
          console.error("Can not get incident: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    close: (
      context: {
        state: IncidentState;
        commit: (mutation: string, data?: any) => void;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Closing incident...");

      services.rem?.closeIncident(
        context.state.incident!.id,
        (data?: any) => {
          console.log("Incident closed: ", data);
          params?.callback?.(data);
        },
        (err?: any) => {
          console.error("Can not close incident: ", err);
          params?.errorCallback?.(err);
        }
      );
    }
  },
  mutations: {
    SET_INCIDENT(state: IncidentState, incident: IncidentDTO) {
      state.incident = incident;
    },
    UNSET_INCIDENT(state: IncidentState) {
      state.incident = null;
    }
  }
};

interface RootState {
  hints: number | null;
  endpointStatus: EndpointStatusDTO | null;
  status: ClientStatus | null;
  telephonyType: TelephonyType | null;
}

export const storeOptions = {
  state: (): RootState => ({
    hints: null,
    endpointStatus: null,
    status: null,
    telephonyType: null
  }),
  getters: {
    endpointStatusCode(state: RootState): EndpointStatus | null {
      return (state.endpointStatus?.status ?? null) !== null
        ? parseInt(
            Number(state.endpointStatus?.status)
              .toString(2)
              .slice(-8),
            2
          )
        : null;
    },
    status(state: RootState) {
      return (
        state.status ??
        (localStorage.getItem(clientStatusStorageKey) !== null
          ? +localStorage.getItem(clientStatusStorageKey)!
          : null)
      );
    },
    isTelephonySubordinate(state: RootState) {
      return !!((state.hints ?? 0) & 0x8);
    },
    isQueueSuspended(state: RootState) {
      return !!((state.hints ?? 0) & 0x20);
    },
    isOnHold(state: RootState): boolean {
      return ((state.endpointStatus?.status ?? 0) & EndpointStatus.onHold) != 0;
    },
    isVideoOn(state: RootState): boolean {
      return (
        ((state.endpointStatus?.status ?? 0) & EndpointStatus.videoOn) != 0
      );
    }
  },
  actions: {
    getEndpointStatus: async (
      context: {
        commit: (mutation: string, data?: unknown, params?: {}) => void;
        dispatch: Function;
        state: RootState;
        getters: any;
        rootGetters: any;
        rootState: any;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Endpoint status retrieving...");
      services.rem?.getEndpointStatus(
        (status: EndpointStatusDTO) => {
          console.log("Endpoint status retrieved: ", status);
          const oldEndpointStatusCode = context.getters.endpointStatusCode;
          context.commit("SET_ENDPOINT_STATUS", status);
          if (
            oldEndpointStatusCode !== null &&
            oldEndpointStatusCode !== context.getters.endpointStatusCode
          ) {
            handleEndpointStatusChange(context);
          }
          params?.callback?.(status);
        },
        (err: unknown) => {
          console.error("Endpoint status retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    setEndpointStatus: (
      context: {
        commit: (mutation: string, data: unknown) => void;
        rootState: any;
        rootGetters: any;
      },
      params: {
        status: EndpointStatus | number;
        details?: string;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending endpoint status...", _.pick(params, "status"));
      services.rem?.setEndpointStatus(
        params.status,
        params.details ?? "",
        context.rootState.session.party &&
          [
            EndpointStatus.inComingCall,
            EndpointStatus.placingCall,
            EndpointStatus.connected
          ].includes(
            parseInt(
              Number(params.status)
                .toString(2)
                .slice(-8),
              2
            )
          )
          ? [context.rootState.session.party].concat(
              context.rootState.telephonyType === TelephonyType.twilio
                ? []
                : [
                    context.rootState.config.config.clientType === "kiosk"
                      ? context.rootState.auth.deviceId
                      : _.find(
                          context.rootState.auth.profile,
                          i => i.name === "webex.email"
                        )?.value
                  ]
            )
          : [],
        (data: unknown) => {
          console.log(`Endpoint status ${params.status} has been sent`);
          params.callback?.(data);
        },
        (err: unknown) => {
          (err: unknown) => {
            console.error(
              `Can not set endpoint status ${params.status}: `,
              err
            );
            params.errorCallback?.(err);
          };
        }
      );
    },
    getStatus: async (
      context: {
        commit: (mutation: string, data?: {}) => void;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Status retrieving...");
      services.rem?.getStatus(
        (status: ClientStatus) => {
          console.log("Status retrieved: ", status);
          context.commit("SET_STATUS", status);
          params?.callback?.(status);
        },
        (err: unknown) => {
          console.error("Status retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    setStatus: async (
      context: {
        commit: (mutation: string, data: ClientStatus) => void;
        rootState: unknown;
      },
      params: {
        status: ClientStatus;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Sending status...", _.pick(params, "status"));
      services.rem?.setStatus(
        params.status,
        (data: unknown) => {
          console.log(`Status ${params.status} has been sent`);
          params.callback?.(data);
        },
        (err: unknown) => {
          console.error(`Can not set status ${params.status}: `, err);
          params.errorCallback?.(err);
        }
      );
    },
    getTelephonyType: (
      context: {
        commit: (mutation: string, data: number) => void;
        rootState: any;
        rootGetters: any;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Telephony type retrieving...");

      services.rem?.getTelephonyType(
        (type: TelephonyType) => {
          console.log("Telephony type retrieved: ", type);

          context.commit("SET_TELEPHONY_TYPE", type);

          const getProperty = (name: string) =>
            context.rootGetters["auth/property"](name);

          const clientType = context.rootState.config.config.clientType;

          if (
            type === TelephonyType.webex &&
            clientType === "expert" &&
            getProperty("cisco.sip.extension")
          ) {
            patchServicesConfig({
              mustangExtension: getProperty("cisco.sip.extension") as string
            });
          }

          if (type === TelephonyType.cucm) {
            if (getProperty("cisco.sip.extension")) {
              patchServicesConfig({
                extension: getProperty("cisco.sip.extension") as string
              });
            }
          } else if (type === TelephonyType.webex) {
            if (clientType === "expert") {
              if (getProperty("webex.email")) {
                patchServicesConfig({
                  extension: getProperty("webex.email") as string
                });
              }
              if (getProperty("webex.integration.id")) {
                patchServicesConfig({
                  webexClientId: getProperty("webex.integration.id") as string
                });
              }
              if (getProperty("webex.authorization.url")) {
                patchServicesConfig({
                  webexScope:
                    new URLSearchParams(
                      getProperty("webex.authorization.url") as string
                    ).get("scope") ?? "spark:all"
                });
              }
            } else {
              if (getProperty("wms.manager.host")) {
                const port = +(
                  (getProperty("wms.manager.port") as
                    | string
                    | number
                    | undefined) ?? ServiceDefaultPort.wms
                );

                patchServicesConfig({
                  wmsServiceUrl: `https://${getProperty(
                    "wms.manager.host"
                  )}:${port}`,
                  wmsServicePort: port
                });
              }
            }
          } else if (type === TelephonyType.twilio) {
            if (getProperty("twilio.management.server.ip")) {
              patchServicesConfig({
                tmsServiceUrl: `https://${getProperty(
                  "twilio.management.server.ip"
                )}:${getProperty("twilio.management.server.port") ||
                  ServiceDefaultPort.tms}`
              });
            }

            if (clientType === "expert") {
              patchServicesConfig({
                extension: getClientId(context.rootState.auth.jwt)
              });
            }
          }

          const awsCredentials = getProperty("aws.translator.credentials");

          if (_.get(awsCredentials, "aws_region")) {
            patchServicesConfig(
              {
                awsRegion: _.get(awsCredentials, "aws_region") as string
              },
              ["aws"]
            );
          }
          if (_.get(awsCredentials, "aws_access_key_id")) {
            patchServicesConfig(
              {
                awsAccessKeyId: _.get(
                  awsCredentials,
                  "aws_access_key_id"
                ) as string
              },
              ["aws"]
            );
          }
          if (_.get(awsCredentials, "aws_secret_access_key")) {
            patchServicesConfig(
              {
                awsSecretAccessKey: _.get(
                  awsCredentials,
                  "aws_secret_access_key"
                ) as string
              },
              ["aws"]
            );
          }
          if (_.get(awsCredentials, "aws_s3_bucket_name")) {
            patchServicesConfig(
              {
                awsS3BucketName: _.get(
                  awsCredentials,
                  "aws_s3_bucket_name"
                ) as string
              },
              ["aws"]
            );
          }

          params?.callback?.(type);
        },
        (err: unknown) => {
          console.error("Telephony type retrieving error: ", err);

          params?.errorCallback?.(err);
        }
      );
    },
    getHints: (
      context: {
        commit: (mutation: string, data: number) => void;
        rootState: unknown;
      },
      params?: {
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("Hints retrieving...");
      services.rem?.getHints(
        (hints: number) => {
          console.log("Hints retrieved: ", hints);
          context.commit("SET_HINTS", hints);
          params?.callback?.(hints);
        },
        (err: unknown) => {
          console.log("Hints retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    },
    syncFirmwareVersion: async (
      context: {
        commit: (mutation: string, data: number) => void;
        dispatch: Function;
        rootState: any;
      },
      params: {
        client: Record<"uuid" | "firmware_uuid", string> | string;
        callback?: Function;
        errorCallback?: Function;
      }
    ) => {
      console.log("DCM spec retrieving...");

      let spec: object;

      try {
        spec = (
          await axios.get(
            `${window.location.protocol}//${window.location.host}${_.trimEnd(
              window.location.pathname,
              "/"
            )}/dcm-spec.json`,
            {
              params: { timestamp: moment().unix() }
            }
          )
        ).data as object;

        console.log("DCM spec retrieved: ", spec);
      } catch (e) {
        console.error("DCM spec retrieving error: ", e.message);
        params?.errorCallback?.(e.message);
        return;
      }

      const firmwareParams = _.pick(spec, [
        "product",
        "family",
        "model",
        "version",
        "build"
      ]) as Record<
        "product" | "family" | "model" | "version" | "build",
        string
      >;

      console.log("Retrieving firmware uuid...", firmwareParams);

      const cb = (firmwareUuid: string) => {
        const cb = (client: Record<"uuid" | "firmware_uuid", string>) => {
          if (client.firmware_uuid !== firmwareUuid) {
            console.log("Updating client - setting new firmware uuid", {
              client,
              firmwareUuid
            });

            services.dcm?.updateClient(
              _.assign({}, client, {
                // eslint-disable-next-line @typescript-eslint/camelcase
                firmware_uuid: firmwareUuid
              }),
              (data: any) => {
                console.log("Client updated", data);
                params?.callback?.();
              },
              (err: any) => {
                console.error("Updating client error: ", err);

                params?.errorCallback?.(err);
              }
            );
          } else {
            params?.callback?.();
          }
        };

        _.isString(params.client)
          ? services.dcm?.getClientByName(
              params.client as string,
              cb,
              (err: any) => params?.errorCallback?.(err)
            )
          : cb(params.client as Record<"uuid" | "firmware_uuid", string>);
      };

      services.dcm?.getFirmwareUuid(
        firmwareParams,
        (uuid: string) => {
          console.log("Firmware uuid retrieved: ", uuid);

          if (uuid) {
            cb(uuid);
          } else {
            console.log("Creating spec...");

            services.dcm?.createSpec(
              spec,
              (uuid: string) => {
                console.log("Spec created: ", uuid);

                cb(uuid);
              },
              (err: any) => {
                console.error("Spec creating error: ", err);

                params?.errorCallback?.(err);
              }
            );
          }
        },
        (err: any) => {
          console.error("Firmware uuid retrieving error: ", err);
          params?.errorCallback?.(err);
        }
      );
    }
  },
  mutations: {
    SET_ENDPOINT_STATUS(state: RootState, status: EndpointStatusDTO) {
      state.endpointStatus = status;
    },
    SET_STATUS(state: RootState, status: ClientStatus) {
      state.status = status;
      localStorage.setItem(clientStatusStorageKey, "" + status);
    },
    UNSET_STATUS(state: RootState) {
      state.status = null;
      localStorage.removeItem(clientStatusStorageKey);
    },
    SET_HINTS(state: RootState, hints: number) {
      state.hints = hints;
    },
    SET_TELEPHONY_TYPE(state: RootState, type: TelephonyType) {
      state.telephonyType = type;
      patchServicesConfig({ telephonyType: type });
    }
  },
  modules: {
    topics: topicsModule,
    queue: queueModule,
    session: sessionModule,
    sharing: sharingModule,
    signals: signalsModule,
    webex: webexModule,
    twilio: twilioModule,
    auth: authModule,
    config: configModule,
    clients: clientsModule,
    inbox: inboxModule,
    network: networkModule,
    surveillance: surveillanceModule,
    agentService: agentServiceModule,
    tickets: ticketsModule,
    incident: incidentModule
  }
};
